/* Clarène Cart · generado por scripts/build.mjs, no editar a mano */
(function () {
'use strict';
// ---- core.mjs ----
// Clarène Cart · núcleo puro (sin DOM ni red). Se prueba en Node con los fixtures reales.
// Reglas (docs/04 §4):
//  - El precio que vale es el de /cart.js. Aquí solo se agrupa, se planifica y se verifica.
//  - Un grupo lógico = todas las líneas que Shopify reparte con las mismas propiedades.
//  - Upgrade = un `add` del delta con las mismas propiedades (+ regalo en la misma petición).
//  - Bajada / borrado = un `update` con todas las keys del grupo.

const GIFT_TOLERANCE = 0; // un regalo debe costar exactamente 0
const PRICE_TOLERANCE = 2; // céntimos de redondeo que aceptamos (docs/04 §1.10)

// ---------- propiedades de Kaching ----------

function parseKaching(line) {
  const raw = line?.properties?.__kaching_bundles;
  if (!raw || typeof raw !== 'string') return null;
  try {
    const o = JSON.parse(raw);
    return o && typeof o === 'object' ? o : null;
  } catch {
    return null;
  }
}

const sig = (props) => JSON.stringify(Object.entries(props || {}).sort(([a], [b]) => (a < b ? -1 : 1)));

// ---------- suscripciones y líneas de las landings ----------

/** Plan de venta (selling plan) de una línea de /cart.js, o null si es de compra única. */
const planOf = (line) => line?.selling_plan_allocation?.selling_plan || null;

/**
 * Líneas de la landing de suscripción (cls-landing.js): _cls_origin ('landing-once' | 'landing-sub') en el producto
 * principal, _cls_welcome en la 2.ª base de bienvenida y _cls_gift ('once' | 'sub') en la Esponja de regalo. Con
 * suscripción, la base de bienvenida y la Esponja van anidadas a la base suscrita (parent_id); en compra única la
 * Esponja va suelta (la deja a 0 € un descuento automático limitado al pack).
 */
const landingGift = (line) => line?.properties?._cls_gift || null;
const landingWelcome = (line) => !!line?.properties?._cls_welcome;

/**
 * Grupo de la landing al que pertenece una línea de regalo o de bienvenida que no trae parent_relationship
 * (la Esponja del pack de compra única, o cualquier carrito pintado desde Liquid, que no lo incluye).
 */
function landingParent(line, groups) {
  const mode = landingWelcome(line) || landingGift(line) === 'sub' ? 'landing-sub' : landingGift(line) === 'once' ? 'landing-once' : null;
  if (!mode) return null;
  const cands = groups.filter((g) => g.kind === 'plain' && g.lines[0]?.properties?._cls_origin === mode && (mode === 'landing-once' || planOf(g.lines[0])));
  // en compra única la Esponja es del pack (bundle de Shopify), no de una base suelta
  return cands.find((g) => g.lines[0].has_components) || cands.find((g) => /pack/i.test(g.lines[0].product_title || '')) || cands[0] || null;
}

/** Clave de grupo de un producto normal: variante, plan de venta (nunca se mezclan) y propiedades. */
const plainKey = (line) => `p:${line.variant_id}${planOf(line) ? '@' + planOf(line).id : ''}:${sig(line.properties)}`;

function randomBundleId(rand = Math.random) {
  const abc = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let s = '';
  for (let i = 0; i < 4; i++) s += abc[Math.floor(rand() * abc.length)];
  return s;
}

/**
 * Propiedades de línea principal idénticas a las del widget (fixtures/widget-items.json). Con test A/B de Kaching
 * (Latinoamérica) llevan también la variante de la visitante (abid, ab), como el widget: sin ella Kaching aplica la A.
 */
function buildMainProps(offer, tier, bundleId) {
  const ab = offer.ab ? { abid: offer.ab.abid, ab: offer.ab.letter } : {};
  return {
    __kaching_bundles: JSON.stringify({
      deal: offer.deal, pp: offer.productId, main: true, market: offer.market, v: offer.version, id: bundleId, bid: tier.bid, ...ab,
    }),
  };
}

/** Propiedades del regalo, en el mismo orden que el widget (con la variante A/B si el pack la lleva). */
function buildGiftProps({ id, deal, v, abid, ab }, giftId) {
  const variant = ab ? { abid, ab } : {};
  return { __kaching_bundles: JSON.stringify({ id, deal, v, ...variant, gift: giftId }) };
}

/**
 * Firma de los tramos de una oferta de Kaching: ids, tipo, cantidades, descuentos y regalos. Vale igual para los
 * bloques de la página (buyDiscountType…) y para window.kachingBundlesApi.fetchDeals() (buyDiscount: {type, value}).
 * Las variantes A y B de un test comparten ids de tramo: se distinguen por los descuentos.
 */
function dealSignature(bars) {
  const d = (type, value) => (!type || type === 'default' ? 'default:0' : `${type}:${Number(value)}`);
  const fromObj = (o) => (o ? d(o.type, o.value) : d());
  return (bars || []).map((b) => {
    const gifts = (b.freeGifts || []).map((g) => g.id).sort().join(',');
    if (b.dealBarType === 'quantity-break') {
      const disc = 'discount' in b ? fromObj(b.discount) : d(b.discountType, b.discountValue);
      return `${b.id}|qb|${b.quantity}|${disc}|${gifts}`;
    }
    if (b.dealBarType === 'bxgy') {
      const buy = 'buyDiscount' in b ? fromObj(b.buyDiscount) : d(b.buyDiscountType, b.buyDiscountValue);
      const get = 'getDiscount' in b ? fromObj(b.getDiscount) : d(b.getDiscountType, b.getDiscountValue);
      return `${b.id}|bxgy|${b.buyQuantity}+${b.getQuantity}|${buy}|${get}|${gifts}`;
    }
    return `${b.id}|${b.dealBarType}`;
  }).sort().join(';');
}

/**
 * Ofertas de las DOS variantes A/B del mercado actual, desde la referencia verificada (config.marketOffers), de forma
 * síncrona: con ellas se reconocen los packs que ya están en el carrito por sus propiedades, desde el primer render y
 * sin esperar a Kaching. Si el precio actual de la base en el mercado no es el verificado, ninguna (modo genérico).
 */
function marketVariantOffers(marketOffers, market) {
  const c = (marketOffers?.offers || []).find((o) => String(o.market) === String(market));
  if (!c) return { offers: [], candidate: null, reason: 'sin oferta verificada para este mercado' };
  if (marketOffers.livePrice !== c.unitPrice) {
    return { offers: [], candidate: c, reason: `precio de la base ${marketOffers.livePrice} ≠ verificado ${c.unitPrice}` };
  }
  const offers = c.variants.map((v) => ({
    deal: c.deal, version: c.version, market: c.market, productId: c.productId, variantId: c.variantId,
    maxQty: v.maxQty, tiers: v.tiers, ab: { abid: v.abid, letter: v.letter },
  }));
  return { offers, candidate: c, reason: null };
}

/**
 * Variante que Kaching asigna a la visitante (fetchDeals, sin peticiones). Solo hace falta para CREAR packs desde el
 * carrito (base del carrusel, base suelta). changed = Kaching tiene la oferta pero ya no es ninguna de las verificadas.
 */
function visitorVariant(candidate, deals) {
  const deal = (deals || []).find((x) => x?.nanoId === candidate?.deal && String(x.marketId) === String(candidate?.market));
  if (!deal) return { letter: null, changed: false, reason: `Kaching no devuelve la oferta ${candidate?.deal}` };
  const v = candidate.variants.find((x) => x.sig === dealSignature(deal.dealBars));
  // version: la vigente en Kaching, para que los packs que crea el carrito lleven las mismas propiedades que el widget
  return v ? { letter: v.letter, version: deal.versionId || null, changed: false, reason: null } : { letter: null, changed: true, reason: 'la oferta de Kaching no coincide con ninguna variante verificada' };
}

/** Oferta con la que el carrito crea un pack nuevo: la de España, o en Latinoamérica la variante de la visitante. */
function newPackOffer(config, variantId) {
  return activeOffers(config).find((o) => o.variantId === variantId && !o.pending && (!o.ab || o.visitor)) || null;
}

/**
 * Oferta de la base en un mercado con test A/B (Latinoamérica), a partir de lo que Kaching resuelve para la visitante
 * (fetchDeals, sin peticiones) y de la referencia verificada en Shopify (config.marketOffers). Solo se usa si la oferta
 * de Kaching es exactamente una de las variantes verificadas y si el precio actual de la base en el mercado es el
 * verificado; si no, null y el carrito muestra el pack de forma genérica (sin upgrades ni unidades gratis).
 */
function resolveMarketOffer(candidate, deals, livePrice) {
  if (!candidate) return { offer: null, reason: 'sin oferta verificada para este mercado' };
  if (livePrice !== candidate.unitPrice) return { offer: null, reason: `precio de la base ${livePrice} ≠ verificado ${candidate.unitPrice}` };
  const deal = (deals || []).find((x) => x?.nanoId === candidate.deal && String(x.marketId) === String(candidate.market));
  if (!deal) return { offer: null, reason: `Kaching no devuelve la oferta ${candidate.deal}` };
  const sig = dealSignature(deal.dealBars);
  const variant = candidate.variants.find((v) => v.sig === sig);
  if (!variant) return { offer: null, reason: 'la oferta de Kaching no coincide con ninguna variante verificada' };
  return {
    offer: {
      deal: candidate.deal, version: candidate.version, market: candidate.market, productId: candidate.productId,
      variantId: candidate.variantId, maxQty: variant.maxQty, tiers: variant.tiers, ab: { abid: variant.abid, letter: variant.letter },
    },
    reason: null,
  };
}

// ---------- configuración ----------

function offerTier(offer, qty) {
  return offer?.tiers.find((t) => t.qty === qty) || null;
}

function giftTier(offer) {
  return offer?.tiers.find((t) => tierGifts(t).length) || null;
}

/**
 * Regalos con producto de un tramo: gift (uno, p. ej. la brocha del 2+1) o gifts (varios). Cada uno va una vez, con
 * sus propiedades (gift = id del regalo en Kaching). Las ventajas solo de texto (perks) no son líneas del carrito.
 */
function tierGifts(tier) {
  return tier ? tier.gifts || (tier.gift ? [tier.gift] : []) : [];
}

const giftIdOf = (line) => parseKaching(line)?.gift ?? null;

/** Ofertas activas en el mercado actual. Fuera de él no hay upgrades (docs/02 §4.1). */
function activeOffers(config) {
  return (config.offers || []).filter((o) => String(o.market) === String(config.market));
}

// ---------- agrupación ----------

/**
 * Convierte un /cart.js en grupos lógicos.
 * kind:
 *  - 'offer'   bundle Kaching de una oferta configurada (upgrade posible)
 *  - 'kaching' otro bundle Kaching (p. ej. Pack Buena Cara): solo se puede eliminar
 *  - 'plain'   producto sin bundle (cantidad libre; si es la base de una oferta → upgrade especial)
 *  - 'orphan-gift' regalo cuyo bundle no está en el carrito
 */
function groupCart(cart, config) {
  const offers = activeOffers(config);
  const items = cart?.items || [];
  const groups = [];
  const byGid = new Map();
  const lineGroup = new Map(); // key → grupo
  const get = (gid, init) => {
    let g = byGid.get(gid);
    if (!g) {
      g = { gid, lines: [], gifts: [], children: [], ...init() };
      byGid.set(gid, g);
      groups.push(g);
    }
    return g;
  };

  // 1) líneas principales y productos normales
  for (const line of items) {
    const k = parseKaching(line);
    if (k?.gift) continue;
    if (line.parent_relationship?.parent_key && !k?.main) continue; // hijos: más abajo
    if (!k && (landingGift(line) || landingWelcome(line))) continue; // regalo o bienvenida de la landing: más abajo
    let g;
    if (k?.main && k.deal && k.id) {
      g = get(`k:${k.deal}:${k.id}`, () => ({ kind: 'kaching', kaching: k, props: line.properties }));
    } else {
      g = get(plainKey(line), () => ({ kind: 'plain', kaching: null, props: line.properties || {} }));
    }
    g.lines.push(line);
    lineGroup.set(line.key, g);
  }

  // 2) regalos: se vinculan por deal + id
  for (const line of items) {
    const k = parseKaching(line);
    if (!k?.gift) continue;
    const g = byGid.get(`k:${k.deal}:${k.id}`);
    if (g) {
      g.gifts.push(line);
      lineGroup.set(line.key, g);
    } else {
      const o = get(`g:${line.key}`, () => ({ kind: 'orphan-gift', kaching: k, props: line.properties }));
      o.lines.push(line);
      lineGroup.set(line.key, o);
    }
  }

  // 3) otras líneas anidadas (parent_relationship) que no son regalo Kaching, y regalos / bienvenida de la landing
  for (const line of items) {
    if (lineGroup.has(line.key)) continue;
    const parent = lineGroup.get(line.parent_relationship?.parent_key) || landingParent(line, groups);
    if (parent) {
      parent.children.push(line);
      lineGroup.set(line.key, parent);
    } else {
      const g = get(plainKey(line), () => ({ kind: 'plain', kaching: null, props: line.properties || {} }));
      g.lines.push(line);
      lineGroup.set(line.key, g);
    }
  }

  for (const g of groups) finalizeGroup(g, offers);
  return groups;
}

/** Importe real de las líneas del pack sumando lo que le quitan los códigos de descuento (lo que cobra Kaching). */
function amountBeforeCodes(g) {
  let codes = 0;
  for (const l of g.lines) {
    for (const a of l.line_level_discount_allocations || []) if (a.discount_application?.type === 'discount_code') codes += a.amount || 0;
  }
  return g.finalCents + codes;
}

function finalizeGroup(g, offers) {
  const sum = (ls, f) => ls.reduce((a, l) => a + (l[f] || 0), 0);
  g.qty = sum(g.lines, 'quantity');
  g.variantId = g.lines[0]?.variant_id ?? null;
  g.finalCents = sum(g.lines, 'final_line_price');
  g.originalCents = sum(g.lines, 'original_line_price');
  g.giftQty = sum(g.gifts, 'quantity');
  g.giftFinalCents = sum(g.gifts, 'final_line_price');
  g.giftOriginalCents = sum(g.gifts, 'original_line_price');
  g.childrenFinalCents = sum(g.children, 'final_line_price');
  g.keys = [...g.lines, ...g.gifts, ...g.children].map((l) => l.key);
  g.offer = null;
  if (g.kind === 'kaching') {
    // El pack se reconoce por SUS propiedades (deal, mercado y, con test A/B, variante abid/ab), no por la variante
    // de la visitante: Kaching cobra según las propiedades de las líneas. Un pack de otro país (carrito compartido de
    // clarene.co) o de una variante desconocida se muestra de forma genérica. La versión (v) NO cuenta: Kaching crea
    // una nueva cada vez que se guarda la oferta (p. ej. al cambiar el reparto del test A/B) y sigue cobrando igual;
    // lo que garantiza el precio es que el importe real del pack sea el verificado para su tramo.
    const sameMarket = (x) => !g.kaching.market || String(g.kaching.market) === String(x.market);
    const sameAb = (x) => (x.ab ? g.kaching.ab === x.ab.letter && g.kaching.abid === x.ab.abid : !g.kaching.ab);
    // con test A/B o con la configuración en vivo de Kaching, además el importe real del pack (sin contar los
    // códigos de descuento, que no son de Kaching) tiene que ser el del tramo
    const verifiedAmount = (x) => {
      if (!x.ab && !x.live) return true;
      const t = offerTier(x, g.qty);
      return !!t && Math.abs(amountBeforeCodes(g) - t.price) <= PRICE_TOLERANCE;
    };
    const o = offers.find((x) => x.deal === g.kaching.deal && sameMarket(x) && sameAb(x)
      && g.lines.every((l) => l.variant_id === x.variantId) && verifiedAmount(x));
    if (o) {
      g.kind = 'offer';
      g.offer = o;
    }
  } else if (g.kind === 'plain' && !Object.keys(g.props).length && !planOf(g.lines[0])) {
    // base de la oferta añadida sin propiedad Kaching (quick-add, página de colección…): para convertirla en pack hace
    // falta la variante de la visitante (en Latinoamérica, cuando Kaching la da)
    g.offer = offers.find((x) => x.variantId === g.variantId && !x.pending && (!x.ab || x.visitor)) || null;
  }
  g.tier = g.offer ? offerTier(g.offer, g.qty) : null;
  g.plan = g.kind === 'plain' ? planOf(g.lines[0]) : null;
  // Cantidad fija (solo se puede eliminar): una suscripción (otra unidad no garantiza ni el plan ni la oferta), un
  // producto con líneas anidadas (base de bienvenida, regalo: no crecerían con él) y los regalos o bases de bienvenida
  // de la landing que se queden sueltos.
  g.fixed = g.kind === 'plain' && (!!g.plan || g.children.length > 0 || !!landingGift(g.lines[0]) || landingWelcome(g.lines[0]));
  g.maxQty = g.fixed ? g.qty : g.offer ? g.offer.maxQty : g.kind === 'plain' ? 99 : g.qty;
  g.editable = (g.kind === 'offer' || g.kind === 'plain') && !g.fixed;
}

/**
 * Orden visual del carrito: primero la base de una oferta (pack de Kaching o base suelta), después los
 * complementarios en su orden de incorporación (/cart.js pone lo último añadido primero, así que se invierte).
 * No cambia el orden real del carrito ni los grupos.
 */
function displayOrder(groups) {
  const isBase = (g) => !!g.offer && (g.kind === 'offer' || g.kind === 'plain');
  return [...groups.filter(isBase), ...groups.filter((g) => !isBase(g)).reverse()];
}

function findGroup(groups, ref) {
  if (!ref) return null;
  if (ref.gid) {
    const g = groups.find((x) => x.gid === ref.gid);
    if (g) return g;
  }
  if (ref.deal && ref.id) return groups.find((x) => x.kaching && x.kaching.deal === ref.deal && x.kaching.id === ref.id && x.kind !== 'orphan-gift') || null;
  return null;
}

/** Referencia estable a un grupo (las keys cambian tras cada mutación). */
function groupRef(g) {
  return { gid: g.gid, deal: g.kaching?.deal, id: g.kaching?.id };
}

// ---------- planificación ----------

/**
 * Operaciones para dejar un grupo con `target` unidades.
 * Devuelve { ops: [{type:'add', items} | {type:'update', updates}], expect }.
 */
function planSetQty(group, target, { rand } = {}) {
  if (!Number.isInteger(target) || target < 0) throw new Error(`Cantidad no válida: ${target}`);
  if (!group.editable && target !== 0) throw new Error('Este producto solo se puede eliminar');
  if (target > group.maxQty) throw new Error(`Máximo ${group.maxQty} unidades en este pack`);
  if (target === group.qty && !needsGiftFix(group)) return { ops: [], expect: expectFor(group, target) };

  if (target === 0) return { ops: [{ type: 'update', updates: zeroAll(group) }], expect: { gone: true, ref: groupRef(group) } };

  if (group.kind === 'offer') return planOffer(group, target);
  if (group.kind === 'plain' && group.offer && giftTier(group.offer)?.qty === target) return planAdopt(group, target, rand);
  // base suelta → tramo con unidades gratis (2+1 de la variante A): sin las propiedades de Kaching las bases añadidas se
  // cobrarían todas y el importe no sería el anunciado; se crea el pack de Kaching (verificado al céntimo)
  const free = group.kind === 'plain' && group.offer ? offerTier(group.offer, target) : null;
  if (free?.free > 0) return planAdopt(group, target, rand);
  if (group.kind === 'plain') return planPlain(group, target);
  throw new Error('Este producto solo se puede eliminar');
}

function needsGiftFix(g) {
  if (g.kind !== 'offer') return false;
  const want = tierGifts(g.tier);
  return want.length ? g.giftQty !== want.length || missingGifts(g, want).length > 0 : g.giftQty > 0;
}

/** Regalos del tramo que el pack aún no tiene (por su id de Kaching). */
function missingGifts(g, want) {
  return want.filter((w) => !g.gifts.some((l) => giftIdOf(l) === w.id));
}

function zeroAll(g) {
  return Object.fromEntries(g.keys.map((k) => [k, 0]));
}

/** Reparte `target` sobre las líneas principales. Si todas son iguales (lo normal), la primera se queda con todo. */
function distribute(lines, target) {
  const same = lines.every((l) => l.variant_id === lines[0].variant_id && sig(l.properties) === sig(lines[0].properties));
  const out = {};
  let left = target;
  lines.forEach((l, i) => {
    const q = same ? (i === 0 ? target : 0) : Math.min(l.quantity, left);
    left -= q;
    out[l.key] = q;
  });
  return out;
}

function planOffer(g, target) {
  const tier = offerTier(g.offer, target);
  const want = tierGifts(tier);
  const ops = [];
  if (target > g.qty) {
    const items = [{ id: g.variantId, quantity: target - g.qty, properties: g.lines[0].properties }];
    // cada regalo del tramo que falta, en la misma petición (parent_id solo vale dentro de ella, docs/04 §1.8)
    for (const w of missingGifts(g, want)) {
      items.push({ id: w.variantId, quantity: 1, properties: buildGiftProps(g.kaching, w.id), parent_id: g.variantId });
    }
    // las keys solo valen antes de mutar: cualquier update va primero
    if (want.length && giftsToTrim(g, want)) ops.push({ type: 'update', updates: giftUpdates(g, want) });
    ops.push({ type: 'add', items });
  } else {
    const updates = target < g.qty ? distribute(g.lines, target) : {};
    if (!want.length) Object.assign(updates, giftUpdates(g, []));
    else if (giftsToTrim(g, want)) Object.assign(updates, giftUpdates(g, want));
    ops.push({ type: 'update', updates });
    // (solo pasa al reparar) el padre ya existe: cada regalo que falta se enlaza por parent_line_key en otra petición
    for (const w of missingGifts(g, want)) {
      ops.push({ type: 'add-gift', gift: { variantId: w.variantId, properties: buildGiftProps(g.kaching, w.id) }, ref: groupRef(g) });
    }
  }
  return { ops, expect: expectFor(g, target) };
}

/** Cantidades de las líneas de regalo: una unidad de cada regalo del tramo (want); el resto (repetidos, otros) a 0. */
function giftUpdates(g, want) {
  const left = new Map(want.map((w) => [w.id, 1]));
  const out = {};
  for (const l of g.gifts) {
    const id = giftIdOf(l);
    const q = Math.min(l.quantity, left.get(id) || 0);
    if (left.has(id)) left.set(id, left.get(id) - q);
    out[l.key] = q;
  }
  return out;
}

/** ¿Hay líneas de regalo que sobran (repetidas o de otro regalo)? */
function giftsToTrim(g, want) {
  const updates = giftUpdates(g, want);
  return g.gifts.some((l) => updates[l.key] !== l.quantity);
}

function planPlain(g, target) {
  if (target > g.qty) {
    const item = { id: g.variantId, quantity: target - g.qty };
    if (Object.keys(g.props).length) item.properties = g.props;
    return { ops: [{ type: 'add', items: [item] }], expect: { ref: groupRef(g), qty: target, plain: true } };
  }
  return { ops: [{ type: 'update', updates: distribute(g.lines, target) }], expect: { ref: groupRef(g), qty: target, plain: true } };
}

/**
 * Base sin propiedad Kaching que pasa al tramo con regalo: no se puede vincular la brocha (C2h),
 * así que se añade el pack completo con la forma del widget y después se quitan las líneas viejas.
 * Si la 2ª operación falla, el rollback borra el pack nuevo (nunca se pierde lo que había).
 */
function planAdopt(g, target, rand) {
  const offer = g.offer;
  const tier = offerTier(offer, target);
  const id = randomBundleId(rand);
  const main = buildMainProps(offer, tier, id);
  const items = [{ id: offer.variantId, quantity: target, properties: main }];
  for (const w of tierGifts(tier)) {
    items.push({
      id: w.variantId, quantity: 1,
      // con test A/B, el regalo lleva la variante del pack, como en el widget (sin ella Kaching aplica la A)
      properties: buildGiftProps({ id, deal: offer.deal, v: offer.version, ...(offer.ab ? { abid: offer.ab.abid, ab: offer.ab.letter } : {}) }, w.id),
      parent_id: offer.variantId,
    });
  }
  return {
    // las keys cambian tras el add: las líneas viejas se resuelven releyendo el carrito
    ops: [{ type: 'add', items }, { type: 'remove-group', ref: groupRef(g) }],
    expect: { ref: { deal: offer.deal, id }, qty: target, price: tier.price, ...giftExpect(tier), replaces: groupRef(g), exact: !!offer.live },
  };
}

/** Regalos que tiene que tener el pack tras la operación: gift (alguno) y giftIds (cuáles, uno de cada). */
function giftExpect(tier) {
  const want = tierGifts(tier);
  return { gift: want.length > 0, giftIds: want.map((w) => w.id) };
}

function expectFor(g, target) {
  const tier = g.offer && g.kind === 'offer' ? offerTier(g.offer, target) : null;
  // exact: al SUBIR de tramo con la configuración en vivo, el importe real tiene que ser exactamente el anunciado (ni
  // más ni menos): si Kaching cambió el precio y la página no lo sabía, se deshace y se enseña el nuevo. Al bajar no.
  return { ref: groupRef(g), qty: target, price: tier?.price ?? null, ...giftExpect(tier), plain: g.kind !== 'offer', exact: !!tier && !!g.offer?.live && target > g.qty };
}

/**
 * Textos del upgrade (titular, subtítulo y etiqueta accesible) con los importes ya formateados: delta (lo que sube el
 * total) y save (ahorro frente al precio normal). exact = el céntimo es seguro; withCode = hay
 * un código de pedido (code) ya descontado en el importe. gift: el tramo trae regalos.
 *  - Con la brocha (2+1 de España y Latinoamérica): el texto de siempre, escrito para ella.
 *  - Con otros regalos o ventajas (Pack Dúo de la variante B: Esponja + regalo misterioso + envío gratis): los nombra.
 *  - Sin regalo (1→2): «¡Llévate la 2ª base…!», como siempre.
 *  - free: el tramo cuesta lo mismo que lo que hay (1 → 1+1 de la variante A): «¡Llévate una 2.ª base GRATIS!».
 *  - from: unidades actuales. Si se suben varias de golpe (1 → 2+1 y 2+1 → 3+2 de la variante A): «¡Llévate 2 bases
 *    más por solo 39,95 €!», con los regalos y ventajas del tramo («regalo misterioso» si Kaching lo llama así) o, si no
 *    trae ninguno, cuántas de las bases añadidas son gratis (fromFree: las gratis que ya hay).
 */
function upgradeCopy(tier, { delta, save, exact, withCode, code, from, free, fromFree = 0 }) {
  const gifts = tierGifts(tier);
  const deltaText = `${exact ? '' : '≈ '}${delta}`;
  const approx = exact ? '' : ' · importe aproximado';
  const added = tier.qty - (from ?? tier.qty - 1);
  if (free) {
    return {
      gift: true,
      head: added === 1 ? `¡Llévate una ${tier.qty}.ª base GRATIS!` : `¡Llévate ${added} bases más GRATIS!`,
      sub: `Aprovecha tu ${tier.paid}+${tier.free} gratis sin pagar nada más${withCode ? ` · ${code || 'tu código'} ya descontado` : ''}`,
      label: `Gratis: ${tierName(tier)}, sin pagar nada más`,
    };
  }
  if (added > 1) {
    const extras = [
      ...gifts.map((g) => ((tier.mystery || []).includes(g.id) ? 'regalo misterioso' : g.title)).filter(Boolean),
      ...(tier.perks || []),
    ];
    const freeAdded = Math.min(added, (tier.free || 0) - fromFree);
    const freeText = freeAdded === 1 ? 'Una de las bases adicionales es GRATIS'
      : freeAdded === added ? `Las ${added} bases adicionales son GRATIS`
        : freeAdded > 1 ? `${freeAdded} de las bases adicionales son GRATIS` : tierName(tier);
    return {
      gift: true,
      head: `¡Llévate ${added} bases más por solo ${deltaText}!`,
      sub: `${extras.length ? `+ ${extras.join(' + ')}` : freeText}${withCode ? ` · ${code || 'tu código'} ya descontado` : ''}${approx}`,
      label: `${exact ? '' : 'aproximadamente '}${delta} más por ${added} bases más${extras.length ? `, con ${extras.join(', ')}` : ''}`,
    };
  }
  const brush = gifts.length === 1 && !tier.perks?.length && /^brocha/i.test(gifts[0].title || 'brocha');
  if (brush) {
    const what = `${tier.qty} bases + Brocha Fusión Doble`;
    return {
      gift: true,
      head: `Llévate una ${tier.qty}ª base + la brocha GRATIS por solo ${deltaText} más`,
      sub: withCode ? `${what} · ${code || 'tu código'} ya descontado${approx}` : what,
      label: `Llévate una ${tier.qty}ª base + la brocha GRATIS por solo ${exact ? '' : 'aproximadamente '}${delta} más`,
    };
  }
  if (gifts.length || tier.perks?.length) {
    const names = gifts.map((g) => g.title).filter(Boolean);
    const extras = [names.length ? `${names.join(' + ')} de regalo` : null, ...(tier.perks || [])].filter(Boolean);
    return {
      gift: true,
      head: `¡Llévate la ${tier.qty}.ª base por solo ${deltaText} más!`,
      sub: `+ ${extras.join(' + ')}${withCode ? ` · ${code || 'tu código'} ya descontado` : ''}${approx}`,
      label: `${exact ? '' : 'aproximadamente '}${delta} más por la ${tier.qty}.ª base${names.length ? `, con ${names.join(' y ')} de regalo` : ''}`,
    };
  }
  return {
    gift: false,
    head: `¡Llévate la ${tier.qty}ª base por solo ${deltaText} más!`,
    sub: `Duplica tu pedido y ahorra ${save}${withCode && !exact ? ' · importe aproximado' : ''}`,
    label: `${exact ? '' : 'aproximadamente '}${delta} más por la ${tier.qty}ª base`,
  };
}

/**
 * Upgrade al siguiente tramo (el de menos unidades por encima de las actuales), o null si no hay. Con tramos
 * seguidos es el de qty + 1, como siempre; con saltos (variante A: 1, 2 y 4) de 2 unidades se pasa a 4.
 */
function nextTier(group) {
  if (!group.offer || !(group.kind === 'offer' || group.kind === 'plain')) return null;
  // oferta de la referencia que Kaching aún no ha confirmado: se reconoce, pero no se anuncia ningún upgrade
  if (group.offer.pending) return null;
  if (group.qty >= group.offer.maxQty) return null;
  return group.offer.tiers.find((t) => t.qty > group.qty && t.qty <= group.offer.maxQty) || null;
}

/**
 * Cantidad a la que llevan «−» (dir −1) y «+» (dir 1). En un pack de Kaching, al tramo anterior o siguiente (o a 0
 * desde el primero); con tramos seguidos es qty ± 1, como siempre. Base suelta de una oferta: «+» al siguiente tramo y
 * «−» una unidad. En los demás productos, una unidad.
 */
function stepQty(group, dir) {
  // base suelta de una oferta: «+» al siguiente tramo (1 → 2+1, nunca 2 bases sueltas si Kaching no lo ofrece)
  if (group.kind === 'plain' && group.offer && dir > 0) return nextTier(group)?.qty ?? group.qty + 1;
  if (group.kind !== 'offer' || !group.offer) return group.qty + dir;
  if (dir > 0) return nextTier(group)?.qty ?? group.qty + 1;
  const prev = Math.max(0, ...group.offer.tiers.filter((t) => t.qty < group.qty).map((t) => t.qty));
  return prev || group.qty - 1;
}

// ---------- representación de los packs ----------

/**
 * Cómo se muestra un grupo: unidades compradas (número del selector) y bases gratis (líneas de regalo).
 * Solo se separan las bases gratis en un pack de Kaching identificado por sus propiedades (__kaching_bundles
 * main + deal de la config → kind 'offer'), con un tramo de la config que las incluya (paid/free salen de
 * buyQuantity/getQuantity) y cuyo importe real en /cart.js es el del tramo (la Function lo ha aplicado).
 * Bases sueltas, tramos desconocidos o packs sin el descuento aplicado se muestran con sus unidades reales.
 * Solo es presentación: el selector sigue pidiendo cantidades reales (qty ± 1 = tramo anterior / siguiente).
 */
function packView(g) {
  const t = g.kind === 'offer' ? g.tier : null;
  const ok = t && t.free > 0 && t.paid > 0 && t.paid + t.free === g.qty && g.finalCents <= t.price + PRICE_TOLERANCE;
  if (!ok) {
    // tramo sin unidades gratis pero con nombre propio en Kaching (el «Dúo» de Latinoamérica: la 2ª base cuesta)
    const named = t && t.free === 0 && t.title && t.qty > 1 && t.qty === g.qty && g.finalCents <= t.price + PRICE_TOLERANCE;
    return { paid: g.qty, free: 0, label: named ? t.title : g.kind === 'offer' && g.qty > 1 ? `Pack ${g.qty} unidades` : '' };
  }
  return { paid: t.paid, free: t.free, label: tierName(t) };
}

/**
 * Ventajas solo de texto del pack que hay en el carrito (Pack Dúo de la variante B: «regalo MISTERIOSO», «Envío gratis»).
 * Solo si las líneas son ese tramo y Kaching lo cobra a su precio; con otro tramo (1 unidad, variante A) no hay ninguna.
 * Son informativas: no añaden líneas ni cambian importes.
 * image: la imagen del widget de Kaching (media: mediaImageGID → { url }, docs/referencia/kaching-media.snapshot.json),
 * solo si el regalo sigue usando ese mismo archivo; si no, null (se queda el icono).
 */
function packPerks(g, media = {}) {
  const t = g.kind === 'offer' ? g.tier : null;
  if (!t?.perks?.length || t.qty !== g.qty || g.finalCents > t.price + PRICE_TOLERANCE) return [];
  return t.perks.map((text, i) => ({ text, shipping: /env[ií]o/i.test(text), image: media[t.perkMedia?.[i]]?.url || null }));
}

/**
 * Nombre de un tramo, como en Kaching («Compra 1 y llévate 1 GRATIS», «1 unidad»). Cabe en una línea a 360 px.
 * Las ofertas de Latinoamérica traen el título de Kaching («Compra 1 y recibe 1 GRATIS», «Dúo Base Inteligente»).
 */
function tierName(t) {
  if (t?.title) return t.title;
  if (t?.free > 0) return `Compra ${t.paid} y llévate ${t.free} GRATIS`;
  return t?.qty > 1 ? `${t.qty} unidades` : '1 unidad';
}

// ---------- verificación ----------

/** Comprueba el carrito releído frente a lo esperado. Devuelve { ok, reason }. */
function verify(cart, config, expect) {
  const groups = groupCart(cart, config);
  if (expect.gone) {
    return findGroup(groups, expect.ref) ? { ok: false, reason: 'el grupo sigue en el carrito' } : { ok: true };
  }
  const g = findGroup(groups, expect.ref);
  if (!g) return { ok: false, reason: 'no se encuentra el grupo' };
  if (g.qty !== expect.qty) return { ok: false, reason: `cantidad ${g.qty} ≠ ${expect.qty}` };
  if (expect.replaces && findGroup(groups, expect.replaces)) return { ok: false, reason: 'las líneas antiguas siguen en el carrito' };
  if (expect.gift) {
    const ids = expect.giftIds || [];
    if (g.giftQty !== (ids.length || 1)) return { ok: false, reason: `regalo x${g.giftQty}` };
    if (ids.some((id) => !g.gifts.some((l) => giftIdOf(l) === id))) return { ok: false, reason: 'falta un regalo del pack' };
    if (g.giftFinalCents > GIFT_TOLERANCE) return { ok: false, reason: 'el regalo se está cobrando' };
  } else if (g.giftQty > 0) {
    return { ok: false, reason: 'queda un regalo que no corresponde' };
  }
  if (expect.exact && expect.price != null && Math.abs(amountBeforeCodes(g) - expect.price) > PRICE_TOLERANCE) {
    return { ok: false, reason: `el precio real ${amountBeforeCodes(g)} no es el anunciado ${expect.price}`, priceChanged: true };
  }
  // El cliente nunca debe pagar más de lo prometido. Si sale más barato, se acepta (manda /cart.js).
  if (expect.price != null && g.finalCents > expect.price + PRICE_TOLERANCE) {
    return { ok: false, reason: `precio ${g.finalCents} > ${expect.price}` };
  }
  return { ok: true, group: g };
}

// ---------- red de seguridad de regalos ----------

/**
 * Regalos que Shopify está cobrando (huérfanos, duplicados o de un pack que ya no califica).
 * Devuelve un objeto `updates` para /cart/update.js, o null si todo está bien.
 */
function giftGuard(cart) {
  const updates = {};
  const seen = new Map(); // deal:id → unidades gratis ya contadas
  for (const line of cart?.items || []) {
    const k = parseKaching(line);
    if (!k?.gift) continue;
    if (line.final_line_price <= GIFT_TOLERANCE) {
      seen.set(`${k.deal}:${k.id}`, (seen.get(`${k.deal}:${k.id}`) || 0) + line.quantity);
      continue;
    }
    // ¿Hay alguna unidad gratis en esta línea? (precio unitario × cantidad > final)
    const unit = line.original_price || line.price || 0;
    const freeUnits = unit > 0 ? Math.round((line.original_line_price - line.final_line_price) / unit) : 0;
    updates[line.key] = Math.max(0, Math.min(freeUnits, line.quantity));
  }
  return Object.keys(updates).length ? updates : null;
}

// ---------- precio del upgrade (CTA) ----------
// Todo en céntimos enteros. El total de partida es siempre el real de /cart.js.
// Sin código de pedido la diferencia es exacta: precio del tramo (verificado en /cart.js) − importe real del pack.
// Con un código de porcentaje (BEAUTY), Shopify redondea el descuento y no hay forma de preguntarle antes del clic.
// Se prueban los modelos de redondeo que encajan con TODAS las mediciones reales (E1-1/2/3) y con el carrito actual;
// si todos dan el mismo céntimo el importe es exacto, si no se marca como aproximado (≈).

/** Modelos de redondeo candidatos. `total-round` está descartado por E1-2 (daría 6,00 € y Shopify aplicó 5,99 €). */
const ROUNDING_MODELS = {
  'line-floor': (lines, bp) => lines.reduce((a, c) => a + Math.floor((c * bp) / 10000), 0),
  'line-round': (lines, bp) => lines.reduce((a, c) => a + Math.floor((c * bp + 5000) / 10000), 0),
  'total-floor': (lines, bp) => Math.floor((lines.reduce((a, c) => a + c, 0) * bp) / 10000),
};

/** Porcentaje en puntos básicos enteros (15 % → 1500), o null. */
function basisPoints(app) {
  if (app.value_type !== 'percentage' || app.value == null) return null;
  const bp = Math.round(Number(app.value) * 100);
  return Number.isFinite(bp) && bp > 0 && bp <= 10000 ? bp : null;
}

function cartLevelAllocated(cart) {
  return (cart?.cart_level_discount_applications || []).reduce((a, d) => a + (d.total_allocated_amount || 0), 0);
}

/** Hay códigos de producto (no de pedido) que alterarían el precio del tramo. */
function hasLineLevelCode(cart) {
  return (cart?.items || []).some((l) => (l.line_level_discount_allocations || []).some((a) => a.discount_application?.type === 'discount_code'));
}

/**
 * Cuánto sube el total del carrito al pasar el grupo a `tier`.
 * Devuelve { deltaCents, exact, withCode }. Si exact = false, la UI lo muestra como aproximado.
 */
function upgradeDelta(cart, group, tier) {
  const groupNow = group.finalCents + group.giftFinalCents + (group.childrenFinalCents || 0);
  const itemsDelta = tier.price - groupNow;
  const apps = cart?.cart_level_discount_applications || [];
  if (hasLineLevelCode(cart)) return { deltaCents: itemsDelta, exact: false, withCode: true };
  if (!apps.length) return { deltaCents: itemsDelta, exact: true, withCode: false };
  if (apps.length > 1) return { deltaCents: approxDelta(cart, itemsDelta), exact: false, withCode: true };

  const [app] = apps;
  const allocated = app.total_allocated_amount || 0;
  if (app.value_type === 'fixed_amount') {
    // un importe fijo ya aplicado entero no cambia al subir el subtotal
    const full = Math.round(Number(app.value) * 100) === allocated;
    return { deltaCents: itemsDelta, exact: full, withCode: true };
  }
  const bp = basisPoints(app);
  if (bp == null) return { deltaCents: approxDelta(cart, itemsDelta), exact: false, withCode: true };

  // Líneas actuales (importes reales) y líneas previstas tras el upgrade (reparto del tramo en la config).
  const inGroup = new Set(group.keys);
  const others = cart.items.filter((l) => !inGroup.has(l.key)).map((l) => l.final_line_price);
  const now = cart.items.map((l) => l.final_line_price);
  const next = [...others, ...(tier.lines || [tier.price])];

  // Solo valen los modelos que reproducen el descuento real de ESTE carrito.
  const models = Object.entries(ROUNDING_MODELS).filter(([, f]) => f(now, bp) === allocated);
  if (!models.length) return { deltaCents: approxDelta(cart, itemsDelta), exact: false, withCode: true };
  const totals = new Set(models.map(([, f]) => next.reduce((a, c) => a + c, 0) - f(next, bp)));
  const [newTotal] = totals;
  return { deltaCents: newTotal - cart.total_price, exact: totals.size === 1, withCode: true, percentBp: bp };
}

/**
 * Precio del grupo con el descuento de pedido (BEAUTY) incluido, SOLO cuando se conoce exacto:
 * si es el único grupo con importe, todo el descuento real de Shopify le corresponde. Si no, null
 * (el descuento se muestra en el resumen, igual que en el checkout).
 */
function groupPriceAfterOrderDiscount(cart, groups, g) {
  const allocated = cartLevelAllocated(cart);
  if (!allocated) return null;
  const amount = (x) => x.finalCents + x.giftFinalCents + (x.childrenFinalCents || 0);
  const priced = groups.filter((x) => amount(x) > 0);
  if (priced.length !== 1 || priced[0].gid !== g.gid) return null;
  return amount(g) - allocated;
}

/**
 * Lo que le toca a cada línea de los códigos de pedido (BEAUTY, BIENVENIDA10…), según el reparto real de Shopify:
 * /cart.js trae en `items[].discounts` el importe de cada descuento en esa línea (total de línea, ya redondeado).
 * Solo se usa si las líneas suman exactamente lo que Shopify aplica al carrito; si no, null (no atribuible).
 */
function orderCodeAllocations(cart) {
  const apps = cart?.cart_level_discount_applications || [];
  const titles = new Set(apps.map((d) => d.title));
  const byKey = new Map();
  let sum = 0;
  for (const l of cart?.items || []) {
    const per = new Map();
    for (const d of l.discounts || []) {
      if (!titles.has(d.title) || !(d.amount > 0)) continue;
      per.set(d.title, (per.get(d.title) || 0) + d.amount);
      sum += d.amount;
    }
    byKey.set(l.key, per);
  }
  return sum === cartLevelAllocated(cart) ? byKey : null;
}

/**
 * Precio de un producto normal (grupo 'plain'), con un único precio tachado:
 *  1. con un código realmente aplicado a sus líneas: precio antes del código → precio final real + «con CÓDIGO»;
 *  2. sin código: precio original → final si hay descuento automático; si no, precio de referencia (compare-at) → venta;
 *  3. sin nada: solo el precio.
 * Importes de línea (como el resto del carrito). `compareAtUnit` = precio de referencia de la variante (0 si no hay).
 * Devuelve { was, now, codes } (was = null si no se tacha nada), o null si el grupo no es un producto normal.
 */
function plainPrice(cart, groups, g, compareAtUnit = 0) {
  if (g.kind !== 'plain') return null;
  const lineCodes = new Map();
  for (const l of g.lines) {
    for (const a of l.line_level_discount_allocations || []) {
      if (a.discount_application?.type !== 'discount_code' || !(a.amount > 0)) continue;
      const t = a.discount_application.title;
      lineCodes.set(t, (lineCodes.get(t) || 0) + a.amount);
    }
  }
  const alloc = orderCodeAllocations(cart);
  const orderCodes = new Map();
  if (alloc) {
    for (const l of g.lines) for (const [t, a] of alloc.get(l.key) || []) orderCodes.set(t, (orderCodes.get(t) || 0) + a);
  } else {
    // reparto no disponible: solo se atribuye si el grupo es el único con importe (todo el descuento es suyo)
    const withCode = groupPriceAfterOrderDiscount(cart, groups, g);
    if (withCode != null) for (const d of cart.cart_level_discount_applications) orderCodes.set(d.title, d.total_allocated_amount || 0);
  }
  const sumOf = (m) => [...m.values()].reduce((a, c) => a + c, 0);
  const lineAmt = sumOf(lineCodes), orderAmt = sumOf(orderCodes);
  if (lineAmt + orderAmt > 0) {
    const codes = [...new Set([...lineCodes.keys(), ...orderCodes.keys()])].filter((t) => (lineCodes.get(t) || 0) + (orderCodes.get(t) || 0) > 0);
    return { was: g.finalCents + lineAmt, now: g.finalCents - orderAmt, codes };
  }
  const now = g.finalCents;
  if (g.originalCents > now) return { was: g.originalCents, now, codes: [] };
  const ref = (compareAtUnit || 0) * g.qty;
  return { was: ref > now ? ref : null, now, codes: [] };
}

/**
 * Producto normal que Shopify deja a 0 € con un descuento del 100 % (p. ej. la 2ª unidad del Tinte con la oferta
 * 1+1 de Kaching): la tarjeta dice «GRATIS» en lugar de «0,00 €». Solo con el importe real de /cart.js: el grupo
 * entero a 0 € y con precio original > 0. Una unidad rebajada (el Colorete a 5 €) no es gratis.
 */
function isFreeLine(g, p) {
  return g?.kind === 'plain' && !!p && g.originalCents > 0 && g.finalCents === 0 && p.now === 0;
}

/**
 * Precio de un pack de Kaching: de la oferta (Base Inteligente) o de otro bundle (Pack Buena Cara). Siempre se tacha el precio original de referencia
 * (unidades × precio original, como sin código) y se destaca el importe real con el código de pedido que Shopify
 * atribuye a las líneas del pack (`discounts` de /cart.js). Sin reparto fiable: solo si es el único grupo con
 * importe (como antes); si no, el precio real del pack sin código. Devuelve { was, now, codes } o null.
 */
function offerPrice(cart, groups, g) {
  // también los demás packs de Kaching: con dos o más (p. ej. dos Packs Buena Cara) cada tarjeta lleva lo que Shopify
  // le reparte del código; la regla de «único grupo con importe» solo queda para cuando ese reparto no cuadra
  if (g.kind !== 'offer' && g.kind !== 'kaching') return null;
  const was = g.originalCents;
  const alloc = orderCodeAllocations(cart);
  const codes = new Map();
  if (alloc) {
    for (const l of g.lines) for (const [t, a] of alloc.get(l.key) || []) codes.set(t, (codes.get(t) || 0) + a);
  } else {
    const withCode = groupPriceAfterOrderDiscount(cart, groups, g);
    if (withCode != null) {
      return { was, now: withCode, codes: (cart.cart_level_discount_applications || []).map((d) => d.title) };
    }
  }
  const amt = [...codes.values()].reduce((a, c) => a + c, 0);
  return { was, now: g.finalCents - amt, codes: [...codes.keys()] };
}

/**
 * Precio de un producto normal de `lineCents` en el carrito si se añadiera ahora, con el código de pedido activo.
 * No toca el carrito. Shopify calcula el descuento del pedido (modelos de redondeo que reproducen el de ESTE
 * carrito, como en upgradeDelta) y lo reparte entre las líneas en proporción a su importe, a la baja; los
 * céntimos que sobran los da a algunas líneas (en las mediciones, casi siempre a la recién añadida, que va
 * primera). Solo es exacto si todos los modelos coinciden y no sobra ningún céntimo; si no, aproximado (≈).
 * null (precio habitual) si no se puede saber si el producto es elegible: sin código, varios códigos, código de
 * producto, código limitado a ciertos productos (target_selection ≠ 'all'), importe fijo o carrito vacío.
 */
function addPriceWithCode(cart, lineCents) {
  const apps = cart?.cart_level_discount_applications || [];
  if (apps.length !== 1 || !(lineCents > 0) || !(cart.items || []).length || hasLineLevelCode(cart)) return null;
  const [app] = apps;
  if (app.target_selection !== 'all' || (app.allocation_method && app.allocation_method !== 'across')) return null;
  const bp = basisPoints(app);
  if (bp == null) return null;
  const now = cart.items.map((l) => l.final_line_price);
  const next = [lineCents, ...now];
  const sub = next.reduce((a, c) => a + c, 0);
  const allocated = app.total_allocated_amount || 0;
  const models = Object.values(ROUNDING_MODELS).filter((f) => f(now, bp) === allocated);
  const totals = models.length ? [...new Set(models.map((f) => f(next, bp)))] : [Math.floor((sub * bp) / 10000)];
  const total = totals[0];
  const share = (c) => Math.floor((c * total) / sub);
  const left = total - next.reduce((a, c) => a + share(c), 0);
  return { cents: lineCents - share(lineCents) - (left > 0 ? 1 : 0), exact: models.length > 0 && totals.length === 1 && left === 0 };
}

/**
 * Precio de referencia (compare-at) de la variante de una línea, buscado en productos con la forma de
 * clarene-cart-product-json. 0 si no hay, o si el precio conocido no coincide con el de la línea.
 */
function variantCompareAt(products, line) {
  if (!line) return 0;
  for (const p of products || []) {
    const v = (p.variants || []).find((x) => String(x.id) === String(line.variant_id))
      || (String(p.variantId) === String(line.variant_id) ? p : null);
    if (v) return v.price === line.original_price && v.compareAt > v.price ? v.compareAt : 0;
  }
  return 0;
}

function approxDelta(cart, itemsDelta) {
  const sub = cart.items_subtotal_price || 0;
  if (sub <= 0) return itemsDelta;
  return Math.round((itemsDelta * (sub - cartLevelAllocated(cart))) / sub);
}

/**
 * Texto del envío en el resumen. Shopify (Envío y entrega, perfil general) da «Envío Gratuito» desde
 * `freeShippingMin` céntimos y lo compara con el importe DESPUÉS de descuentos (Kaching y códigos), sin impuestos:
 * es `total_price` de /cart.js. Por debajo, el coste depende de la dirección (España 2,95 €; otras zonas sin
 * envío), así que no se da un importe: «Calculado en el checkout». Sin umbral configurado: siempre gratis (antes).
 */
function shippingSummary(cart, config = {}) {
  // El umbral está en la moneda de config.freeShippingCurrency (EUR). En otra moneda (Latinoamérica) Shopify lo
  // convierte y por debajo no hay envío: no se puede asegurar «Gratis» → «Calculado en el checkout».
  if (config.freeShippingCurrency && cart?.currency && cart.currency !== config.freeShippingCurrency) {
    return { free: false, text: config.shippingPendingText || 'Calculado en el checkout' };
  }
  const min = Number(config.freeShippingMin);
  const free = !Number.isFinite(min) || config.freeShippingMin == null || (cart?.total_price || 0) >= min;
  return free
    ? { free: true, text: config.shippingText || 'Gratis' }
    : { free: false, text: config.shippingPendingText || 'Calculado en el checkout' };
}

/** Ahorro total del carrito frente al precio original (Kaching + códigos). */
function cartSavings(cart) {
  return Math.max(0, (cart?.original_total_price || 0) - (cart?.total_price || 0));
}

/**
 * Ritmo de un plan de venta a partir de su opción o su nombre («Entregar cada mes», «cada 2 semanas»).
 * Devuelve { every: 'cada mes', per: 'al mes', adj: 'mensual', short: '/mes' } (adj solo si es cada 1), o null si no se entiende.
 */
function planCadence(plan) {
  const text = [...(plan?.options || []).map((o) => o.value), plan?.name].filter(Boolean).join(' · ');
  const m = text.match(/cada\s+(?:(\d+)\s+)?(d[ií]as?|semanas?|mes(?:es)?|años?)/i);
  if (!m) return null;
  const n = m[1] ? Number(m[1]) : 1;
  const u = m[2].toLowerCase();
  const unit = u.startsWith('d') ? ['día', 'días', 'al día', 'diaria'] : u.startsWith('s') ? ['semana', 'semanas', 'a la semana', 'semanal']
    : u.startsWith('m') ? ['mes', 'meses', 'al mes', 'mensual'] : ['año', 'años', 'al año', 'anual'];
  if (n === 1) return { every: `cada ${unit[0]}`, per: unit[2], adj: unit[3], short: `/${unit[0]}` };
  return { every: `cada ${n} ${unit[1]}`, per: `cada ${n} ${unit[1]}`, adj: null, short: ` cada ${n} ${unit[1]}` };
}

/**
 * Cobro recurrente del carrito: líneas con un plan de venta de entregas recurrentes. amount = lo que cuesta cada entrega
 * según Shopify (per_delivery_price × cantidad; sin envío ni lo que solo va en el primer pedido); cadence = planCadence,
 * o null si hay planes con ritmos distintos. null si no hay suscripción.
 */
function recurringSummary(cart) {
  const lines = (cart?.items || []).filter((l) => planOf(l) && planOf(l).recurring_deliveries !== false);
  if (!lines.length) return null;
  const amount = lines.reduce((a, l) => a + (l.selling_plan_allocation.per_delivery_price ?? l.selling_plan_allocation.price ?? 0) * l.quantity, 0);
  const cadences = [...new Set(lines.map((l) => JSON.stringify(planCadence(planOf(l)))))];
  return { amount, cadence: cadences.length === 1 ? JSON.parse(cadences[0]) : null, lines };
}

/**
 * Beneficios que Shopify Flow entrega DESPUÉS del pago (saldo de tienda del «Regalo misterioso»), con las mismas
 * condiciones que los flujos (docs/referencia/regalos-tras-el-pago.json): la regla se cumple si hay una línea de su
 * producto (productId) o variante (variantId) y la moneda es la suya. Devuelve [{ gid, rule }]: el grupo de esa línea.
 * No son líneas del carrito ni cambian importes.
 */
function afterPayPerks(cart, groups, rules = []) {
  const out = [];
  for (const r of rules) {
    if (r.currency && cart?.currency && cart.currency !== r.currency) continue;
    const hit = (l) => (r.productId && String(l.product_id) === String(r.productId)) || (r.variantId && String(l.variant_id) === String(r.variantId));
    const g = groups.find((x) => [...x.lines, ...x.children].some(hit));
    if (g && !out.some((o) => o.gid === g.gid && o.rule.text === r.text)) out.push({ gid: g.gid, rule: r });
  }
  return out;
}

/**
 * Ampliación de la landing de compra única (docs/15): la base suelta de 1 unidad (producto propio de la landing, sin
 * Kaching, así que nextTier no da nada) se cambia por el pack de Shopify Bundles + su regalo, las mismas líneas que
 * añade la landing. Reglas en config.landingUpgrades (docs/referencia/ampliaciones-landing.json). Solo con 1 unidad,
 * sin plan, sin líneas anidadas, sin el pack ya en el carrito y si el pack cuesta más que la base.
 * Devuelve la regla con { key, delta } o null.
 */
function landingUpgradeFor(cart, g, config) {
  if (!g || g.kind !== 'plain' || g.plan || g.qty !== 1 || g.lines.length !== 1 || g.children.length) return null;
  const line = g.lines[0];
  for (const u of config?.landingUpgrades || []) {
    if (String(line.variant_id) !== String(u.from.variantId)) continue;
    const origin = line.properties?._cls_origin;
    if (origin && u.from.origin && origin !== u.from.origin) continue;
    if ((cart?.items || []).some((l) => String(l.variant_id) === String(u.pack.variantId))) continue;
    const delta = u.pack.price - g.originalCents;
    if (!(delta > 0)) continue;
    return { ...u, key: line.key, delta };
  }
  return null;
}

/** Tras la ampliación: sin la base, 1 pack a su precio y sin plan, y 1 regalo a 0 €. */
function verifyLandingUpgrade(cart, u) {
  const items = cart?.items || [];
  const has = (l, props) => Object.entries(props || {}).every(([k, v]) => l.properties?.[k] === v);
  const packs = items.filter((l) => String(l.variant_id) === String(u.pack.variantId));
  const gifts = items.filter((l) => String(l.variant_id) === String(u.gift.variantId) && has(l, u.gift.properties));
  if (items.some((l) => l.key === u.key)) return { ok: false, reason: 'base_still_there' };
  if (packs.length !== 1 || packs[0].quantity !== 1 || packs[0].selling_plan_allocation) return { ok: false, reason: 'pack' };
  if (packs[0].original_line_price !== u.pack.price) return { ok: false, reason: 'pack_price' };
  if (gifts.length !== 1 || gifts[0].quantity !== 1 || gifts[0].final_line_price !== 0) return { ok: false, reason: 'gift' };
  return { ok: true };
}

/** Imagen del pack de la landing en el carrito: la principal de la landing (solo para ese pack). */
function landingPackImage(config, line) {
  if (!line) return null;
  return (config?.landingUpgrades || []).find((u) => u.image && String(u.pack.productId) === String(line.product_id))?.image || null;
}

function formatMoney(cents, currency = 'EUR', locale = 'es-ES') {
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format((cents || 0) / 100);
}

/**
 * Packs nativos de Shopify (bundles con componentes, p. ej. el Mega Pack de la landing de Smooche): el carrito los
 * presenta con su nombre corto y el desglose «Incluye: N × producto». Qué productos se presentan así lo declara
 * config.componentPacks (por id de producto); qué lleva cada pack sale de Shopify (line_item.item_components en
 * Liquid, snippet clarene-cart-components: variante del pack → [[variante, unidades por pack, título]]), porque
 * /cart.js solo trae has_components.
 */
function componentPack(config, line) {
  // por id de producto: {{ cart | json }} (carrito inicial y respuesta de los adds) no trae has_components
  if (!line) return null;
  return (config?.componentPacks || []).find((p) => String(p.productId) === String(line.product_id)) || null;
}

/** Mapa de componentes de un HTML de sección (clarene-cart-state) o de un JSON ya leído; null si no hay. */
function componentsFrom(src) {
  let m = src;
  if (typeof src === 'string') {
    const t = src.match(/<script[^>]*data-cc-cart-components[^>]*>([\s\S]*?)<\/script>/);
    if (!t) return null;
    try { m = JSON.parse(t[1]); } catch { return null; }
  }
  return m && typeof m === 'object' && !Array.isArray(m) ? m : null;
}

/** Filas del desglose para `packs` packs: [{ qty, title }], con el nombre corto declarado si lo hay. null si no se sabe. */
function packIncludes(pack, comps, packs) {
  if (!Array.isArray(comps) || !comps.length) return null;
  const rows = comps
    .map(([variantId, perPack, title]) => ({ qty: Number(perPack) * packs, title: pack?.labels?.[String(variantId)] || title }))
    .filter((r) => r.qty > 0 && r.title);
  return rows.length ? rows : null;
}

// ---- ledger.mjs ----
// Clarène Cart · formato de la anotación _cc (docs/07-analitica.md). Sin dependencias: lo usan el carrito (src/analytics.mjs)
// y el cálculo en el pedido (src/attribution.mjs, píxel y script de pedidos). Importes en centésimas, como /cart.js.
// Sintaxis ES2017 (sin encadenamiento opcional, sin fusión nula ni catch sin variable): el editor de píxeles de Shopify
// rechaza lo posterior (docs/07 §11; lo comprueba tests/unit/pixel-syntax.test.mjs).
//
// Anotación _cc (atributo del carrito → «Detalles adicionales» del pedido):
//   { v: 1, m: mercado, c: moneda, fx: unidades de la moneda por 1 € (Shopify.currency.rate),
//     u: upgrades [{ k: id del pack de Kaching | null, vid: variante, d: deal, f: unidades antes, t: unidades después,
//                    b: importe del pack antes (sin códigos), bv: importe de la variante antes (sin códigos),
//                    x: importe anunciado del tramo destino (sin códigos) | null }],
//     r: «Completa tu rutina» [{ v: variante, q: unidades añadidas, h: unidades pagadas que ya había, k: id del pack | null }] }

const CC_ATTR = '_cc';
// Destino de los eventos cc_* (carrito y píxel): solo esta propiedad de GA4, no las otras etiquetas de la página (Google Ads)
const GA4_ID = 'G-XVWB6FB5N9';
const MAX_ENTRIES = 12;

function emptyLedger() { return { v: 1, u: [], r: [] }; }

/** Lee la anotación (texto del atributo u objeto). Si no es válida, vacía: nunca rompe nada. */
function parseLedger(value) {
  try {
    const x = typeof value === 'string' ? (value ? JSON.parse(value) : null) : value;
    if (!x || x.v !== 1) return emptyLedger();
    return Object.assign({}, x, { u: Array.isArray(x.u) ? x.u.filter(Boolean) : [], r: Array.isArray(x.r) ? x.r.filter(Boolean) : [] });
  } catch (err) {
    return emptyLedger();
  }
}

/** Texto del atributo; '' (Shopify borra la clave) si no queda nada que atribuir. */
function serializeLedger(l) {
  if (!l || (!l.u.length && !l.r.length)) return '';
  const { v, m, c, fx, u, r } = l;
  return JSON.stringify({ v, m, c, fx, u: u.slice(-MAX_ENTRIES), r: r.slice(-MAX_ENTRIES) });
}

/** Id del pack de Kaching de una línea (propiedad __kaching_bundles) y si es un regalo. */
function kachingOf(props) {
  const raw = props && props.__kaching_bundles;
  if (!raw) return null;
  try {
    const k = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return k && k.id ? { id: String(k.id), gift: !!k.gift, deal: k.deal || null } : null;
  } catch (err) {
    return null;
  }
}

// ---- analytics.mjs ----
// Clarène Cart · medición de upgrades y «Completa tu rutina» (docs/07-analitica.md).
// - Eventos propios de GA4 (cc_*) con el gtag que ya carga la app de Google: clic, éxito (solo con el carrito confirmado
//   por Shopify) y fallo. No se repiten add_to_cart ni purchase: esos los envía la app de Google.
// - Anotación _cc en los atributos del carrito, dentro de las MISMAS peticiones que hace el carrito (sin peticiones
//   nuevas), para atribuir en el pedido. No toca líneas, propiedades, precios, regalos ni Kaching.
// Sin datos personales: ids de producto y variante, tramos, importes, mercado y moneda.


const codeAllocs = (l) => (l.line_level_discount_allocations || []).reduce((a, x) => a + (x.discount_application?.type === 'discount_code' ? x.amount || 0 : 0), 0);
const lineBefore = (l) => (l.final_line_price || 0) + codeAllocs(l);
const isGift = (l) => !!kachingOf(l.properties)?.gift;
const linesOf = (cart, v) => (cart?.items || []).filter((l) => l.variant_id === Number(v) && !isGift(l));

/** Unidades pagadas de una variante (sin regalos de Kaching ni líneas gratis), como las cuenta el pedido. */
function paidUnits(cart, v) {
  return linesOf(cart, v).filter((l) => lineBefore(l) > 0).reduce((a, l) => a + l.quantity, 0);
}
/** Importe de una variante antes de códigos de descuento. */
function variantBefore(cart, v) {
  return linesOf(cart, v).reduce((a, l) => a + lineBefore(l), 0);
}

const ledgerOf = (cart) => parseLedger(cart?.attributes?.[CC_ATTR]);
const withCtx = (l, ctx = {}) => ({ ...l, m: ctx.market ?? l.m ?? null, c: ctx.currency ?? l.c ?? null, fx: ctx.fx ?? l.fx ?? 1 });

/** Upgrade de un pack: una entrada por pack (1+1 → 2+1 → … queda como un solo upgrade desde el tramo de origen). */
function noteUpgrade(cart, { group: g, target, expect }, ctx) {
  const L = ledgerOf(cart);
  const k = expect?.ref?.id ?? g.kaching?.id ?? null; // al adoptar una base suelta, el pack nuevo
  const key = (e) => (e.k ?? `v${e.vid}`);
  const prev = L.u.find((e) => key(e) === (k ?? `v${g.variantId}`)) || (k && L.u.find((e) => !e.k && e.vid === g.variantId));
  const x = expect?.price ?? null;
  const u = prev
    ? L.u.map((e) => (e === prev ? { ...e, k: k ?? e.k, t: target, x } : e))
    : [...L.u, { k, vid: g.variantId, d: g.offer?.deal ?? g.kaching?.deal ?? null, f: g.qty, t: target, b: amountBeforeCodes(g), bv: variantBefore(cart, g.variantId), x }];
  return withCtx({ ...L, u }, ctx);
}

/** Producto o pack añadido desde «Completa tu rutina» (la línea principal es la primera). */
function noteUpsell(cart, items, ctx) {
  const L = ledgerOf(cart);
  const main = items?.[0];
  if (!main) return L;
  const v = Number(main.id);
  const k = kachingOf(main.properties)?.id ?? null;
  const q = Number(main.quantity) || 1;
  const prev = !k && L.r.find((e) => !e.k && e.v === v);
  const r = prev ? L.r.map((e) => (e === prev ? { ...e, q: e.q + q } : e)) : [...L.r, { v, q, h: paidUnits(cart, v), k }];
  return withCtx({ ...L, r }, ctx);
}

/** Bajar o quitar desde el carrito: lo que deja de estar no se atribuye (subir con + no es un upsell). */
function noteSetQty(cart, { group: g, target }) {
  const L = ledgerOf(cart);
  if (!L.u.length && !L.r.length) return L;
  const k = g.kaching?.id ?? null;
  let { u, r } = L;
  if (target === 0) {
    if (k) {
      u = u.filter((e) => e.k !== k);
      r = r.filter((e) => e.k !== k);
    } else u = u.filter((e) => e.k || e.vid !== g.variantId);
  }
  if (!k && target < g.qty) {
    const left = paidUnits(cart, g.variantId) - (g.qty - target);
    r = r.map((e) => (!e.k && e.v === g.variantId ? { ...e, q: Math.max(0, Math.min(e.q, left - e.h)) } : e)).filter((e) => e.k || e.q > 0);
  }
  return { ...L, u, r };
}

/**
 * Atributos que tiene que llevar la petición del carrito para esta acción, o null si la anotación no cambia.
 * ctx: { action: 'upgrade' | 'set' | 'upsell' | 'bundle', group, target, expect, items, before }.
 */
function annotate(ctx, env) {
  const cart = ctx.before;
  if (!cart) return null;
  let next;
  if (ctx.action === 'upgrade') next = noteUpgrade(cart, ctx, env);
  else if (ctx.action === 'upsell' || ctx.action === 'bundle') next = noteUpsell(cart, ctx.items, env);
  else if (ctx.action === 'set' && ctx.group) next = noteSetQty(cart, ctx);
  else return null;
  const value = serializeLedger(next);
  const current = cart.attributes?.[CC_ATTR] ?? '';
  return value === current ? null : { [CC_ATTR]: value };
}

// ---------- eventos de GA4 ----------

/** Nombre corto del tramo («1+1», «2+1», «3»). */
function packLabel(tier, qty) {
  if (tier?.free > 0) return `${tier.paid}+${tier.free}`;
  return String(tier?.qty ?? qty ?? '');
}

/** Importe para GA4: en euros (tipo de cambio de Shopify) y en la moneda local. */
function moneyParams(cents, ctx = {}) {
  const local = Math.round(Number(cents) || 0) / 100;
  const fx = Number(ctx.fx) || 1;
  return { value: Math.round((local / fx) * 100) / 100, currency: 'EUR', local_value: local, local_currency: ctx.currency || 'EUR' };
}

/** Motivo de un fallo, con el resultado del controlador. */
function failReason(res) {
  if (!res) return 'error';
  if (res.priceChanged) return 'price_changed';
  if (res.changed) return 'changed_elsewhere';
  if (res.rolledBack === true) return 'rolled_back';
  if (res.rolledBack === false) return 'not_restored';
  return 'error';
}

class Tracker {
  constructor(win) {
    this.win = win;
    this.log = []; // últimos eventos (window.ClareneCart.analytics()), para comprobarlos sin GA4
  }

  context() {
    const S = this.win?.Shopify || {};
    return { market: S.country || null, currency: S.currency?.active || null, fx: Number(S.currency?.rate) || 1 };
  }

  /** gtag de la app de Google, solo hacia GA4 (send_to); si no está, Customer Events (el píxel de Clarène lo reenvía a GA4). Nunca lanza. */
  send(name, params = {}) {
    try {
      const ctx = this.context();
      const p = { market: ctx.market, ...params, cc: 1 };
      this.log.push({ name, params: p, at: Date.now() });
      if (this.log.length > 30) this.log.shift();
      const w = this.win;
      if (typeof w?.gtag === 'function') w.gtag('event', name, { ...p, send_to: GA4_ID });
      else w?.Shopify?.analytics?.publish?.('clarene_cart', { name, params: p });
    } catch { /* la medición nunca afecta al carrito */ }
  }
}

// ---- kaching-live.mjs ----
// Clarène Cart · configuración de Kaching en vivo (docs/06). Puro: sin DOM ni red.
// Fuente: el metafield app--…--kaching_bundles/deal_blocks, que Liquid pinta reducido al mercado de la página
// (snippets/clarene-cart-kaching.liquid). Kaching es la fuente de la oferta; lo que no se puede interpretar o no
// está verificado con medidas reales de Shopify se marca y NO se anuncia (nunca precios inventados).

const LIVE_FORMAT = 1;
const TOLERANCE = 2; // céntimos (igual que PRICE_TOLERANCE de core.mjs)

/**
 * Combinaciones de precio de Kaching medidas con carritos reales de Shopify (docs/referencia + tests/compat). Un tramo
 * solo se anuncia si su combinación COMPLETA (p. ej. «buy1:default+get1:amount») está aquí: dos partes medidas por
 * separado no bastan. tests/unit/kaching-live.test.mjs comprueba que cada una tiene medidas reales en las referencias.
 */
const VERIFIED_COMBOS = new Set([
  'qb1:specific', // España 1 unidad, LatAm A 1 unidad, Pack Buena Cara
  'qb1:specific-cap', // LatAm B 1 unidad (precio fijo ≥ precio normal → precio normal)
  'buy1:default+get1:specific', // España 1+1 (2ª a 0,02 €), Colombia B Dúo
  'buy1:default+get1:pct100', // México y Colombia A 1+1
  'buy1:default+get1:amount', // México, Chile y Perú B Dúo
  'buy1:default+get1:amount-floor', // Chile y Perú A 1+1 (rebaja ≥ precio → 0)
  'buyN:specific+get1:pct100', // España 2+1, LatAm A 2+1
  'qb1:default', // España B 1 unidad (precio normal)
  'qbN:specific', // España B Pack Dúo: precio fijo del pack entero (docs/05 §3 quattuortricies)
  'buy1:specific+get1:amount-floor', // España A 1+1 con la base a 44,95 € (Halloween, 2026-10-29: 39,95 € + 0 €)
  'buy1:specific-cap+get1:amount-floor', // España A 1+1 con la base a 39,95 € (2026-10-31: 39,95 € + 0 €)
  'buyN:specific+getN:pct100', // España A 2+2 (2026-10-31): las 2 primeras a 67,95 € en total + 2 a 0 €
  'buyN:default+get1:pct100', // España 2+1 (2026-11-01): 2 a precio normal (79,90 €) + 1 a 0 €
  'buyN:default+getN:pct100', // España 3+2 (2026-11-01): 3 a precio normal (119,85 €) + 2 a 0 €
]);
const comboOf = (regimes) => regimes.join('+');

/**
 * Una combinación medida sigue valiendo si un cambio del precio de la base solo la lleva al otro lado de su borde:
 * «-cap» (precio fijo ≥ precio normal → precio normal) y «-floor» (rebaja ≥ precio → 0) dependen únicamente del precio
 * unitario, kachingBarLines los calcula con min/max y los dos lados están medidos en tienda (qb1:specific-cap,
 * get1:amount-floor). Así una promoción que sube o baja la base (Halloween, Black Friday) no deja de reconocer los packs.
 * La estructura de la combinación (partes, cantidades, tipos de descuento) sigue teniendo que estar medida, y el pack
 * solo se reconoce si el importe real de /cart.js coincide con el calculado (live: true en finalizeGroup).
 */
const edgeless = (combo) => combo.replace(/-(cap|floor)(?=\+|$)/g, '');
const VERIFIED_EDGELESS = new Set([...VERIFIED_COMBOS].map(edgeless));
const isVerifiedCombo = (combo) => VERIFIED_COMBOS.has(combo) || VERIFIED_EDGELESS.has(edgeless(combo));

const cents = (v) => Math.round(Number(v) * 100);

/**
 * Precio que cobra Kaching por un tramo, con las reglas medidas en Shopify (docs/05 §3 octovicies):
 * precio fijo con tope en el precio normal, importe rebajado con mínimo 0, porcentaje. Devuelve las líneas de bases.
 * El precio fijo de un tramo de cantidad es el del pack entero (Pack Dúo: 2 bases a 49,95 €, medido el 2026-10-26),
 * igual que el de la parte «compra» de un bxgy; con 1 unidad las dos lecturas coinciden.
 */
function kachingBarLines(bar, unit) {
  const part = (type, value, qty, specificIsTotal) => {
    if (!type || type === 'default') return unit * qty;
    if (type === 'percentage') return Math.round(unit * qty * (1 - Number(value) / 100));
    if (type === 'specific') return specificIsTotal ? Math.min(cents(value), unit * qty) : Math.min(cents(value), unit) * qty;
    if (type === 'amount') return Math.max(0, unit - cents(value)) * qty;
    throw new Error(`Descuento de Kaching no soportado: ${type}`);
  };
  if (bar.dealBarType === 'quantity-break') return [part(bar.discountType, bar.discountValue, bar.quantity, true)];
  if (bar.dealBarType === 'bxgy') {
    return [part(bar.buyDiscountType, bar.buyDiscountValue, bar.buyQuantity, true), part(bar.getDiscountType, bar.getDiscountValue, bar.getQuantity, false)];
  }
  throw new Error(`Tipo de tramo no soportado: ${bar.dealBarType}`);
}

/** Régimen de cada parte del tramo (p. ej. «get1:amount-floor»: rebaja mayor que el precio, Kaching cobra 0). */
function barRegimes(bar, unit) {
  const one = (prefix, qty, type, value, specificIsTotal) => {
    const p = `${prefix}${qty > 1 ? 'N' : '1'}`;
    if (!type || type === 'default') return `${p}:default`;
    if (type === 'percentage') return Number(value) === 100 ? `${p}:pct100` : `${p}:pct`;
    if (type === 'specific') return `${p}:${cents(value) >= (specificIsTotal ? unit * qty : unit) ? 'specific-cap' : 'specific'}`;
    if (type === 'amount') return `${p}:${cents(value) >= unit ? 'amount-floor' : 'amount'}`;
    return `${p}:${type}`;
  };
  if (bar.dealBarType === 'quantity-break') return [one('qb', bar.quantity, bar.discountType, bar.discountValue, true)];
  if (bar.dealBarType === 'bxgy') {
    return [one('buy', bar.buyQuantity, bar.buyDiscountType, bar.buyDiscountValue, true), one('get', bar.getQuantity, bar.getDiscountType, bar.getDiscountValue, false)];
  }
  return [`tipo:${bar.dealBarType}`];
}

/**
 * Bloques crudos del metafield → mismo formato que pinta Liquid (para comparar con la consulta de Kaching y en
 * pruebas). market: id del mercado de la página; se quedan los de ese mercado y los que no tienen mercado.
 */
function reduceRawBlocks(raw, market) {
  return (Array.isArray(raw) ? raw : [])
    .filter((b) => b && (b.marketId == null || String(b.marketId) === String(market)))
    .map((b) => ({
      nanoId: b.nanoId ?? null, id: b.id ?? null, versionId: b.versionId ?? null, marketId: b.marketId ?? null,
      excludedMarketIds: b.excludedMarketIds ?? null, currency: b.currency?.currencyCode ?? null,
      visibility: b.blockVisibility ?? null, products: b.selectedProductIds ?? null, excludedProducts: b.excludedProductIds ?? null,
      collections: b.selectedCollectionIds ?? null, excludedCollections: b.excludedCollectionIds ?? null,
      ab: b.abTestVariantId ? {
        abid: b.abTestVariantId, letter: b.abTestVariantLetter ?? null, range: b.abTestVariantRange ?? null,
        number: b.abTestVariantNumber ?? null, count: b.abTestVariantsCount ?? null, traffic: b.abTestTrafficAllocation ?? null,
      } : null,
      flags: {
        collectionBreaks: b.collectionBreaksEnabled ?? null, subscriptions: b.subscriptionsEnabled ?? null,
        progressiveGifts: b.progressiveGiftsEnabled ?? null, checkboxUpsells: b.checkboxUpsellsEnabled ?? null,
        priceRounding: b.priceRounding ?? null,
      },
      bars: (Array.isArray(b.dealBars) ? b.dealBars : null)?.map((d) => ({
        id: d.id ?? null, title: d.title ?? null, type: d.dealBarType ?? null, q: d.quantity ?? null, qs: d.quantitySelector ?? null,
        bq: d.buyQuantity ?? null, gq: d.getQuantity ?? null, dt: d.discountType ?? null, dv: d.discountValue ?? null,
        bdt: d.buyDiscountType ?? null, bdv: d.buyDiscountValue ?? null, gdt: d.getDiscountType ?? null, gdv: d.getDiscountValue ?? null,
        upsells: Array.isArray(d.upsells) ? d.upsells.length : null, mgs: Array.isArray(d.multipleGiftsSelectors) ? d.multipleGiftsSelectors.length : null,
        sp: d.sellingPlanEnabled ?? null,
        gifts: (Array.isArray(d.freeGifts) ? d.freeGifts : null)?.map((g) => ({
          id: g.id ?? null, type: g.giftType ?? null, product: g.productGID ?? null, variants: g.variantGIDs ?? null, qty: g.quantity ?? null,
          text: g.text ?? null, media: g.mediaImageGID ?? null,
        })) ?? null,
      })) ?? null,
    }));
}

const gidNum = (gid) => (gid == null ? null : Number(String(gid).split('/').pop()) || null);
const toBar = (d) => ({
  id: d.id, title: d.title, dealBarType: d.type, quantity: d.q, buyQuantity: d.bq, getQuantity: d.gq,
  discountType: d.dt, discountValue: d.dv, buyDiscountType: d.bdt, buyDiscountValue: d.bdv, getDiscountType: d.gdt, getDiscountValue: d.gdv,
});

/** Motivo por el que un bloque entero no se puede usar, o null. */
function blockProblem(b, ctx) {
  if (!b || typeof b.nanoId !== 'string' || !Array.isArray(b.bars) || !b.bars.length) return 'formato desconocido';
  if (b.marketId != null && String(b.marketId) !== String(ctx.market)) return 'otro mercado';
  if (Array.isArray(b.excludedMarketIds) && b.excludedMarketIds.some((m) => String(m) === String(ctx.market))) return 'mercado excluido';
  if (b.currency == null) {
    // sin moneda propia (bloque sin mercado): solo se sabe cómo cobra en la moneda de la tienda
    if (ctx.currency !== ctx.shopCurrency) return `sin moneda propia y el carrito está en ${ctx.currency}`;
  } else if (b.currency !== ctx.currency) return `moneda ${b.currency} ≠ ${ctx.currency}`;
  if (b.visibility !== 'selected-products' || !Array.isArray(b.products) || !b.products.length) return `visibilidad no soportada: ${b.visibility}`;
  if ((b.excludedProducts || []).length || (b.collections || []).length || (b.excludedCollections || []).length) return 'filtros de producto o colección no soportados';
  const f = b.flags || {};
  for (const k of ['collectionBreaks', 'subscriptions', 'progressiveGifts', 'checkboxUpsells', 'priceRounding']) {
    if (f[k] !== false) return `opción ${k} = ${JSON.stringify(f[k])}`;
  }
  if (b.ab && (!b.ab.abid || !['A', 'B', 'C', 'D'].includes(b.ab.letter) || !Array.isArray(b.ab.range) || b.ab.range.length !== 2)) return 'test A/B con formato desconocido';
  return null;
}

/** Motivo por el que un tramo no se puede usar, o null. */
function barProblem(d) {
  if (!d || typeof d.id !== 'string') return 'tramo sin id';
  if (d.type !== 'quantity-break' && d.type !== 'bxgy') return `tipo de tramo ${d.type}`;
  if (d.qs !== false && d.qs != null) return 'selector de cantidad';
  if (d.upsells !== 0 || d.mgs !== 0 || d.sp !== false) return 'upsells, selector de regalos o suscripción en el tramo';
  const types = d.type === 'quantity-break' ? [d.dt] : [d.bdt, d.gdt];
  if (types.some((t) => !['default', 'specific', 'percentage', 'amount'].includes(t))) return `descuento ${types.join('/')}`;
  if (d.type === 'quantity-break' && !(Number.isInteger(d.q) && d.q >= 1)) return 'cantidad no válida';
  if (d.type === 'bxgy' && !(Number.isInteger(d.bq) && d.bq >= 1 && Number.isInteger(d.gq) && d.gq >= 1)) return 'cantidades no válidas';
  if (!Array.isArray(d.gifts)) return 'regalos con formato desconocido';
  return null;
}

/**
 * Regalo del tramo con su variante, desde los productos que pinta Liquid (products[id] = { available, variants:
 * [[id, precio, disponible]] }). Solo regalos de producto, de 1 unidad, con la variante indicada o un producto de
 * una sola variante; disponible. Si no, { problem }.
 * Un regalo de producto SIN producto ni variantes («+ regalo MISTERIOSO», «+ Envío gratis») es solo un texto del
 * widget: Kaching no añade ninguna línea ni cambia el precio (medido el 2026-10-26). Se devuelve como { perk, media }
 * (media: mediaImageGID, la imagen que muestra el widget).
 * Un regalo de tipo «shipping» («+ Envío GRATIS» del 2+2 de la variante A) tampoco añade línea ni cambia el importe
 * (medido el 2026-10-31, docs/14): también es una ventaja de texto.
 */
function resolveGift(g, products) {
  if ((g.type === 'product' || g.type === 'shipping') && !g.product && !(Array.isArray(g.variants) && g.variants.length)) {
    return { perk: String(g.text || '').replace(/^\s*\+\s*/, '').trim() || null, media: g.media || null };
  }
  if (g.type !== 'product') return { problem: `regalo de tipo ${g.type}` };
  if (g.qty != null && g.qty !== 1) return { problem: `regalo x${g.qty}` };
  const pid = gidNum(g.product);
  if (!pid) return { problem: 'regalo sin producto' };
  const p = products?.[pid];
  if (!p) return { problem: `regalo ${pid} no encontrado en la tienda` };
  const variants = p.variants || [];
  let v;
  if (Array.isArray(g.variants) && g.variants.length) {
    if (g.variants.length !== 1) return { problem: 'regalo con varias variantes a elegir' };
    v = variants.find(([id]) => id === gidNum(g.variants[0]));
    if (!v) return { problem: 'variante del regalo no encontrada' };
  } else {
    if (variants.length !== 1) return { problem: `regalo con ${variants.length} variantes sin indicar cuál` };
    [v] = variants;
  }
  if (!v[2]) return { problem: 'regalo sin stock' };
  return { gift: { id: g.id, variantId: v[0], productId: pid, ...(p.title ? { title: p.title } : {}) } };
}

/**
 * Catálogo de ofertas del mercado a partir de lo que pinta Liquid. Una oferta por bloque (deal + variante A/B) y
 * producto, con tramos ordenados por cantidad. Cada tramo lleva su precio según las reglas, sus regímenes y si están
 * todos verificados (verified). Lo que no se puede usar va a rejected con su motivo.
 */
function buildCatalog(live, { market, currency } = {}) {
  const out = { offers: [], rejected: [], ok: false, problem: null };
  if (!live || live.format !== LIVE_FORMAT) { out.problem = 'sin configuración de Kaching o formato desconocido'; return out; }
  if (!Array.isArray(live.blocks)) { out.problem = 'metafield de Kaching ausente'; return out; }
  if (live.truncated) out.warning = 'la tienda tiene más productos de los que Liquid recorre';
  const ctx = { market: market ?? live.market, currency: currency ?? live.currency, shopCurrency: live.shopCurrency };
  for (const b of live.blocks) {
    const key = `${b?.nanoId}|${b?.ab?.abid || '-'}`;
    const problem = blockProblem(b, ctx);
    if (problem) { out.rejected.push({ key, deal: b?.nanoId ?? null, ab: b?.ab?.letter ?? null, problem }); continue; }
    const barIssue = b.bars.map(barProblem).find(Boolean);
    if (barIssue) { out.rejected.push({ key, deal: b.nanoId, ab: b.ab?.letter ?? null, problem: barIssue }); continue; }
    for (const pid of b.products) {
      const p = live.products?.[pid];
      if (!p || !(p.variants || []).length) { out.rejected.push({ key: `${key}|${pid}`, deal: b.nanoId, ab: b.ab?.letter ?? null, problem: `producto ${pid} no encontrado en la tienda` }); continue; }
      const prices = new Set(p.variants.map(([, price]) => price));
      if (prices.size !== 1) { out.rejected.push({ key: `${key}|${pid}`, deal: b.nanoId, ab: b.ab?.letter ?? null, problem: 'variantes con precios distintos' }); continue; }
      const [unit] = prices;
      const tiers = b.bars.map((d) => {
        const bar = toBar(d);
        const lines = kachingBarLines(bar, unit);
        const qty = d.type === 'bxgy' ? d.bq + d.gq : d.q;
        const regimes = barRegimes(bar, unit);
        // unidades gratis: las «get» que Kaching deja a 0 (o a 1 céntimo por unidad); la 2ª del Dúo cuesta
        const free = d.type === 'bxgy' && lines[1] <= TOLERANCE * d.gq ? d.gq : 0;
        const tier = { bid: d.id, qty, price: lines.reduce((a, x) => a + x, 0), lines, paid: qty - free, free, title: d.title, regimes };
        const problems = [];
        const resolved = d.gifts.map((g) => resolveGift(g, live.products));
        for (const g of resolved) if (g.problem) problems.push(g.problem);
        const gifts = resolved.filter((g) => g.gift);
        // cada regalo se añade una vez y se reconoce por su id (o su variante): repetidos, no se sabría cuál falta
        if (new Set(gifts.map((g) => g.gift.id)).size !== gifts.length || new Set(gifts.map((g) => g.gift.variantId)).size !== gifts.length) problems.push('regalo repetido en el tramo');
        if (!problems.length && gifts.length) {
          if (gifts.length === 1) tier.gift = gifts[0].gift;
          else tier.gifts = gifts.map((g) => g.gift);
          // regalos que Kaching anuncia como «regalo MISTERIOSO» (la Esponja): así se nombran en la ampliación
          const mystery = d.gifts.filter((g, i) => resolved[i].gift && /misterios/i.test(g.text || '')).map((g) => g.id);
          if (mystery.length) tier.mystery = mystery;
        }
        const perkGifts = resolved.filter((g) => 'perk' in g && g.perk);
        if (perkGifts.length) {
          tier.perks = perkGifts.map((g) => g.perk);
          tier.perkMedia = perkGifts.map((g) => g.media); // misma posición que perks
        }
        if (!isVerifiedCombo(comboOf(regimes))) problems.push(`combinación sin medir: ${comboOf(regimes)}`);
        tier.verified = !problems.length;
        if (problems.length) tier.problems = problems;
        return tier;
      }).sort((x, y) => x.qty - y.qty);
      if (new Set(tiers.map((t) => t.qty)).size !== tiers.length) { out.rejected.push({ key: `${key}|${pid}`, deal: b.nanoId, ab: b.ab?.letter ?? null, problem: 'dos tramos con la misma cantidad' }); continue; }
      out.offers.push({
        key: `${key}|${pid}`, deal: b.nanoId, blockId: b.id, version: b.versionId, market: b.marketId, currency: b.currency ?? ctx.shopCurrency,
        productId: pid, variantId: p.variants.length === 1 ? p.variants[0][0] : null, variantIds: p.variants.map(([id]) => id), unitPrice: unit,
        ab: b.ab ? { abid: b.ab.abid, letter: b.ab.letter, range: b.ab.range } : null,
        maxQty: Math.max(...tiers.map((t) => t.qty)), tiers,
      });
    }
  }
  out.ok = true;
  return out;
}

/** Bucket A/B de Kaching para una sesión (kaching-bundles-api.js, comprobado el 2026-10-17). */
function kachingBucket(sessionId) {
  const h = parseInt(String(sessionId || '').replace(/-/g, '').slice(0, 4), 16);
  return Number.isFinite(h) ? Math.floor((h % 256) * 100 / 256) : null;
}

/** Variante que Kaching asignará a la sesión para un deal (solo PREDICCIÓN: la confirma fetchDeals). */
function predictVariant(catalog, deal, sessionId) {
  const bucket = kachingBucket(sessionId);
  if (bucket == null) return null;
  const o = catalog.offers.find((x) => x.deal === deal && x.ab && bucket >= x.ab.range[0] && bucket < x.ab.range[1]);
  return o ? o.ab.letter : null;
}

/** Huella comparable de un catálogo (para saber si dos fuentes dicen lo mismo). */
function catalogDigest(catalog) {
  return catalog.offers.map((o) => [o.key, o.version, o.unitPrice, o.tiers.map((t) => [t.bid, t.qty, t.price, t.lines.join('+'), t.free, t.gift?.variantId ?? '', (t.gifts || []).map((g) => g.variantId).join('+'), t.verified]).join(';')].join('|')).sort().join('\n');
}

/**
 * Referencias de docs/referencia (lo que usa hoy el carrito) en el formato del catálogo, para comparar.
 * config: la del carrito (offers de España, marketOffers de LatAm, bundles).
 */
function referenceCatalog(config) {
  const offers = [];
  for (const o of config.offers || []) {
    if (o.ab) continue; // las de LatAm se toman de marketOffers (con sus dos variantes)
    if (o.market != null && String(o.market) !== String(config.market)) continue;
    offers.push({ key: `${o.deal}|-|${o.productId}`, deal: o.deal, version: o.version, market: o.market, productId: o.productId, ab: null, tiers: o.tiers });
  }
  const mo = (config.marketOffers?.offers || []).find((c) => String(c.market) === String(config.market));
  for (const v of mo?.variants || []) {
    offers.push({ key: `${mo.deal}|${v.abid}|${mo.productId}`, deal: mo.deal, version: mo.version, market: mo.market, productId: mo.productId, ab: { abid: v.abid, letter: v.letter }, tiers: v.tiers, unitPrice: mo.unitPrice });
  }
  for (const b of config.bundles || []) {
    for (const v of b.ab || []) {
      offers.push({
        key: `${b.deal}|${v.id}|${b.productId}`, deal: b.deal, version: b.version, market: b.market, productId: b.productId, ab: { abid: v.id, letter: v.letter },
        tiers: [{ bid: b.bar.id, qty: b.bar.qty, price: v.price, gifts: b.gifts.map((g) => ({ id: g.id, variantId: g.variantId, productId: g.productId })) }],
      });
    }
  }
  return offers;
}

/**
 * Diferencias entre el catálogo en vivo y las referencias. Solo compara las ofertas que tienen referencia (las que
 * usa hoy el carrito); el resto se lista aparte. Devuelve [{ key, field, live, ref }].
 */
function compareWithReference(catalog, reference) {
  const diffs = [];
  const push = (key, field, live, ref) => diffs.push({ key, field, live, ref });
  for (const r of reference) {
    const o = catalog.offers.find((x) => x.key === r.key);
    if (!o) {
      const rej = catalog.rejected.find((x) => x.key === r.key || x.key === r.key.split('|').slice(0, 2).join('|'));
      push(r.key, 'oferta', rej ? `descartada: ${rej.problem}` : 'no está en Kaching', 'existe');
      continue;
    }
    if (r.version && o.version !== r.version) push(r.key, 'versión', o.version, r.version);
    if (r.unitPrice != null && o.unitPrice !== r.unitPrice) push(r.key, 'precio unitario', o.unitPrice, r.unitPrice);
    if (String(o.market ?? '') !== String(r.market ?? '')) push(r.key, 'mercado', o.market, r.market);
    if ((o.ab?.letter ?? null) !== (r.ab?.letter ?? null)) push(r.key, 'variante', o.ab?.letter, r.ab?.letter);
    for (const rt of r.tiers) {
      const t = o.tiers.find((x) => x.qty === rt.qty);
      const k = `${r.key} ${rt.qty}u`;
      if (!t) { push(k, 'tramo', 'no existe', rt.bid); continue; }
      if (t.bid !== rt.bid) push(k, 'id de tramo', t.bid, rt.bid);
      if (t.price !== rt.price) push(k, 'precio', t.price, rt.price);
      if (rt.lines && t.lines.join('+') !== rt.lines.join('+')) push(k, 'reparto en líneas', t.lines.join('+'), rt.lines.join('+'));
      if (rt.free != null && t.free !== rt.free) push(k, 'unidades gratis', t.free, rt.free);
      const lg = t.gift ? [t.gift] : t.gifts || [];
      const rg = rt.gift ? [rt.gift] : rt.gifts || [];
      const gs = (a) => a.map((g) => `${g.id}:${g.variantId}`).sort().join(',');
      if (gs(lg) !== gs(rg)) push(k, 'regalos', gs(lg) || '—', gs(rg) || '—');
      if (!t.verified) push(k, 'verificado', (t.problems || []).join('; '), 'sí (medido)');
    }
    for (const t of o.tiers) if (!r.tiers.some((x) => x.qty === t.qty)) push(`${r.key} ${t.qty}u`, 'tramo', t.bid, 'no existe');
  }
  return diffs;
}

/**
 * Cómo reconocería el catálogo un pack del carrito (propiedades de Kaching + líneas). Mismas comprobaciones que
 * finalizeGroup: deal, mercado, variante A/B (letra y abid), producto, tramo por cantidad, id de tramo existente e
 * importe real = verificado. Devuelve { offer, tier } o { problem }.
 */
function recognizePack(catalog, { kaching, qty, finalCents, productId, market }) {
  if (!kaching?.deal) return { problem: 'sin propiedades de Kaching' };
  if (kaching.market != null && String(kaching.market) !== String(market)) return { problem: 'pack de otro mercado' };
  const offer = catalog.offers.find((o) => o.deal === kaching.deal && o.productId === productId
    && (o.ab ? kaching.ab === o.ab.letter && kaching.abid === o.ab.abid : !kaching.ab));
  if (!offer) return { problem: 'oferta no encontrada (deal, variante A/B o producto)' };
  if (kaching.bid && !offer.tiers.some((t) => t.bid === kaching.bid)) return { problem: 'el tramo del pack ya no existe en Kaching' };
  const tier = offer.tiers.find((t) => t.qty === qty);
  if (!tier) return { problem: `sin tramo de ${qty} unidades` };
  if (Math.abs(finalCents - tier.price) > TOLERANCE) return { problem: `importe real ${finalCents} ≠ ${tier.price}` };
  return { offer, tier };
}

// ---------- conexión con el carrito (docs/06, fase 2) ----------

/**
 * Ofertas en el formato de core.mjs a partir del catálogo. Solo las que el carrito sabe ampliar con seguridad:
 * producto de una sola variante, tramos verificados desde 1 unidad (se corta en el primero que no lo está: ese tramo
 * y los siguientes no se anuncian). Puede haber saltos (1, 2 y 4 en la variante A desde el 2026-10-31): las
 * ampliaciones y «−»/«+» pasan de un tramo al siguiente (core.mjs, nextTier y stepQty). Regalos con producto: gift (uno) o gifts (varios, cada uno una
 * vez); perks: ventajas solo de texto del widget. live: true obliga a que el importe real del pack cuadre con el del
 * tramo para reconocerlo (finalizeGroup).
 */
function cartOffersFromCatalog(catalog) {
  const out = [];
  for (const o of catalog?.offers || []) {
    if (!o.variantId) continue; // varias variantes (tonos): el carrito no las amplía, como hasta ahora
    const tiers = [];
    for (const t of o.tiers) {
      if (!t.verified || (!tiers.length && t.qty !== 1)) break;
      tiers.push({
        bid: t.bid, qty: t.qty, price: t.price, lines: t.lines, paid: t.paid, free: t.free, title: t.title,
        ...(t.gift ? { gift: t.gift } : {}), ...(t.gifts ? { gifts: t.gifts } : {}), ...(t.perks ? { perks: t.perks, perkMedia: t.perkMedia } : {}),
        ...(t.mystery ? { mystery: t.mystery } : {}),
      });
    }
    if (!tiers.length) continue;
    out.push({
      deal: o.deal, version: o.version, market: o.market, productId: o.productId, variantId: o.variantId,
      maxQty: tiers[tiers.length - 1].qty, tiers, live: true, ...(o.ab ? { ab: { abid: o.ab.abid, letter: o.ab.letter }, range: o.ab.range } : {}),
    });
  }
  return out;
}

/**
 * Bundles que el carrito recomienda (p. ej. Pack Buena Cara) con los datos vigentes de Kaching: tramo, precio de cada
 * variante A/B y regalos. De la referencia solo se conserva lo que no es de Kaching (competesWith). Si alguna variante
 * no está, no está verificada o no se puede usar en este mercado (sin moneda propia fuera del euro), el bundle no se
 * recomienda.
 */
function bundlesFromCatalog(refBundles, catalog) {
  const out = [];
  for (const b of refBundles || []) {
    const offers = (catalog?.offers || []).filter((o) => o.deal === b.deal && o.productId === b.productId);
    // alguna variante A/B del deal descartada entera (p. ej. sin moneda propia fuera del euro): no se recomienda
    if (!offers.length || catalog.rejected.some((r) => r.deal === b.deal && r.key.split('|').length === 2)) continue;
    const tiers = offers.map((o) => o.tiers.find((t) => t.qty === b.bar.qty));
    if (tiers.some((t) => !t || !t.verified) || new Set(tiers.map((t) => t.bid)).size !== 1) continue;
    const gifts = tiers[0].gifts || (tiers[0].gift ? [tiers[0].gift] : []);
    const giftKey = (t) => (t.gifts || (t.gift ? [t.gift] : [])).map((g) => `${g.id}:${g.variantId}`).sort().join(',');
    if (tiers.some((t) => giftKey(t) !== giftKey(tiers[0]))) continue;
    out.push({
      ...b, version: offers[0].version, market: offers[0].market ?? null, bar: { id: tiers[0].bid, qty: tiers[0].qty },
      gifts: gifts.map((g) => ({ ...g, title: g.title || b.gifts?.find((x) => x.id === g.id)?.title || '' })),
      ab: offers.filter((o) => o.ab).map((o, i) => ({ id: o.ab.abid, letter: o.ab.letter, price: tiers[i].price })),
      live: true,
    });
  }
  return out;
}

/**
 * Letra de la variante que Kaching ha asignado a la visitante para un deal, comparando lo que devuelve fetchDeals
 * (ya resuelto, sin versión) con los precios del catálogo. null si no coincide con ninguna (datos distintos).
 */
function visitorLetter(catalog, deal) {
  const offers = (catalog?.offers || []).filter((o) => o.deal === deal?.nanoId && o.ab);
  const part = (o) => (o ? { type: o.type, value: o.value } : { type: 'default', value: 0 });
  const matches = offers.filter((o) => (deal.dealBars || []).length === o.tiers.length && (deal.dealBars || []).every((b) => {
    const t = o.tiers.find((x) => x.bid === b.id);
    if (!t) return false;
    const bar = b.dealBarType === 'quantity-break'
      ? { dealBarType: b.dealBarType, quantity: b.quantity, discountType: part(b.discount).type, discountValue: part(b.discount).value }
      : { dealBarType: b.dealBarType, buyQuantity: b.buyQuantity, getQuantity: b.getQuantity, buyDiscountType: part(b.buyDiscount).type, buyDiscountValue: part(b.buyDiscount).value, getDiscountType: part(b.getDiscount).type, getDiscountValue: part(b.getDiscount).value };
    try { return kachingBarLines(bar, o.unitPrice).reduce((a, x) => a + x, 0) === t.price; } catch { return false; }
  }));
  return matches.length === 1 ? matches[0].ab.letter : null;
}

/**
 * Configuración del carrito desde Kaching en vivo. Devuelve { source: 'kaching', catalog, offers, bundles,
 * predicted } o { source: 'referencia', problem } si Liquid no la trae o no se entiende (el carrito usa entonces las
 * referencias, pero sin anunciar upgrades hasta que Kaching las confirme).
 * predicted: variante A/B por deal según el id de sesión de Kaching (se marca como visitor; Kaching la confirma).
 */
function liveConfig(config, live, { sessionId } = {}) {
  const catalog = buildCatalog(live, { market: config.market, currency: config.currency });
  if (!catalog.ok) return { source: 'referencia', problem: catalog.problem, catalog };
  // los bundles que recomienda el carrito (Pack Buena Cara) no son ofertas ampliables: se muestran como pack y solo
  // se eliminan, como siempre (antes los excluía, de paso, que tuvieran dos regalos)
  const bundleDeals = new Set((config.bundles || []).map((b) => b.deal));
  const offers = cartOffersFromCatalog(catalog).filter((o) => !bundleDeals.has(o.deal));
  const predicted = {};
  for (const deal of new Set(offers.filter((o) => o.ab).map((o) => o.deal))) {
    predicted[deal] = predictVariant(catalog, deal, sessionId);
    for (const o of offers) if (o.deal === deal && o.ab) o.visitor = predicted[deal] ? o.ab.letter === predicted[deal] : false;
  }
  return { source: 'kaching', catalog, digest: catalogDigest(catalog), offers, bundles: bundlesFromCatalog(config.bundles, catalog), predicted };
}

/** Configuración de Kaching que viene en la respuesta de un add (sección clarene-cart-state), o null. */
function liveFromSections(json, section) {
  const html = json?.sections?.[section];
  const m = typeof html === 'string' && html.match(/<script[^>]*data-cc-kaching-live[^>]*>([\s\S]*?)<\/script>/);
  if (!m) return null;
  try {
    const live = JSON.parse(m[1]);
    return live && live.format === LIVE_FORMAT ? live : null;
  } catch {
    return null;
  }
}

// ---- controller.mjs ----
// Clarène Cart · acceso a la Ajax Cart API y orquestación de operaciones.
// Todas las mutaciones pasan por una cola FIFO: un doble clic nunca lanza dos operaciones a la vez (C5).


const OWN_HEADER = 'X-Clarene-Cart';
/** Sección del tema que devuelve el carrito en JSON (sections/clarene-cart-state.liquid). */
const STATE_SECTION = 'clarene-cart-state';
const isCart = (c) => !!c && Array.isArray(c.items) && typeof c.total_price === 'number';

/**
 * /cart/update.js devuelve el carrito con los descuentos de pedido envueltos ({ amount, discount_application }),
 * no con la forma de /cart.js. Se pasan a la forma de /cart.js; si alguno no se puede interpretar, null (se relee).
 */
function cartFromUpdate(res) {
  if (!isCart(res)) return null;
  const apps = res.cart_level_discount_applications || [];
  const flat = apps.map((a) => (a && a.discount_application
    ? { ...a.discount_application, total_allocated_amount: a.discount_application.total_allocated_amount ?? a.amount }
    : a));
  if (flat.some((d) => !d || !d.title || typeof d.total_allocated_amount !== 'number')) return null;
  return { ...res, cart_level_discount_applications: flat };
}

class CartClient {
  constructor({ fetch: f, root = '/', timeoutMs = 15000 } = {}) {
    this.fetch = f;
    this.root = root.endsWith('/') ? root : root + '/';
    this.timeoutMs = timeoutMs;
    this.requests = 0;
  }

  async _req(path, body) {
    this.requests++;
    const init = {
      method: body === undefined ? 'GET' : 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest', [OWN_HEADER]: '1' },
    };
    if (body !== undefined) {
      init.headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(body);
    }
    let attempt = 0;
    for (;;) {
      let res;
      try {
        res = await withTimeout(this.fetch(this.root + path, init), this.timeoutMs);
      } catch (e) {
        // un único reintento, solo en errores de red; nunca en 429 (no forzamos los límites de Shopify)
        if (attempt++ === 0) continue;
        throw new CartError('network', 'No se ha podido conectar. Inténtalo de nuevo.', e);
      }
      let data = null;
      try { data = await res.json(); } catch { /* respuesta vacía */ }
      if (res.ok) return data;
      if (res.status >= 500 && attempt++ === 0) continue;
      const msg = res.status === 429 ? 'Demasiadas peticiones. Espera unos segundos.' : data?.description || data?.message || 'No se ha podido actualizar el carrito.';
      throw new CartError(res.status, msg, data);
    }
  }

  get() { return this._req('cart.js'); }
  // ?kaching_bundles=true evita que el interceptor de Kaching reescriba el add. Con state, la respuesta trae también el
  // carrito confirmado (sección clarene-cart-state) y no hace falta releer /cart.js.
  // attributes: la anotación de la medición (_cc, docs/07) viaja en la misma petición; no toca líneas ni precios
  add(items, { state, attributes } = {}) { return this._req('cart/add.js?kaching_bundles=true', { items, ...(state ? { sections: state } : {}), ...(attributes ? { attributes } : {}) }); }
  update(updates, attributes) { return this._req('cart/update.js', { ...(updates ? { updates } : {}), ...(attributes ? { attributes } : {}) }); }
  setDiscount(codes) { return this._req('cart/update.js', { discount: codes.join(',') }); }
}

/**
 * Observa las peticiones de otros scripts que cambian el carrito y llama a onChange(kind):
 *  - 'add'      POST /cart/add (widget de Kaching, quick-add del tema)
 *  - 'cart'     POST /cart/change|update|clear
 *  - 'discount' /discount/CODIGO, que es como el popup de Klaviyo aplica BEAUTY
 *               (fetch GET sin recarga ni evento; comprobado en su script Render.js, 2026-10-15)
 * Las peticiones propias (cabecera X-Clarene-Cart) se ignoran: ya se releen en el controlador.
 */
function classifyCartRequest(url, method = 'GET') {
  const path = String(url || '').replace(/^https?:\/\/[^/]+/, '');
  if (/\/discount\/[^/?#]+/.test(path)) return 'discount';
  if (String(method).toUpperCase() !== 'POST') return null;
  const m = path.match(/\/cart\/(add|change|update|clear)(\.js)?(\?|$)/);
  if (!m) return null;
  return m[1] === 'add' ? 'add' : 'cart';
}

/**
 * Bundled Section Rendering: pide la sección `section` (el carrito en JSON) en la MISMA petición /cart/add de otro
 * script (widget de Kaching vía el formulario del tema), solo añadiendo el parámetro `sections`. Devuelve el init
 * nuevo, o null si el cuerpo no es de un tipo que sepamos ampliar sin riesgo (entonces no se toca la petición).
 */
function withStateSection(init, section) {
  const body = init?.body;
  const add = (list) => [...new Set([...(list ? String(list).split(',').filter(Boolean) : []), section])].slice(-5).join(',');
  if (typeof body === 'string') {
    const t = body.trim();
    if (t.startsWith('{')) {
      try { const o = JSON.parse(t); o.sections = add(Array.isArray(o.sections) ? o.sections.join(',') : o.sections); return { ...init, body: JSON.stringify(o) }; } catch { return null; }
    }
    const p = new URLSearchParams(t);
    p.set('sections', add(p.get('sections')));
    return { ...init, body: p.toString() };
  }
  if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
    const p = new URLSearchParams(body);
    p.set('sections', add(p.get('sections')));
    return { ...init, body: p };
  }
  if (typeof FormData !== 'undefined' && body instanceof FormData) {
    const f = new FormData();
    for (const [k, v] of body.entries()) if (k !== 'sections') f.append(k, v);
    f.append('sections', add(body.get('sections')));
    return { ...init, body: f };
  }
  return null;
}

/**
 * Completa un carrito de Liquid con las líneas anidadas (parents: key → key del padre, de clarene-cart-parents):
 * {{ cart | json }} no trae parent_relationship. No toca las líneas que ya lo traen. Devuelve el mismo carrito.
 */
function withParents(cart, parents) {
  if (!cart?.items || !parents || typeof parents !== 'object') return cart;
  for (const l of cart.items) if (!l.parent_relationship && parents[l.key]) l.parent_relationship = { parent_key: parents[l.key] };
  return cart;
}

/** El carrito confirmado por Shopify que viene en la respuesta del add (sección clarene-cart-state), o null. */
function cartFromSections(json, section) {
  const html = json?.sections?.[section];
  const m = typeof html === 'string' && html.match(/<script[^>]*data-cc-cart-state[^>]*>([\s\S]*?)<\/script>/);
  if (!m) return null;
  try {
    const cart = JSON.parse(m[1]);
    const p = html.match(/<script[^>]*data-cc-cart-parents[^>]*>([\s\S]*?)<\/script>/);
    if (p) { try { withParents(cart, JSON.parse(p[1])); } catch { /* sin mapa: se agrupa por las propiedades */ } }
    return cart && Array.isArray(cart.items) && typeof cart.total_price === 'number' ? cart : null;
  } catch {
    return null;
  }
}

function installCartWatcher(win, onChange, { stateSection, onAddStart } = {}) {
  const origFetch = win.fetch;
  win.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : input?.url || String(input || '');
    const method = init?.method || input?.method || 'GET';
    const h = init?.headers;
    const own = h && (h[OWN_HEADER] || (typeof h.get === 'function' && h.get(OWN_HEADER)));
    const kind = own ? null : classifyCartRequest(url, method);
    // add de otro script: se pide también el carrito actualizado en la misma respuesta (si el cuerpo lo permite)
    const bundled = kind === 'add' && stateSection && typeof input === 'string' ? withStateSection(init, stateSection) : null;
    const p = bundled ? origFetch.call(this, input, bundled) : origFetch.apply(this, arguments);
    if (kind === 'add') onAddStart?.();
    // /discount/ responde con una redirección: se relee el carrito aunque la respuesta final no sea "ok"
    if (kind) {
      p.then(async (r) => {
        if (!(r.ok || kind === 'discount')) { if (kind === 'add') onChange('add-failed'); return; }
        let cart = null, json = null;
        if (bundled) { try { json = await r.clone().json(); cart = cartFromSections(json, stateSection); } catch { cart = null; } }
        onChange(kind, cart, json); // json: la respuesta completa (trae también la configuración vigente de Kaching)
      }, () => { if (kind === 'add') onChange('add-failed'); });
    }
    return p;
  };
  const XHR = win.XMLHttpRequest;
  if (XHR?.prototype?.open) {
    const open = XHR.prototype.open;
    XHR.prototype.open = function (method, url) {
      const kind = classifyCartRequest(url, method);
      if (kind === 'add') {
        onAddStart?.();
        this.addEventListener('error', () => onChange('add-failed'));
      }
      if (kind) this.addEventListener('load', () => { if (this.status < 400 || kind === 'discount') onChange(kind); else if (kind === 'add') onChange('add-failed'); });
      return open.apply(this, arguments);
    };
  }
  return () => { win.fetch = origFetch; };
}

// Mensaje cuando no se ha podido comprobar que el carrito ha vuelto a estar como antes: nunca se dice que sí
const NOT_RESTORED = 'No hemos podido completar el cambio y tu carrito puede haber cambiado. Revísalo antes de pagar.';
/** El carrito con los atributos escritos ('' borra la clave, como Shopify). */
function patchAttrs(cart, attrs) {
  if (!cart || !attrs) return cart;
  const a = { ...(cart.attributes || {}) };
  for (const [k, v] of Object.entries(attrs)) if (v === '') delete a[k]; else a[k] = v;
  return { ...cart, attributes: a };
}
const changedElsewhere = (cart) => ({ ok: false, error: 'Tu carrito ha cambiado (quizá en otra pestaña). Revísalo antes de continuar.', cart, changed: true });

class CartError extends Error {
  constructor(status, message, data) {
    super(message);
    this.status = status;
    this.data = data;
  }
}

function withTimeout(p, ms) {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error('timeout')), ms);
    p.then((v) => { clearTimeout(t); resolve(v); }, (e) => { clearTimeout(t); reject(e); });
  });
}

class Queue {
  constructor() { this.tail = Promise.resolve(); this.pending = 0; }
  run(fn) {
    this.pending++;
    const p = this.tail.then(fn);
    this.tail = p.catch(() => {}).finally(() => { this.pending--; });
    return p;
  }
  get busy() { return this.pending > 0; }
}

class CartController {
  constructor({ client, config, log = () => {} }) {
    this.client = client;
    this.config = config;
    this.queue = new Queue();
    // (ctx) => atributos del carrito para la medición, o null (analytics.annotate). Sin él, nada cambia.
    this.annotate = null;
    this.cart = null;
    this.groups = [];
    this.listeners = new Set();
    this.log = log;
  }

  subscribe(fn) { this.listeners.add(fn); return () => this.listeners.delete(fn); }

  _set(cart, extra = {}) {
    this.cart = cart;
    this.groups = groupCart(cart, this.config);
    for (const fn of this.listeners) fn({ cart, groups: this.groups, ...extra });
  }

  _emit(extra) { for (const fn of this.listeners) fn({ cart: this.cart, groups: this.groups, ...extra }); }

  /** Estado inicial sin petición (carrito renderizado por Liquid). */
  hydrate(cart) { this._set(cart); }

  /** Carrito ya confirmado por Shopify (respuesta del add con la sección de estado): se usa sin releer /cart.js. */
  useCart(cart) {
    // El JSON de Liquid no trae discount_codes (los códigos introducidos, aplicables o no): añadir un producto no los
    // cambia, así que se conservan los anteriores. Lo que se cobra (cart_level_discount_applications) sí viene.
    return this.queue.run(() => this._fresh(this._withCodes(cart)));
  }

  /**
   * Confirma en segundo plano el carrito ya pintado (p. ej. el estado de los códigos tras un add): relee /cart.js y
   * solo vuelve a pintar si algo ha cambiado de verdad (importes, líneas o códigos).
   */
  confirm() {
    const sig = (c) => JSON.stringify([c?.total_price, c?.item_count, c?.discount_codes, c?.cart_level_discount_applications?.map((d) => [d.title, d.total_allocated_amount]),
      (c?.items || []).map((l) => [l.key, l.quantity, l.final_line_price])]);
    return this.queue.run(async () => {
      const cart = await this.client.get();
      return sig(cart) === sig(this.cart) ? this.cart : this._fresh(cart);
    });
  }

  /** Vuelve a agrupar el carrito actual (p. ej. cuando llega la oferta del mercado), sin petición. */
  regroup() { if (this.cart) this._set(this.cart); }

  refresh() { return this.queue.run(() => this._fresh()); }

  async _fresh(known) {
    let cart = known || (await this.client.get());
    cart = await this._guardGifts(cart);
    this._set(cart);
    return cart;
  }

  /** Nunca dejamos un regalo cobrándose: se reduce a las unidades gratis o se quita. */
  async _guardGifts(cart) {
    for (let i = 0; i < 2; i++) {
      const updates = giftGuard(cart);
      if (!updates) return cart;
      this.log('gift-guard', updates);
      await this.client.update(updates);
      cart = await this.client.get();
    }
    return cart;
  }

  /**
   * from: unidades que veía la clienta al pulsar. Si el carrito real ya no las tiene (lo cambió otra pestaña o
   * otro script), no se aplica nada: se pinta el carrito real para que lo revise.
   */
  setQty(gid, target, { from } = {}) {
    return this._mutate(async () => {
      const before = await this.client.get();
      const g = findGroup(groupCart(before, this.config), { gid });
      if (!g) return { ok: false, error: 'El producto ya no está en el carrito.', cart: before };
      if (from != null && g.qty !== from) return changedElsewhere(before);
      return this._apply(g, target, before);
    });
  }

  /** to: el tramo que se anunciaba al pulsar. Si el carrito real llevaría a otro (otro precio), no se aplica nada. */
  upgrade(gid, { to } = {}) {
    return this._mutate(async () => {
      const before = await this.client.get();
      const g = findGroup(groupCart(before, this.config), { gid });
      const tier = g && nextTier(g);
      if (!tier) return to != null ? changedElsewhere(before) : { ok: false, error: 'Este pack ya no se puede ampliar.', cart: before };
      if (to != null && tier.qty !== to) return changedElsewhere(before);
      return this._apply(g, tier.qty, before, 'upgrade');
    });
  }

  /**
   * Ampliación de la landing (landingUpgradeFor): añade el pack y su regalo con las propiedades de la landing, quita la
   * base suelta y lo comprueba en /cart.js (verifyLandingUpgrade). Si no cuadra o falla a medias, se deshace: fuera lo
   * añadido y vuelve la base con sus propiedades; solo se dice que sigue como estaba si /cart.js lo confirma.
   */
  upgradeLanding(gid) {
    return this._mutate(async () => {
      const before = await this.client.get();
      const g = findGroup(groupCart(before, this.config), { gid });
      const u = landingUpgradeFor(before, g, this.config);
      if (!u) return changedElsewhere(before);
      const base = g.lines[0];
      const items = [
        { id: u.pack.variantId, quantity: 1, properties: u.pack.properties || {} },
        { id: u.gift.variantId, quantity: 1, properties: u.gift.properties || {} },
      ];
      this.log('upgrade-landing', { gid, from: base.variant_id, to: u.pack.variantId });
      let after = null, reason = 'network';
      try {
        await this.client.add(items);
        await this.client.update({ [base.key]: 0 });
        after = await this.client.get();
        const v = verifyLandingUpgrade(after, u);
        if (v.ok) return { ok: true, cart: after, beforeTotal: before.total_price };
        reason = v.reason;
      } catch (e) {
        this.log('upgrade-landing-error', e);
      }
      this.log('upgrade-landing-failed', reason);
      let cart = null;
      try {
        const cur = after || (await this.client.get());
        const had = new Set(before.items.map((l) => l.key));
        const added = cur.items.filter((l) => !had.has(l.key) && (l.variant_id === u.pack.variantId || l.variant_id === u.gift.variantId));
        if (added.length) await this.client.update(Object.fromEntries(added.map((l) => [l.key, 0])));
        if (!cur.items.some((l) => l.key === base.key)) await this.client.add([{ id: base.variant_id, quantity: base.quantity, properties: base.properties || {} }]);
        cart = await this.client.get();
      } catch (e) {
        this.log('rollback-error', e);
      }
      const restored = !!cart && cart.items.some((l) => l.key === base.key && l.quantity === base.quantity)
        && !cart.items.some((l) => l.variant_id === u.pack.variantId);
      return { ok: false, rolledBack: restored, reason, error: restored ? 'No hemos podido mejorar tu pack. Tu carrito sigue como estaba.' : NOT_RESTORED, cart };
    });
  }

  remove(gid) { return this.setQty(gid, 0); }

  addItems(items) {
    return this._mutate(async () => {
      // «Añadir» del carrusel: el carrito confirmado viene en la misma respuesta (sin releer /cart.js)
      const before = this.cart;
      const beforeTotal = before?.total_price ?? null;
      const attributes = this._attrs({ action: 'upsell', items, before });
      let cart;
      try {
        cart = await this._addState(items, attributes);
      } catch (e) {
        await this._revertAttrs(attributes, before);
        return { ok: false, error: e.message || String(e), beforeTotal };
      }
      return cart ? { ok: true, cart: this._withCodes(cart), beforeTotal } : { ok: true, beforeTotal };
    });
  }

  /**
   * Añade un bundle de Kaching (Pack Buena Cara) con sus regalos y lo verifica en /cart.js.
   * Si el precio o los regalos no son los esperados, se quita el pack entero (con sus regalos) y se avisa.
   */
  addBundle(items, expect) {
    return this._mutate(async () => {
      // doble clic o dos pestañas: si ese pack ya está en el carrito no se añade otro desde las recomendaciones
      const before = await this.client.get();
      const already = groupCart(before, this.config).some((g) => g.kaching?.deal === expect.ref.deal && g.variantId === items[0].id);
      if (already) return { ok: true, noop: true, cart: before };
      const attributes = this._attrs({ action: 'bundle', items, expect, before });
      try {
        await this.client.add(items, { attributes });
      } catch (e) {
        await this._revertAttrs(attributes, before);
        throw e;
      }
      const after = await this.client.get();
      const v = verifyBundle(after, this.config, expect);
      if (v.ok) return { ok: true, cart: after, beforeTotal: before.total_price };
      this.log('bundle-verify-failed', v.reason);
      const g = findGroup(groupCart(after, this.config), expect.ref);
      if (g) await this.client.update(planSetQty(g, 0).ops[0].updates).catch((e) => this.log('rollback-error', e));
      // solo se dice que sigue como estaba si el pack ya no está (comprobado con /cart.js)
      let cart = null;
      try { cart = await this.client.get(); } catch { cart = null; }
      const restored = !!cart && !findGroup(groupCart(cart, this.config), expect.ref);
      cart = patchAttrs(cart, await this._revertAttrs(attributes, before));
      return { ok: false, rolledBack: restored, error: restored ? 'No hemos podido añadir el pack con sus regalos. Tu carrito sigue como estaba.' : NOT_RESTORED, reason: v.reason, cart };
    });
  }

  /** Añade un código (BEAUTY) conservando los que ya haya. */
  applyCode(code) {
    return this._mutate(async () => {
      const cur = await this.client.get();
      const codes = (cur.discount_codes || []).map((d) => d.code).filter((c) => c.toUpperCase() !== code.toUpperCase());
      await this.client.setDiscount([...codes, code]);
      return { ok: true, code };
    });
  }

  removeCode(code) {
    return this._mutate(async () => {
      const cur = await this.client.get();
      const codes = (cur.discount_codes || []).map((d) => d.code).filter((c) => c.toUpperCase() !== code.toUpperCase());
      await this.client.setDiscount(codes);
      return { ok: true };
    });
  }

  _mutate(fn) {
    this._emit({ busy: true });
    return this.queue.run(async () => {
      let result;
      try {
        result = await fn();
      } catch (e) {
        result = { ok: false, error: e.message || String(e) };
      }
      try {
        const known = result.cart;
        delete result.cart;
        await this._fresh(known); // el carrito ya verificado se reutiliza: una petición menos
      } catch (e) {
        result = { ...result, ok: false, error: result.error || e.message };
      }
      this._emit({ busy: this.queue.pending > 1, result });
      return result;
    });
  }

  async _apply(g, target, before, action = 'set') {
    const { ops, expect } = planSetQty(g, target);
    if (!ops.length) return { ok: true, noop: true };
    this.log(action, { gid: g.gid, from: g.qty, to: target, ops });
    const attributes = this._attrs({ action, group: g, target, expect, before });
    const beforeTotal = before.total_price;
    let fromOps = null;
    try {
      fromOps = await this._run(ops, attributes);
    } catch (e) {
      // falló a medias (red, stock…): se deshace y SE COMPRUEBA que el carrito ha vuelto a estar como antes
      const r = await this._restore(g, before, expect);
      const cart = patchAttrs(r.cart, await this._revertAttrs(attributes, before));
      return { ok: false, rolledBack: r.restored, error: r.restored ? e.message || String(e) : NOT_RESTORED, cart, beforeTotal };
    }
    // el carrito confirmado por Shopify en la respuesta de la última operación; si no vino, se relee
    const after = fromOps ? this._withCodes(fromOps) : await this.client.get();
    const v = verify(after, this.config, expect);
    if (!v.ok) {
      this.log('verify-failed', v.reason);
      const r = await this._restore(g, before, expect);
      r.cart = patchAttrs(r.cart, await this._revertAttrs(attributes, before));
      // «Hemos dejado tu carrito como estaba» solo si de verdad está como estaba (comprobado con /cart.js)
      const error = !r.restored ? NOT_RESTORED
        : v.priceChanged ? 'El precio de la oferta ha cambiado. Hemos dejado tu carrito como estaba: revisa el nuevo precio.'
          : 'No se ha podido aplicar la oferta. Hemos dejado tu carrito como estaba.';
      return { ok: false, rolledBack: r.restored, error, reason: v.reason, priceChanged: !!v.priceChanged, cart: r.cart, beforeTotal };
    }
    return { ok: true, action, cart: after, beforeTotal };
  }

  /** Atributos de la medición para esta acción (this.annotate, docs/07), o null. Un error aquí nunca para la acción. */
  _attrs(ctx) {
    try { return this.annotate?.(ctx) || null; } catch (e) { this.log('annotate', e); return null; }
  }

  /**
   * Tras un fallo, la anotación vuelve a la de antes (solo si se había enviado una nueva): lo que no se aplicó no se
   * atribuye. Es la única petición que añade la medición, y solo en fallos. Devuelve los atributos escritos, o null.
   */
  async _revertAttrs(attributes, before) {
    if (!attributes) return null;
    const prev = Object.fromEntries(Object.keys(attributes).map((k) => [k, before?.attributes?.[k] ?? '']));
    try {
      await this.client.update(null, prev);
      return prev;
    } catch (e) {
      this.log('annotate-revert', e);
      return null;
    }
  }

  /**
   * Ejecuta las operaciones. Devuelve el carrito que Shopify confirma en la respuesta de la ÚLTIMA (update.js devuelve
   * el carrito completo; add.js, la sección de estado), o null si no vino y hay que releer /cart.js.
   */
  async _run(ops, attributes) {
    let last = null;
    for (const op of ops) {
      last = null;
      if (op.type === 'add') last = await this._addState(op.items, attributes);
      else if (op.type === 'update') {
        if (Object.keys(op.updates).length) last = cartFromUpdate(await this.client.update(op.updates, attributes));
      } else if (op.type === 'remove-group') {
        const cart = await this.client.get();
        const g = findGroup(groupCart(cart, this.config), op.ref);
        if (g) last = cartFromUpdate(await this.client.update(planSetQty(g, 0).ops[0].updates, attributes));
      } else if (op.type === 'add-gift') {
        // el padre ya existe: hay que releer para conocer su key actual
        const cart = await this.client.get();
        const g = findGroup(groupCart(cart, this.config), op.ref);
        if (!g) throw new Error('No se encuentra el pack para el regalo');
        last = await this._addState([{ id: op.gift.variantId, quantity: 1, properties: op.gift.properties, parent_line_key: g.lines[0].key }], attributes);
      } else throw new Error(`Operación desconocida: ${op.type}`);
    }
    return isCart(last) ? last : null;
  }

  /**
   * add.js pidiendo la sección de estado: devuelve el carrito confirmado (o null). La misma respuesta trae la
   * configuración vigente de Kaching; onAddResponse la recibe ANTES de verificar el resultado (docs/06 fase 2).
   */
  async _addState(items, attributes) {
    const json = await this.client.add(items, { state: STATE_SECTION, attributes });
    try { this.onAddResponse?.(json); } catch (e) { this.log('kaching-live', e); }
    return cartFromSections(json, STATE_SECTION);
  }

  /** El JSON de Liquid no trae discount_codes: se conservan los conocidos (un add o un cambio de cantidad no los cambia). */
  _withCodes(cart) {
    return !('discount_codes' in cart) && this.cart?.discount_codes ? { ...cart, discount_codes: this.cart.discount_codes } : cart;
  }

  /**
   * Deshace y comprueba con /cart.js que el grupo ha quedado como estaba: mismas unidades, mismos regalos y, si era
   * una adopción, sin el pack nuevo. Devuelve { restored, cart }; ese carrito se reutiliza para pintar (sin otra petición).
   */
  async _restore(original, before, expect) {
    try {
      await this._rollback(original, before, expect);
    } catch (err) {
      this.log('rollback-error', err);
    }
    let cart = null;
    try { cart = await this.client.get(); } catch { return { restored: false, cart: null }; }
    const groups = groupCart(cart, this.config);
    const g = findGroup(groups, groupRef(original));
    const restored = !!g && g.qty === original.qty && g.giftQty === original.giftQty
      && (!expect.replaces || !findGroup(groups, expect.ref));
    if (!restored) this.log('rollback-incompleto', { esperado: [original.qty, original.giftQty], real: g ? [g.qty, g.giftQty] : null });
    return { restored, cart };
  }

  /** Deja el grupo como estaba antes de la operación (con el carrito releído). */
  async _rollback(original, before, expect) {
    const cart = await this.client.get();
    const groups = groupCart(cart, this.config);
    // pack nuevo de una adopción (base sin propiedades → pack completo)
    if (expect.replaces) {
      const created = findGroup(groups, expect.ref);
      if (created) await this._run(planSetQty(created, 0).ops);
      const old = findGroup(groupCart(await this.client.get(), this.config), groupRef(original));
      const missing = original.qty - (old?.qty || 0);
      if (missing > 0) {
        const item = { id: original.variantId, quantity: missing };
        if (Object.keys(original.props || {}).length) item.properties = original.props;
        await this.client.add([item]);
      }
      return;
    }
    const cur = findGroup(groups, groupRef(original));
    if (cur && cur.editable) {
      const { ops } = planSetQty(cur, Math.min(original.qty, cur.maxQty));
      await this._run(ops);
    } else if (cur) {
      // el pack ya no se reconoce como oferta (p. ej. Kaching cobra otro precio que la página aún no sabe): no se puede
      // bajar de tramo, así que se quita entero y se vuelve a añadir exactamente como estaba
      await this._run(planSetQty(cur, 0).ops);
      const items = [{ id: original.variantId, quantity: original.qty, properties: original.props }];
      for (const gl of original.gifts) items.push({ id: gl.variant_id, quantity: gl.quantity, properties: gl.properties, parent_id: original.variantId });
      await this.client.add(items);
    } else if (original.qty > 0) {
      // se borró por completo: se re-añade igual que estaba
      const items = [{ id: original.variantId, quantity: original.qty, properties: original.props }];
      for (const gl of original.gifts) items.push({ id: gl.variant_id, quantity: gl.quantity, properties: gl.properties, parent_id: original.variantId });
      await this.client.add(items);
    }
  }
}

// ---- bundles.mjs ----
// Clarène Cart · bundles de Kaching añadidos desde las recomendaciones (p. ej. Pack Buena Cara).

// Verificado en /cart.js real (tests/compat/bundles.mjs): con las líneas del widget y la variante A/B del visitante
// el pack sale a su precio y los regalos a 0 €. Sin la variante A/B, Kaching aplica la A (BC-noab): por eso,
// si no se puede saber la variante del visitante, el bundle NO se recomienda.

/**
 * Variante A/B del visitante para un bundle, a partir de lo que devuelve window.kachingBundlesApi.fetchDeals()
 * (la oferta ya resuelta por Kaching para este visitante). Devuelve { ab, price } o null si no se puede verificar:
 * oferta no encontrada, regalos distintos de los configurados o un precio que no es de ninguna variante conocida.
 */
function resolveBundle(bundle, deals) {
  const deal = (deals || []).find((d) => d?.nanoId === bundle.deal);
  const bar = deal?.dealBars?.find((b) => b.id === bundle.bar.id);
  if (!bar || bar.dealBarType !== 'quantity-break' || bar.quantity !== bundle.bar.qty) return null;
  if (bar.discount?.type !== 'specific') return null;
  const giftIds = (bar.freeGifts || []).map((g) => g.id).sort();
  if (JSON.stringify(giftIds) !== JSON.stringify(bundle.gifts.map((g) => g.id).sort())) return null;
  const price = Math.round(Number(bar.discount.value) * 100);
  if (!bundle.ab?.length) return { ab: null, price };
  const ab = bundle.ab.find((v) => v.price === price);
  // Si las dos variantes tuvieran el mismo precio no se podría distinguir: mejor no recomendarlo.
  if (!ab || bundle.ab.filter((v) => v.price === price).length > 1) return null;
  return { ab, price };
}

/** Líneas idénticas a kaching-bundles-block.items() (docs/referencia/kaching-buena-cara.snapshot.json). */
function buildBundleItems(bundle, resolved, bundleId) {
  const main = { deal: bundle.deal, pp: bundle.productId, main: true };
  if (bundle.market != null) main.market = bundle.market;
  Object.assign(main, { v: bundle.version, id: bundleId, bid: bundle.bar.id });
  const abProps = resolved.ab ? { abid: resolved.ab.id, ab: resolved.ab.letter } : {};
  Object.assign(main, abProps);
  return [
    { id: bundle.variantId, quantity: bundle.bar.qty, properties: { __kaching_bundles: JSON.stringify(main) } },
    ...bundle.gifts.map((g) => ({
      id: g.variantId,
      quantity: 1,
      properties: { __kaching_bundles: JSON.stringify({ id: bundleId, deal: bundle.deal, v: bundle.version, ...abProps, gift: g.id }) },
      parent_id: bundle.variantId, // en la misma petición (docs/04 §1.8)
    })),
  ];
}

/** Lo que tiene que haber en /cart.js después del add (se comprueba y, si no, se deshace). */
function bundleExpect(bundle, resolved, bundleId) {
  return { ref: { deal: bundle.deal, id: bundleId }, qty: bundle.bar.qty, price: resolved.price, gifts: bundle.gifts.length };
}

/** Comprueba el bundle en el carrito releído: cantidad, precio (nunca más caro de lo anunciado) y regalos a 0 €. */
function verifyBundle(cart, config, expect) {
  const g = findGroup(groupCart(cart, config), expect.ref);
  if (!g) return { ok: false, reason: 'no se encuentra el pack' };
  if (g.qty !== expect.qty) return { ok: false, reason: `cantidad ${g.qty} ≠ ${expect.qty}` };
  if (g.finalCents > expect.price + PRICE_TOLERANCE) return { ok: false, reason: `precio ${g.finalCents} > ${expect.price}` };
  if (g.giftQty !== expect.gifts) return { ok: false, reason: `regalos x${g.giftQty} ≠ ${expect.gifts}` };
  if (g.giftFinalCents !== 0) return { ok: false, reason: 'algún regalo se está cobrando' };
  return { ok: true, group: g };
}

// ---- upsells.mjs ----
// Clarène Cart · recomendaciones «Completa tu rutina» (puro, sin DOM ni red).
// Fuentes: manual (editor de Shopify), más vendidos de una colección, relacionados (recomendaciones nativas de Shopify).

const MODES = ['manual', 'bestsellers', 'related', 'hybrid'];

/** Qué fuentes automáticas hay que cargar para el modo configurado. */
function sourcesFor(upsell) {
  const mode = MODES.includes(upsell?.mode) ? upsell.mode : 'hybrid';
  if (mode === 'manual') return [];
  if (mode === 'bestsellers') return upsell.collection ? ['bestsellers'] : [];
  if (mode === 'related') return ['related'];
  // híbrido: se completa con la fuente elegida (y, si es "más vendidos" sin colección, con relacionados)
  return upsell.fill === 'bestsellers' && upsell.collection ? ['bestsellers'] : ['related'];
}

/** Productos del carrito que sirven de ancla para "relacionados" (los que más pesan primero, máximo 2). */
function relatedAnchors(groups, limit = 2) {
  const ids = [];
  const priced = [...groups].sort((a, b) => b.finalCents - a.finalCents);
  for (const g of priced) {
    const pid = g.lines[0]?.product_id;
    if (pid && !ids.includes(pid)) ids.push(pid);
    if (ids.length >= limit) break;
  }
  return ids;
}

/** Extrae el JSON de la sección clarene-cart-upsells (respuesta HTML del Section Rendering API). */
function parseUpsellSection(html) {
  const m = String(html || '').match(/<script[^>]*data-cc-upsells[^>]*>([\s\S]*?)<\/script>/);
  if (!m) return [];
  try {
    const list = JSON.parse(m[1]);
    return Array.isArray(list) ? list.filter((p) => p && p.productId && p.variantId) : [];
  } catch {
    return [];
  }
}

/**
 * Productos que no se deben recomendar ahora mismo, con el motivo (útil para depurar y para las pruebas).
 *  - ya están en el carrito (cualquier variante)
 *  - excluidos en el editor
 *  - regalos de un pack que la clienta ya tiene o puede conseguir gratis con el upgrade
 *  - la base de una oferta si ya hay un pack de esa oferta (el upgrade de Kaching es la recomendación)
 */
function blockedProducts(cart, groups, config) {
  const blocked = new Map();
  for (const [id, reasons] of blockReasons(cart, groups, config)) blocked.set(id, reasons[0]);
  return blocked;
}

/** Igual que blockedProducts, pero con todos los motivos de cada producto (en el mismo orden de prioridad). */
function blockReasons(cart, groups, config) {
  const blocked = new Map();
  const block = (id, why) => {
    if (!id) return;
    const r = blocked.get(String(id)) || [];
    if (!r.includes(why)) r.push(why);
    blocked.set(String(id), r);
  };
  for (const l of cart?.items || []) block(l.product_id, 'en el carrito');
  for (const id of config.upsell?.exclude || []) block(id, 'excluido');
  const offers = activeOffers(config);
  for (const o of offers) {
    const packs = groups.filter((g) => g.offer?.deal === o.deal);
    if (!packs.length) continue;
    block(o.productId, packs.some((g) => nextTier(g)) ? 'upgrade de Kaching' : 'pack ya en el carrito');
  }
  // Regalos: solo se excluyen cuando la clienta YA los recibe gratis en el carrito (línea de regalo presente).
  // Un pack de 2 bases no lleva la brocha, así que la brocha sí puede recomendarse.
  for (const g of groups) for (const l of g.gifts) block(l.product_id, 'regalo del pack');
  return blocked;
}

/** Bundles configurados que compiten con un pack que ya está en el carrito (Pack Buena Cara frente a la Base). */
function competingBundles(groups, config) {
  const dealsInCart = new Set(groups.filter((g) => g.offer).map((g) => g.offer.deal));
  return new Set((config.bundles || []).filter((b) => (b.competesWith || []).some((d) => dealsInCart.has(d))).map((b) => String(b.productId)));
}

/**
 * Elige los productos a mostrar. Todos se añaden sin salir del carrito:
 *  - 'add'    producto normal (una variante)
 *  - 'choose' producto con tonos: selector en la tarjeta, solo tonos disponibles
 *  - 'bundle' bundle de Kaching (Pack Buena Cara) con su variante A/B y sus regalos
 * Lo que no se puede añadir de forma verificable no se muestra (nunca se manda a la ficha).
 * @param manual       productos del editor (en su orden)
 * @param automatic    productos de las fuentes automáticas (relacionados / más vendidos), ya en orden
 * @param bundleState  Map productId → { ab, price } (resolveBundle) o null si no se puede verificar
 */
function pickUpsells({ cart, groups, config, manual = [], automatic = [], bundleState = new Map() }) {
  const u = config.upsell || {};
  if (u.enabled === false) return [];
  const mode = MODES.includes(u.mode) ? u.mode : 'hybrid';
  const max = Math.max(1, Math.min(8, Number(u.max) || 4));
  const blocked = blockReasons(cart, groups, config);
  const inCartVariants = new Set((cart?.items || []).map((l) => String(l.variant_id)));
  const competing = competingBundles(groups, config);
  const bundles = new Map((config.bundles || []).map((b) => [String(b.productId), b]));
  const tag = (list, source) => list.map((p) => ({ p, source }));
  let pool = mode === 'manual' ? tag(manual, 'manual') : mode === 'hybrid' ? [...tag(manual, 'manual'), ...tag(automatic, 'auto')] : tag(automatic, 'auto');
  // Con Base Inteligente en el carrito, un pack que compite con sus upgrades no se recomienda automáticamente
  // y, si lo eligió el equipo a mano, pasa al final (primero lo complementario).
  pool = pool.filter(({ p, source }) => !(source === 'auto' && competing.has(String(p.productId))));
  pool = [...pool.filter(({ p }) => !competing.has(String(p.productId))), ...pool.filter(({ p }) => competing.has(String(p.productId)))];

  const seen = new Set();
  const out = [];
  for (const { p } of pool) {
    const id = String(p.productId);
    if (seen.has(id)) continue; // duplicados entre fuentes
    seen.add(id);
    if (!p.available) continue;
    // Un producto con tonos que solo está bloqueado por tener ALGÚN tono en el carrito sigue disponible con los
    // tonos que faltan (más abajo). Cualquier otro motivo (excluido, regalo, base con pack…) lo bloquea entero.
    const reasons = blocked.get(id) || [];
    const onlyInCart = reasons.length === 1 && reasons[0] === 'en el carrito';
    if (reasons.length && !(onlyInCart && p.multi && !bundles.has(id))) continue;
    let item;
    if (bundles.has(id)) {
      const resolved = bundleState.get(id);
      if (!resolved) continue; // sin variante A/B verificable: no se recomienda
      const b = bundles.get(id);
      const was = Math.max(p.compareAt || 0, p.price || 0);
      item = { ...p, action: 'bundle', price: resolved.price, compareAt: was > resolved.price ? was : 0, giftCount: b.gifts.length };
    } else if (p.multi) {
      // Tonos disponibles que aún no están en el carrito, en su orden original (el primero queda seleccionado).
      // Para sumar unidades de un tono ya comprado están los +/− de su línea.
      const options = (p.variants || []).filter((v) => v.available && !inCartVariants.has(String(v.id)));
      if (!options.length) continue; // sin tonos disponibles, todos ya en el carrito o sin datos de variantes
      item = { ...p, action: 'choose', options };
    } else {
      item = { ...p, action: 'add' };
    }
    out.push(item);
    if (out.length >= max) break;
  }
  return out;
}

/** Líneas a añadir para un upsell normal. La base de una oferta entra con la forma del widget (pack de 1). */
function upsellItems(product, config, { buildMainProps, offerTier, randomBundleId }, variantId = product.variantId) {
  if (product.action === 'bundle') throw new Error('Los packs se añaden con addBundle');
  if (product.action === 'choose' && !product.options.some((v) => String(v.id) === String(variantId))) throw new Error('Elige un tono disponible');
  const id = Number(variantId);
  const offer = newPackOffer(config, id); // en Latinoamérica, la variante A/B de la visitante
  if (offer) return [{ id: offer.variantId, quantity: 1, properties: buildMainProps(offer, offerTier(offer, 1), randomBundleId()) }];
  return [{ id, quantity: 1 }];
}

// ---- viewport.mjs ----
// Clarène Cart · encaje del carrito en el área visible (puro, se prueba en Node).
//
// En Safari iOS el teclado NO encoge el layout viewport (window.innerHeight se queda igual); solo encoge el
// visualViewport, y Safari desplaza la página para enseñar el campo enfocado. Por eso:
//  - el carrito se coloca sobre el área visible (top = visualViewport.offsetTop, alto = visualViewport.height);
//  - con el teclado abierto no se reserva la barra inferior de la vista previa de Shopify: queda detrás del
//    teclado, y reservarla mientras Safari desplaza la página daba un hueco variable (0–102 px) que hacía saltar
//    el carrito. Esta regla no depende de la vista previa: sin barra, la reserva es 0 en cualquier caso;
//  - con el teclado abierto el pie fijo (total + botón) se retira para dejar sitio al campo y a «Aplicar»; vuelve
//    en cuanto se cierra el teclado.

const KEYBOARD_MIN = 120; // px que el teclado le quita al área visible para considerarlo abierto

/**
 * @param innerHeight  window.innerHeight (layout viewport)
 * @param vvHeight     visualViewport.height
 * @param vvTop        visualViewport.offsetTop
 * @param bar          { top, height } de la barra de vista previa (getBoundingClientRect) o null
 * @param editing      hay un campo de texto del carrito enfocado
 */
function viewportLayout({ innerHeight, vvHeight, vvTop = 0, bar = null, editing = false }) {
  const h = Math.max(0, Math.round(vvHeight));
  const top = Math.max(0, Math.round(vvTop));
  const keyboard = !!editing && innerHeight - vvHeight > KEYBOARD_MIN;
  let reserve = 0;
  if (!keyboard && bar && bar.height > 0) {
    const visibleBottom = top + h;
    if (bar.top < visibleBottom) reserve = Math.max(0, Math.min(200, Math.round(visibleBottom - bar.top)));
  }
  const usable = h - reserve;
  return { vh: h, vt: top, reserve, keyboard, compact: usable < 740, tiny: usable < 520 };
}

/** Cuánto hay que desplazar el contenedor para dejar el campo centrado en su zona visible (0 si ya se ve). */
function scrollDeltaToReveal(field, scroller, margin = 12) {
  const top = field.top - scroller.top;
  const bottom = field.bottom - scroller.top;
  if (top >= margin && bottom <= scroller.height - margin) return 0;
  return Math.round(top - (scroller.height - (field.bottom - field.top)) / 2);
}

// ---- carousel.mjs ----
// Clarène Cart · navegación del carrusel «Completa tu rutina» en escritorio (puro, se prueba en Node).
//
// En móvil se desliza con el dedo (nativo, sin cambios). En escritorio se añaden flechas en el encabezado y
// Shift + rueda; el trackpad sigue desplazando de forma nativa.

const EDGE = 4; // px de tolerancia para considerar que se está en el principio o el final

/** Estado del carrusel: si hay contenido fuera del área visible y si se está en un extremo. */
function carouselState({ scrollLeft, clientWidth, scrollWidth }) {
  const max = Math.max(0, scrollWidth - clientWidth);
  return { scrollable: max > EDGE, atStart: scrollLeft <= EDGE, atEnd: scrollLeft >= max - EDGE };
}

/** Paso de una flecha: ancho real de una tarjeta más la separación entre tarjetas. */
function carouselStep(cardWidth, gap) {
  return Math.max(1, (Number(cardWidth) || 0) + (Number(gap) || 0));
}

/**
 * Posición de destino al avanzar (dir = 1) o retroceder (dir = -1) una tarjeta, alineada con las tarjetas
 * (coincide con los puntos de scroll-snap) y limitada a los extremos.
 */
function carouselTarget({ scrollLeft, clientWidth, scrollWidth }, step, dir) {
  const max = Math.max(0, scrollWidth - clientWidth);
  const index = Math.round(scrollLeft / step) + (dir > 0 ? 1 : -1);
  return Math.min(max, Math.max(0, index * step));
}

/**
 * Shift + rueda del ratón → dirección horizontal (1 / -1), o 0 si no hay que intervenir:
 *  - sin Shift: es el scroll vertical normal de la página;
 *  - con desplazamiento horizontal propio (trackpad, o el navegador ya convierte Shift + rueda): lo hace el navegador.
 */
function wheelDirection({ deltaX = 0, deltaY = 0, shiftKey = false }) {
  if (!shiftKey || deltaX !== 0 || deltaY === 0) return 0;
  return deltaY > 0 ? 1 : -1;
}

// ---- drawer.mjs ----
// Clarène Cart · interfaz del carrito lateral. Solo pinta el estado de /cart.js; nunca calcula totales.









const UPSELL_TTL = 30 * 60 * 1000; // recomendaciones cacheadas 30 min por sesión: pocas peticiones a la tienda

const $ = (sel, root = document) => root.querySelector(sel);
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
// CDN de Shopify: el ancho se pide con ?width= (si ya viene, se respeta). La misma imagen llega como
// cdn.shopify.com/s/files/1/…/files/X (desde /cart.js) o como //dominio/cdn/shop/files/X (desde Liquid): se usa
// siempre la segunda forma, en el propio dominio, para que sea la misma URL (misma caché, sin repintar ni descargar dos veces).
const cdnPath = (url) => String(url)
  .replace(/^(https?:)?\/\/cdn\.shopify\.com\/s\/files\/1\/\d+\/\d+\/\d+\/(files|products)\//, '/cdn/shop/$2/')
  .replace(/^(https?:)?\/\/[^/]+(\/cdn\/shop\/)/, '$2');
// imágenes del widget de Kaching para esas ventajas (mediaImageGID → { url }); la build pone aquí
// docs/referencia/kaching-media.snapshot.json
const KACHING_MEDIA = {"gid://shopify/MediaImage/74791973650766":{"url":"https://cdn.shopify.com/s/files/1/1035/9029/7934/files/REGALO.png?v=1792869894"},"gid://shopify/MediaImage/74791973617998":{"url":"https://cdn.shopify.com/s/files/1/1035/9029/7934/files/ENVIO.png?v=1792869893"}};
// iconos de las ventajas solo de texto de un pack (packPerks) si su imagen de Kaching no está en la referencia
const PERK_ICON_GIFT = '<svg viewBox="0 0 24 24" width="16" height="16"><path d="M4 11h16v9H4zM3 7h18v4H3zM12 7v13M12 7c-1.5-3-5-3.5-5-1.2C7 7 9 7 12 7zm0 0c1.5-3 5-3.5 5-1.2C17 7 15 7 12 7z" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>';
const PERK_ICON_SHIPPING = '<svg viewBox="0 0 24 24" width="16" height="16"><path d="M2 6h12v10H2zM14 9h4l3 3v4h-7zM6 18.5a1.5 1.5 0 1 0 0-.01M17 18.5a1.5 1.5 0 1 0 0-.01" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>';
const ICON_CHECK = '<svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const ICON_REPEAT = '<svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true"><path d="M4 12a8 8 0 0 1 13.7-5.6L20 8.7M20 4v4.7h-4.7M20 12a8 8 0 0 1-13.7 5.6L4 15.3M4 20v-4.7h4.7" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
// «+ regalo MISTERIOSO» de Kaching y los saldos de Shopify Flow: se cuenta igual en los dos sitios, sin revelar el importe
// Solo la imagen y el título: sin texto secundario sobre el contenido o la entrega (decisión de la dueña, 2026-10-29)
const MYSTERY = { text: 'Regalo misterioso incluido' };
const isMystery = (text) => /misterios/i.test(text || '');
const img = (url, w) => { if (!url) return ''; const u = cdnPath(url); return /[?&]width=/.test(u) ? u : u + (u.includes('?') ? '&' : '?') + 'width=' + w; };

/**
 * Pinta un bloque solo si su HTML cambia: un repintado idéntico (p. ej. al confirmar /cart.js al abrir) no recrea las
 * imágenes ni mueve el carrusel. Devuelve true si ha cambiado.
 */
function setHTML(el, html) {
  if (!el || el.__ccHtml === html) return false;
  el.innerHTML = html;
  el.__ccHtml = html;
  return true;
}

function readJSON(id) {
  try { return JSON.parse(document.getElementById(id)?.textContent || 'null'); } catch { return null; }
}

/**
 * Ofertas de Kaching ya resueltas para la visitante (fetchDeals no hace peticiones; responde cuando Kaching ha
 * descargado su configuración, a los 1–4 s). Espera a que cargue su script; null si no llega.
 */
async function kachingDealsWhenReady(maxWaitApi, maxWaitDeals) {
  const t0 = Date.now();
  while (!window.kachingBundlesApi?.fetchDeals && Date.now() - t0 < maxWaitApi) await new Promise((r) => setTimeout(r, 150));
  try {
    const api = window.kachingBundlesApi;
    return api?.fetchDeals ? await Promise.race([api.fetchDeals(), new Promise((r) => setTimeout(() => r(null), maxWaitDeals))]) : null;
  } catch {
    return null;
  }
}

/** Id de sesión de Kaching (con él asigna la variante A/B), o null. */
function kachingSession() {
  try { return localStorage.getItem('kaching_session_id'); } catch { return null; }
}

// Configuración de Kaching con más de 5 min (pestaña abierta un rato): al abrir el carrito se pide la vigente en segundo
// plano; con más de 2 min, se comprueba también antes de aplicar un upgrade (ensureFresh)
const KACHING_MAX_AGE = 5 * 60 * 1000;
const KACHING_CHECK_AGE = 2 * 60 * 1000;

/**
 * La misma consulta que hace Kaching en cada página (Storefront API, con el token público que publica Kaching en
 * #kaching-bundles-config). Solo para refrescar una pestaña abierta mucho tiempo. Devuelve los bloques o null.
 */
async function fetchDealBlocks() {
  const cfg = readJSON('kaching-bundles-config');
  if (!cfg?.storefrontAccessToken) return null;
  const query = 'query FetchDealBlocks($metafieldNamespace: String) { shop { metafield(namespace: $metafieldNamespace, key: "deal_blocks") { value } } }';
  const res = await window.fetch('/api/2026-07/graphql.json', {
    method: 'POST', credentials: 'omit',
    headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': cfg.storefrontAccessToken },
    body: JSON.stringify({ query, variables: { metafieldNamespace: '$app:kaching_bundles' } }),
  });
  if (!res.ok) return null;
  const value = (await res.json())?.data?.shop?.metafield?.value;
  try { return value ? JSON.parse(value) : null; } catch { return null; }
}

class ClareneCart {
  constructor(root, config, initialCart) {
    this.root = root;
    this.config = config;
    this.debug = /[?&]clarene_cart=debug/.test(location.search);
    this.money = (c) => formatMoney(c, this.config.currency || 'EUR', this.config.locale || 'es-ES');
    this.client = new CartClient({ fetch: (...a) => window.fetch(...a), root: config.routes?.root || '/' });
    this.ctl = new CartController({ client: this.client, config, log: (...a) => this.debug && console.info('[clarene-cart]', ...a) });
    // medición (docs/07): eventos cc_* en GA4 y anotación _cc dentro de las peticiones que ya hace el carrito
    this.track = new Tracker(window);
    this.ctl.annotate = (ctx) => annotate(ctx, this.track.context());
    this.body = $('[data-cc-body]', root);
    this.foot = $('[data-cc-foot]', root);
    this.details = $('[data-cc-details]', root);
    this.summary = $('[data-cc-summary]', root);
    this.upsEl = $('[data-cc-ups]', root);
    this.imgLoading = 'lazy'; // ver warmImages
    this.bundleState = new Map(); // productId → variante A/B resuelta por Kaching (o null)
    this.comps = {}; // packs nativos: variante del pack → componentes (clarene-cart-components)
    this.bundlesLoaded = false;
    this.totalEl = $('[data-cc-total]', root);
    this.auto = []; // recomendaciones automáticas cargadas
    this.autoSig = null;
    this.shown = []; // recomendaciones pintadas ahora mismo
    this.status = $('[data-cc-status]', root);
    this.busy = false;
    this.notice = null;
    this.ctl.subscribe((s) => this.onState(s));
    // adds del propio carrito (upgrades, carrusel): su respuesta trae la configuración vigente de Kaching
    this.ctl.onAddResponse = (json) => {
      this.learnComponents(json?.sections?.['clarene-cart-state']);
      const live = liveFromSections(json, 'clarene-cart-state');
      if (live && this.kaching) this.applyLive(live, 'respuesta del add', { regroup: false });
    };
    // pestaña que vuelve a primer plano (o de la caché del navegador) con el carrito abierto
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible' && !this.root.hidden) this.refreshIfStale(); });
    window.addEventListener('pageshow', (e) => { if (e.persisted && !this.root.hidden) this.refreshIfStale(); });
    this.bind();
    if (initialCart) this.ctl.hydrate(initialCart);
  }

  // ---------- eventos ----------
  bind() {
    this.root.addEventListener('click', (e) => {
      const t = e.target.closest('[data-cc-action]');
      if (!t || t.disabled) return;
      const { ccAction: action, gid, qty, code, index } = t.dataset;
      if (action === 'close') return this.close();
      if (this.busy) return; // la cola ya serializa; además ignoramos clics mientras hay una operación
      if (action === 'upgrade' || action === 'qty') {
        const g = this.ctl.groups.find((x) => x.gid === gid);
        const up = action === 'upgrade' || Number(qty) > (g?.qty ?? 0);
        const meta = action === 'upgrade' && g ? this.upgradeMeta(g) : null;
        if (meta) this.track.send('cc_upgrade_click', meta.params);
        this.preflight(g, up).then((ok) => {
          if (!ok) {
            if (meta) this.track.send('cc_upgrade_fail', { ...meta.params, reason: 'offer_refreshed' });
            return;
          }
          // se manda lo que veía la clienta: si el carrito real ya no es ese (otra pestaña), no se aplica nada
          if (action === 'upgrade') this.run(this.ctl.upgrade(gid, { to: g ? nextTier(g)?.qty : undefined }), 'upgrade', meta);
          else this.run(this.ctl.setQty(gid, Number(qty), { from: g?.qty }));
        });
      }
      else if (action === 'upgrade-landing') {
        const g = this.ctl.groups.find((x) => x.gid === gid);
        const u = g && landingUpgradeFor(this.ctl.cart, g, this.config);
        const meta = u ? { event: 'cc_upgrade', params: { pack_from: '1 ud.', pack_to: 'pack', from_qty: 1, to_qty: 2, deal: null, item_id: g.lines[0]?.product_id ?? null, announced_value: moneyParams(u.delta, this.moneyCtx()).value } } : null;
        if (meta) this.track.send('cc_upgrade_click', meta.params);
        this.run(this.ctl.upgradeLanding(gid), 'upgrade', meta);
      }
      else if (action === 'remove') this.run(this.ctl.remove(gid));
      else if (action === 'remove-code') this.run(this.ctl.removeCode(code));
      else if (action === 'upsell') this.addUpsell(Number(index));
    });
    this.root.addEventListener('change', (e) => {
      if (e.target.matches('[data-cc-variant]')) this.onVariantChange(e.target);
    });
    this.root.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
      if (e.key === 'Tab') this.trapFocus(e);
    });

    // Iconos del carrito del tema
    document.addEventListener('click', (e) => {
      const a = e.target.closest('.js-drawer-open-cart, a[href$="/cart"][data-cc-open], [data-cc-open]');
      if (!a || document.body.classList.contains('template-cart')) return;
      e.preventDefault();
      this.open(true);
    }, true);
    const intent = (e) => { if (e.target.closest?.('.js-drawer-open-cart, a[href$="/cart"][data-cc-open], [data-cc-open]')) this.warmImages(); };
    for (const ev of ['pointerover', 'touchstart', 'focusin']) document.addEventListener(ev, intent, { passive: true, capture: true });
    document.addEventListener('cart:open', () => this.open(true));
    document.addEventListener('cart:close', () => this.close());
    // El tema avisa de un add: si Clarène Cart está recibiendo (o acaba de recibir) el carrito confirmado en la misma
    // respuesta, no se abre con el contenido anterior ni se relee /cart.js; lo abre el vigilante con el carrito nuevo.
    document.addEventListener('ajaxProduct:added', () => {
      if (this.addsInFlight > 0) {
        // salvaguarda: si la respuesta confirmada no llega, se abre como siempre (releyendo /cart.js)
        const at = Date.now();
        setTimeout(() => { if (this.root.hidden && (this.confirmedAt || 0) < at) this.open(true); }, 2500);
        return;
      }
      if (Date.now() - (this.confirmedAt || 0) < 2000) return this.open(false);
      this.open(true);
    });
    document.addEventListener('cart:build', () => this.ctl.refresh().catch(() => {}));
    this.watchExternalMutations();
  }

  /** Cambios del carrito hechos por otros scripts (widget de Kaching, quick-add, popup de Klaviyo con BEAUTY…). */
  watchExternalMutations() {
    let timer, pendingAdd = false;
    this.addsInFlight = 0;
    const settle = () => { this.addsInFlight = Math.max(0, this.addsInFlight - 1); };
    installCartWatcher(window, (kind, cart, json) => {
      if (kind === 'add' || kind === 'add-failed') settle();
      if (kind === 'add-failed') return; // el tema muestra su error; no se abre el carrito (como antes)
      if (kind === 'add' && cart) {
        // carrito confirmado por Shopify en la respuesta del add: se pinta y se abre con él (una sola petición)
        this.confirmedAt = Date.now();
        this.learnComponents(json?.sections?.['clarene-cart-state']);
        // la misma respuesta trae la configuración vigente de Kaching: se aplica antes de agrupar el carrito nuevo
        const live = liveFromSections(json, 'clarene-cart-state');
        if (live) this.applyLive(live, 'respuesta del add', { regroup: false });
        // códigos introducidos (o desconocidos: el carrito inicial de Liquid no los trae)
        const codes = this.ctl.cart?.discount_codes;
        const hadCodes = !Array.isArray(codes) || codes.length > 0;
        this.ctl.useCart(cart).then(() => {
          this.open(false);
          // con códigos introducidos, su estado («No aplicable») se confirma en segundo plano, sin esperar
          if (hadCodes) setTimeout(() => this.ctl.confirm().catch(() => {}), 0);
        }, () => this.open(true));
        return;
      }
      pendingAdd = pendingAdd || kind === 'add';
      clearTimeout(timer);
      timer = setTimeout(() => {
        const add = pendingAdd;
        pendingAdd = false;
        if (add) this.open(true);
        else this.refreshOnce(); // p. ej. BEAUTY aplicado por Klaviyo: se releen todos los importes, sin abrir el carrito
      }, 50);
    }, { stateSection: 'clarene-cart-state', onAddStart: () => {
      this.addsInFlight++;
      this.warmImages();
      // add desde una ficha: la línea nueva casi siempre es ese producto; su imagen se descarga mientras Shopify responde
      if (this.config.pageImage && !this.pageImageWarmed) {
        this.pageImageWarmed = true;
        for (const w of [200, 96]) new Image().src = img(this.config.pageImage, w); // la línea y la de su unidad gratis o regalo
      }
    } });
  }

  async run(promise, kind, meta) {
    const res = await promise;
    if (meta) this.trackResult(meta, res);
    // el precio real no era el anunciado (la página tenía una configuración antigua; también pasa unos segundos tras
    // guardar en Kaching, mientras Shopify propaga el cambio): se pide la vigente a la fuente de Kaching y se aplica
    // aunque parezca igual, para que lo que se vuelve a anunciar sea lo que se cobra
    if (res?.priceChanged) await this.forceFreshConfig();
    if (res && !res.ok) this.notice = { type: 'error', text: res.error || 'No se ha podido actualizar el carrito.' };
    else if (kind === 'upgrade') this.notice = { type: 'ok', text: '¡Pack actualizado!' };
    else this.notice = null;
    this.render();
    if (this.notice?.text) this.status.textContent = this.notice.text;
  }

  async addUpsell(index) {
    const u = this.shown[index];
    if (!u) return;
    const select = this.upsEl.querySelector(`[data-cc-variant][data-index="${index}"]`);
    const variantId = Number(select ? select.value : u.variantId);
    // la base de una oferta: con la configuración vigente y, con test A/B, con la variante que confirma Kaching
    const packOffer = u.action === 'bundle' ? null : newPackOffer(this.config, variantId);
    const meta = { event: 'cc_rutina', params: { item_id: u.productId ?? null, variant_id: variantId || null, item_name: u.title || '', position: index + 1, kind: u.action === 'bundle' ? 'pack' : packOffer ? 'base' : 'producto' } };
    this.track.send('cc_rutina_click', meta.params);
    const fail = (reason) => this.track.send('cc_rutina_fail', { ...meta.params, reason });
    if (u.action === 'bundle') {
      // Pack de Kaching (Pack Buena Cara): líneas del widget con la variante A/B del visitante; se verifica y,
      // si el precio o los regalos no cuadran en /cart.js, se quita entero.
      const b = (this.config.bundles || []).find((x) => String(x.productId) === String(u.productId));
      const resolved = this.bundleState.get(String(u.productId));
      if (!b || !resolved) return fail('unavailable');
      const id = randomBundleId();
      return this.run(this.ctl.addBundle(buildBundleItems(b, resolved, id), bundleExpect(b, resolved, id)), 'upsell', meta);
    }
    if (packOffer && !(await this.preflight({ kind: 'plain', offer: packOffer }, true))) return fail('offer_refreshed');
    let items;
    try {
      // la base de una oferta entra con la forma del widget (pack de 1) para que Kaching la reconozca
      items = upsellItems(u, this.config, { buildMainProps, offerTier, randomBundleId }, select ? select.value : u.variantId);
    } catch (e) {
      fail('error');
      this.notice = { type: 'error', text: e.message };
      return this.render();
    }
    this.run(this.ctl.addItems(items), 'upsell', meta);
  }

  // ---------- medición (docs/07) ----------
  /** Lo que veía la clienta al pulsar «Mejorar»: tramos e importe anunciado. */
  upgradeMeta(g) {
    const tier = nextTier(g);
    if (!tier || !this.ctl.cart) return null;
    const { deltaCents } = upgradeDelta(this.ctl.cart, g, tier);
    return {
      event: 'cc_upgrade',
      params: {
        pack_from: packLabel(g.tier, g.qty), pack_to: packLabel(tier, tier.qty), from_qty: g.qty, to_qty: tier.qty,
        deal: g.offer?.deal ?? null, item_id: g.lines[0]?.product_id ?? null,
        announced_value: moneyParams(deltaCents, this.moneyCtx()).value,
      },
    };
  }

  moneyCtx() {
    return { ...this.track.context(), currency: this.ctl.cart?.currency || this.track.context().currency };
  }

  /**
   * Éxito solo con el carrito que Shopify ha confirmado (y verificado); el importe es lo que ha subido de verdad su
   * total, con descuentos y en la moneda del mercado. Un doble clic o una acción sin cambios (noop) no envía nada.
   */
  trackResult(meta, res) {
    if (!res || res.noop) return;
    if (!res.ok) return this.track.send(`${meta.event}_fail`, { ...meta.params, reason: failReason(res) });
    const after = this.ctl.cart?.total_price;
    const delta = res.beforeTotal != null && after != null ? after - res.beforeTotal : null;
    this.track.send(`${meta.event}_success`, { ...meta.params, ...(delta != null ? moneyParams(delta, this.moneyCtx()) : {}) });
  }

  /** Recomendaciones vistas: una vez por apertura, cuando el carrusel tiene productos. */
  trackView() {
    if (this.viewSent || this.root.hidden || !this.shown.length) return;
    this.viewSent = true;
    this.track.send('cc_rutina_view', { item_ids: this.shown.map((p) => p.productId).join(',').slice(0, 100), count: this.shown.length });
  }

  upPrice(v, p) {
    // Con un código de pedido activo: precio de venta tachado → precio estimado con el código, sin «≈» (decisión
    // de la usuaria: acepta ±0,01 € por el reparto de céntimos de Shopify; el carrito muestra luego el importe real).
    // No para bundles ni la base de una oferta (Kaching cambia su precio).
    const base = p && activeOffers(this.config).some((o) => String(o.productId) === String(p.productId));
    // la base entra como pack de 1 de la oferta: se anuncia el precio de ese tramo, que es el que cobra Kaching
    const pack = base && p.action !== 'bundle' ? newPackOffer(this.config, Number(v.id)) : null;
    const t1 = pack ? offerTier(pack, 1) : null;
    if (t1) {
      const was = Math.max(v.compareAt || 0, v.price);
      return `${was > t1.price ? `<s>${this.money(was)}</s> ` : ''}<strong>${this.money(t1.price)}</strong>`;
    }
    const est = p && p.action !== 'bundle' && !base ? addPriceWithCode(this.ctl.cart, v.price) : null;
    if (est && est.cents < v.price) return `<s>${this.money(v.price)}</s> <strong>${this.money(est.cents)}</strong>`;
    return `${v.compareAt > v.price ? `<s>${this.money(v.compareAt)}</s> ` : ''}<strong>${this.money(v.price)}</strong>`;
  }

  /** Al cambiar de tono, se actualizan el precio y la imagen de esa tarjeta. */
  onVariantChange(select) {
    const p = this.shown[Number(select.dataset.index)];
    const v = p?.options?.find((o) => String(o.id) === select.value);
    if (!v) return;
    const card = select.closest('.cc-up');
    card.querySelector('[data-cc-up-price]').innerHTML = this.upPrice(v, p);
    const im = card.querySelector('.cc-up__img img');
    if (im && v.image) im.src = img(v.image, 120);
  }

  // ---------- recomendaciones automáticas ----------
  /** Carga relacionados / más vendidos solo cuando cambian los productos del carrito (y con caché de sesión). */
  maybeLoadAuto() {
    const u = this.config.upsell;
    if (!u?.enabled || this.root.hidden || !this.ctl.cart) return;
    this.loadBundles();
    let sources = sourcesFor(u);
    const anchors = relatedAnchors(this.ctl.groups);
    if (sources.includes('related') && !anchors.length) sources = u.collection ? ['bestsellers'] : [];
    const sec = encodeURIComponent(u.section || 'clarene-cart-upsells');
    const paths = [];
    if (sources.includes('bestsellers')) paths.push(`collections/${encodeURIComponent(u.collection)}?sort_by=best-selling&section_id=${sec}`);
    if (sources.includes('related')) for (const id of anchors) paths.push(`recommendations/products?product_id=${id}&limit=8&intent=related&section_id=${sec}`);
    const sig = paths.join('|');
    if (sig === this.autoSig) return;
    this.autoSig = sig;
    if (!paths.length) { this.auto = []; return; }
    (async () => {
      const lists = [];
      for (const p of paths) lists.push(await this.fetchSection(p)); // de una en una
      if (this.autoSig !== sig) return;
      this.auto = lists.flat();
      this.render();
    })();
  }

  /**
   * Variante A/B de cada bundle para este visitante, tal como la resuelve Kaching (fetchDeals no hace peticiones).
   * Si Kaching no está o no responde en 2 s, los bundles no se recomiendan; el resto del carrito sigue igual.
   */
  async loadBundles() {
    if (this.bundlesLoaded || !(this.config.bundles || []).length) return;
    this.bundlesLoaded = true;
    let deals = null;
    try {
      deals = await kachingDealsWhenReady(10000, 8000);
    } catch { deals = null; }
    let changed = false;
    for (const b of this.config.bundles) {
      const r = deals ? resolveBundle(b, deals) : null;
      const key = String(b.productId);
      if (JSON.stringify(this.bundleState.get(key) ?? null) !== JSON.stringify(r)) changed = true;
      this.bundleState.set(key, r);
    }
    if (changed) this.render(); // sin cambios (p. ej. el bundle no se recomienda), nada se mueve
  }

  // ---------- Kaching en vivo (docs/06, fase 2) ----------
  /**
   * La configuración de Kaching que pintó Liquid ya está aplicada desde el primer render (boot). Aquí, en segundo
   * plano y sin peticiones:
   *  - se compara con lo que la propia Kaching descarga en esta página (observado en <head>): si Kaching tiene algo más
   *    nuevo que la página, se usa lo suyo;
   *  - fetchDeals confirma la variante A/B de la visitante (la predicción por sesión solo adelanta el render; crear un
   *    pack espera a esta confirmación, ensureVisitor). Si lo que da Kaching no cuadra con la configuración, ese deal
   *    pasa a genérico.
   * Si Liquid no trajo la configuración, las referencias se usan sin anunciar upgrades hasta que Kaching las confirme.
   */
  async watchKaching() {
    let resolve;
    this.abReady = new Promise((r) => { resolve = r; });
    this.visitor = {};
    this.visitorFailed = new Set();
    if (this.kaching.source !== 'kaching') {
      await this.watchReference();
      resolve();
      return;
    }
    const abDeals = () => [...new Set(activeOffers(this.config).filter((o) => o.ab).map((o) => o.deal))];
    // sin predicción (primera visita, aún sin sesión de Kaching), la base no se recomienda hasta saber la variante
    const exclude = this.config.upsell ? (this.config.upsell.exclude = this.config.upsell.exclude || []) : null;
    const held = [...new Set(abDeals().filter((d) => !this.kaching.predicted[d]).flatMap((d) => activeOffers(this.config).filter((o) => o.deal === d).map((o) => o.productId)))];
    for (const p of held) exclude?.push(p);
    const download = this.watchKachingDownload();
    const deals = await kachingDealsWhenReady(10000, 8000);
    const settle = () => {
      for (const deal of abDeals()) {
        const d = (deals || []).find((x) => x.nanoId === deal);
        const letter = d ? visitorLetter(this.kaching.catalog, d) : null;
        if (letter) { this.visitor[deal] = letter; this.visitorFailed.delete(deal); } else if (deals) this.visitorFailed.add(deal);
      }
    };
    settle();
    // Kaching da algo que no cuadra con la página: puede que su descarga traiga una configuración más nueva
    if (this.visitorFailed.size) { await download; settle(); }
    for (const deal of this.visitorFailed) this.ctl.log('kaching', `${deal}: lo que da Kaching no cuadra con la configuración; pasa a genérico`);
    for (const p of held) { const i = exclude ? exclude.indexOf(p) : -1; if (i >= 0) exclude.splice(i, 1); }
    const mismatch = Object.entries(this.visitor).filter(([d, l]) => this.kaching.predicted[d] && this.kaching.predicted[d] !== l);
    if (mismatch.length) this.ctl.log('kaching', 'la predicción A/B no coincide con Kaching', mismatch);
    this.applyVisitor();
    const first = abDeals()[0] || [...this.visitorFailed][0];
    this.market = first ? { deal: first, ab: this.visitor[first] || null, reason: this.visitorFailed.has(first) ? 'la oferta de Kaching no cuadra con la configuración' : deals ? null : 'Kaching no ha cargado' } : null;
    this.abConfirmed = !!deals;
    resolve();
    // solo se repinta si Kaching cambia algo de lo que ya se ve (si confirma la predicción, nada se mueve)
    if (mismatch.length || this.visitorFailed.size || held.length) this.ctl.regroup();
  }

  /** Variante de la visitante en cada oferta: la confirmada por Kaching o, mientras tanto, la predicha. */
  applyVisitor() {
    this.config.offers = (this.config.offers || []).filter((o) => !(o.ab && this.visitorFailed?.has(o.deal)));
    for (const o of this.config.offers) {
      if (!o.ab) continue;
      const letter = this.visitor?.[o.deal] || this.kaching.predicted?.[o.deal] || null;
      o.visitor = !!letter && o.ab.letter === letter;
    }
  }

  /** Respuesta de la consulta deal_blocks que hace la propia Kaching en esta carga (snippet clarene-cart-kaching-watch). */
  async watchKachingDownload() {
    const t0 = Date.now();
    while (!window.__ccKachingSeen && Date.now() - t0 < 15000) await new Promise((r) => setTimeout(r, 150));
    const seen = window.__ccKachingSeen;
    if (!seen?.value) return;
    let raw = null;
    try { raw = JSON.parse(seen.value); } catch { return; }
    this.applyRaw(raw, 'descarga de Kaching');
  }

  /** Bloques crudos de Kaching (su descarga o la consulta propia) con los productos que pintó Liquid. */
  applyRaw(raw, origin) {
    if (!Array.isArray(raw)) return false;
    return this.applyLive({ ...this.kaching.live, total: raw.length, blocks: reduceRawBlocks(raw, this.config.market) }, origin);
  }

  /**
   * Configuración nueva (de la respuesta de un add, de la descarga de Kaching o de la consulta propia). Solo cambia
   * algo si es distinta de la que hay. Si no se entiende, Kaching ha cambiado algo que el carrito no sabe leer: no
   * se anuncia ningún upgrade (las ofertas pasan a pendientes) hasta tener datos que sí se entiendan.
   */
  applyLive(live, origin, { regroup = true } = {}) {
    if (this.kaching.source !== 'kaching' || !live) return false;
    this.kaching.at = Date.now();
    const lc = liveConfig({ ...this.config, bundles: this.refBundles }, live, { sessionId: kachingSession() });
    if (lc.source !== 'kaching') {
      for (const o of this.config.offers || []) o.pending = true;
      this.kaching.problem = `${origin}: ${lc.problem}`;
    } else {
      if (lc.digest === this.kaching.digest) return false;
      this.config.offers = lc.offers;
      this.config.bundles = lc.bundles;
      this.bundlesLoaded = false;
      this.bundleState.clear();
      Object.assign(this.kaching, { live, catalog: lc.catalog, digest: lc.digest, predicted: lc.predicted, problem: null, changes: (this.kaching.changes || 0) + 1, origin });
      this.applyVisitor();
    }
    this.ctl.log('kaching', `configuración actualizada (${origin})`);
    if (regroup) this.ctl.regroup();
    return true;
  }

  /**
   * Pestaña abierta mucho tiempo (o devuelta desde la caché del navegador): si la configuración tiene más de 5 min,
   * se pide la vigente con la misma consulta que usa Kaching (una petición, en segundo plano y sin bloquear nada).
   */
  async refreshIfStale() {
    if (this.kaching.source !== 'kaching' || this.refreshing || Date.now() - this.kaching.at < KACHING_MAX_AGE) return;
    this.refreshing = true;
    try {
      const raw = await fetchDealBlocks();
      if (raw) this.applyRaw(raw, 'consulta propia (pestaña abierta)');
      else this.kaching.at = Date.now(); // sin respuesta útil: no se reintenta en cada apertura
    } catch (e) {
      this.ctl.log('kaching', 'no se ha podido refrescar la configuración', e);
    } finally {
      this.refreshing = false;
    }
  }

  /**
   * Antes de SUBIR de tramo o crear un pack: la oferta que ve la clienta tiene que ser la vigente (ensureFresh) y, si
   * se crea un pack con test A/B, con la variante que confirma Kaching (ensureVisitor). Mientras se comprueba, otro
   * clic no lanza una segunda operación.
   */
  async preflight(g, up) {
    if (this.checking) return false;
    if (!up || !g?.offer) return true;
    this.checking = true;
    try {
      if (!(await this.ensureFresh(g.offer))) return false;
      if (g.kind === 'plain' && g.offer.ab && !(await this.ensureVisitor(g.offer))) return false;
      return true;
    } finally {
      this.checking = false;
    }
  }

  /**
   * Pestaña abierta un rato (configuración de más de 2 min): antes de aplicar un upgrade se comprueba la configuración
   * vigente de Kaching (una consulta, ~150 ms, solo en ese caso). Si la oferta que ve la clienta ha cambiado, no se
   * aplica nada: se repinta con el precio nuevo y se avisa. Si la consulta no responde, se sigue: verify() exige
   * además que el importe real sea exactamente el anunciado y, si no, deshace la operación.
   */
  async ensureFresh(offer) {
    if (this.kaching.source !== 'kaching' || Date.now() - this.kaching.at < KACHING_CHECK_AGE) return true;
    const key = (o) => (o ? JSON.stringify([o.deal, o.ab?.abid ?? null, o.productId, o.tiers.map((t) => [t.qty, t.price, tierGifts(t).map((x) => x.variantId).join('+') || null])]) : null);
    const before = key(offer);
    let raw = null;
    try { raw = await Promise.race([fetchDealBlocks(), new Promise((r) => setTimeout(() => r(null), 3000))]); } catch { raw = null; }
    if (!raw) return true;
    this.applyRaw(raw, 'comprobación antes de aplicar');
    const now = activeOffers(this.config).find((o) => o.deal === offer.deal && (o.ab?.abid ?? null) === (offer.ab?.abid ?? null) && o.productId === offer.productId);
    if (key(now) === before && !now.pending) return true;
    this.notice = { type: 'error', text: 'La oferta ha cambiado. Revisa el nuevo precio antes de continuar.' };
    this.render();
    return false;
  }

  async forceFreshConfig() {
    try {
      const raw = await Promise.race([fetchDealBlocks(), new Promise((r) => setTimeout(() => r(null), 3000))]);
      if (raw) { this.kaching.digest = null; this.applyRaw(raw, 'precio distinto del anunciado'); }
    } catch (e) {
      this.ctl.log('kaching', 'no se ha podido pedir la configuración vigente', e);
    }
  }

  /**
   * Crear un pack con test A/B (base suelta → pack, base del carrusel) solo con la variante que confirma Kaching.
   * Si la predicción no coincide, no se crea nada: se repinta con la oferta correcta para que la clienta la vea.
   */
  async ensureVisitor(offer) {
    if (!offer?.ab) return true;
    const shown = offer.ab.letter;
    if (!this.visitor?.[offer.deal]) await Promise.race([this.abReady, new Promise((r) => setTimeout(r, 6000))]);
    const confirmed = this.visitor?.[offer.deal];
    if (confirmed === shown) return true;
    this.notice = { type: 'error', text: confirmed ? 'Hemos actualizado la oferta. Revísala antes de continuar.' : 'No se ha podido comprobar la oferta. Inténtalo de nuevo en unos segundos.' };
    this.render();
    return false;
  }

  /** Sin configuración en vivo: las referencias, sin upgrades hasta que fetchDeals confirme que siguen siendo las vigentes. */
  async watchReference() {
    const deals = await kachingDealsWhenReady(10000, 8000);
    const { candidate } = this.marketInfo || {};
    let changed = false;
    for (const o of this.config.offers || []) {
      if (!o.pending || String(o.market) !== String(this.config.market)) continue;
      if (o.ab) {
        const res = candidate ? visitorVariant(candidate, deals) : { letter: null };
        if (res.letter) {
          this.visitor[o.deal] = res.letter;
          o.visitor = o.ab.letter === res.letter;
          if (o.visitor) { o.pending = false; changed = true; } // la otra variante: se reconoce, sin upgrades
        }
      } else {
        const d = (deals || []).find((x) => x.nanoId === o.deal);
        if (d && o.sig && dealSignature(d.dealBars) === o.sig) { o.pending = false; changed = true; }
      }
    }
    this.market = { reason: `configuración de Kaching no disponible (${this.kaching.problem}); referencias ${changed ? 'confirmadas' : 'sin confirmar'}` };
    if (changed) this.ctl.regroup();
  }

  /** Estado para depurar: window.ClareneCart.kaching(). */
  kachingInfo() {
    const k = this.kaching;
    return {
      source: k.source, origin: k.origin, problem: k.problem, ageMs: Date.now() - k.at, changes: k.changes || 0, predicted: k.predicted,
      visitor: this.visitor, failed: [...(this.visitorFailed || [])],
      offers: activeOffers(this.config).map((o) => `${o.deal}${o.ab ? '-' + o.ab.letter : ''}${o.visitor ? '*' : ''}${o.pending ? ' (pendiente)' : ''} [${o.tiers.map((t) => `${t.qty}u=${t.price}`).join(' ')}]`),
      bundles: (this.config.bundles || []).map((b) => `${b.deal} ${(b.ab || []).map((v) => `${v.letter}=${v.price}`).join(' ')}`),
    };
  }

  async fetchSection(path) {
    const key = 'cc-ups2:' + path; // v2: incluye variantes (tonos)
    try {
      const hit = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (hit && Date.now() - hit.t < UPSELL_TTL) return hit.list;
    } catch { /* sin sessionStorage */ }
    try {
      const res = await window.fetch((this.config.routes?.root || '/') + path, { credentials: 'same-origin' });
      if (!res.ok) return [];
      const list = parseUpsellSection(await res.text());
      try { sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), list })); } catch { /* lleno o bloqueado */ }
      return list;
    } catch {
      return [];
    }
  }

  // ---------- apertura ----------
  open(refresh) {
    if (!this.root.hidden && !refresh) return;
    if (this.root.hidden) this.viewSent = false;
    this.lastFocus = document.activeElement;
    this.warmImages();
    this.root.hidden = false;
    requestAnimationFrame(() => this.root.classList.add('is-open'));
    document.documentElement.classList.add('cc-lock');
    $('.cc__panel', this.root).focus({ preventScroll: true });
    if (refresh) this.confirmOnce();
    this.trackView();
    this.refreshIfStale();
    this.maybeLoadAuto();
    this.watchViewport();
    this.fitViewport();
  }

  // ---------- encaje en pantalla ----------
  /**
   * El panel se ajusta al área REALMENTE visible (visualViewport), no a 100vh/100dvh: en Safari iOS la barra
   * flotante y el teclado cambian esa altura, y así el pie con «Finalizar compra» nunca queda fuera.
   * En la vista previa de Shopify, su barra inferior (#PBarNextFrameWrapper, popover en la capa superior,
   * 68 px + zona segura) tapa cualquier z-index: se reserva su altura para que el botón quede por encima.
   */
  fitViewport() {
    this.fitQueued = false;
    if (this.root.hidden) return;
    const vv = window.visualViewport;
    const barEl = document.getElementById('PBarNextFrameWrapper') || document.getElementById('preview-bar-iframe');
    const br = barEl ? barEl.getBoundingClientRect() : null;
    const L = viewportLayout({
      innerHeight: window.innerHeight,
      vvHeight: vv ? vv.height : window.innerHeight,
      vvTop: vv ? vv.offsetTop : 0,
      bar: br ? { top: br.top, height: br.height } : null,
      editing: this.editing,
    });
    // Solo se toca el DOM si algo cambia: durante el desplazamiento de iOS al enfocar llegan muchos eventos seguidos
    const key = `${L.vh}|${L.vt}|${L.reserve}|${L.keyboard}`;
    if (key === this.lastLayout) return;
    const wasKeyboard = this.keyboardOpen;
    this.lastLayout = key;
    this.keyboardOpen = L.keyboard;
    this.root.style.setProperty('--cc-vh', `${L.vh}px`);
    this.root.style.setProperty('--cc-vt', `${L.vt}px`);
    this.root.style.setProperty('--cc-reserve', `${L.reserve}px`);
    this.root.classList.toggle('is-compact', L.compact);
    this.root.classList.toggle('is-tiny', L.tiny);
    this.root.classList.toggle('is-typing', L.keyboard);
    if (L.keyboard && !wasKeyboard) this.pageY = window.scrollY;
    if (L.keyboard) requestAnimationFrame(() => this.revealField());
    // al cerrar el teclado, iOS a veces deja la página desplazada: se devuelve a donde estaba
    if (!L.keyboard && wasKeyboard && this.pageY != null && Math.abs(window.scrollY - this.pageY) > 1) window.scrollTo(0, this.pageY);
  }

  /** Deja el campo enfocado (y «Aplicar», en la misma fila) centrado en la zona con scroll del carrito. */
  revealField() {
    const el = document.activeElement;
    const scroller = $('.cc__scroll', this.root);
    if (!el || !scroller || !this.root.contains(el)) return;
    const row = el.closest('.cc-code') || el;
    const r = row.getBoundingClientRect();
    const sr = scroller.getBoundingClientRect();
    const d = scrollDeltaToReveal({ top: r.top, bottom: r.bottom }, { top: sr.top, height: scroller.clientHeight });
    if (d) scroller.scrollTop += d;
  }

  scheduleFit() {
    if (this.fitQueued) return;
    this.fitQueued = true;
    requestAnimationFrame(() => this.fitViewport());
  }

  watchViewport() {
    if (this.viewportWatched) return;
    this.viewportWatched = true;
    const fit = () => this.scheduleFit();
    window.visualViewport?.addEventListener('resize', fit);
    window.visualViewport?.addEventListener('scroll', fit);
    window.addEventListener('resize', fit);
    window.addEventListener('orientationchange', fit);
    // campo de texto del carrito enfocado (código de descuento): decide el modo teclado junto con visualViewport
    const isField = (t) => t && t.matches && t.matches('input[type="text"], input:not([type]), input[type="search"]');
    this.root.addEventListener('focusin', (e) => { if (isField(e.target)) { this.editing = true; fit(); } });
    this.root.addEventListener('focusout', (e) => {
      if (!isField(e.target)) return;
      // si el foco pasa a otro campo del carrito, no se sale del modo teclado
      setTimeout(() => { this.editing = isField(document.activeElement) && this.root.contains(document.activeElement); fit(); }, 0);
    });
    // la barra de vista previa se puede ocultar o volver a mostrar
    const bar = document.getElementById('PBarNextFrameWrapper');
    if (bar && 'ResizeObserver' in window) new ResizeObserver(fit).observe(bar);
    if (bar) new MutationObserver(fit).observe(bar, { attributes: true });
  }

  /** El tema (ajaxProduct:added) y el observador de fetch avisan del mismo add: una sola lectura. */
  /** Al abrir: se relee /cart.js (otra pestaña pudo cambiarlo) y solo se repinta si algo cambió. */
  confirmOnce() {
    if (!this.pending) {
      this.pending = this.ctl.confirm()
        .catch((e) => { this.notice = { type: 'error', text: e.message }; this.render(); })
        .finally(() => { setTimeout(() => { this.pending = null; }, 300); });
    }
    return this.pending;
  }

  /**
   * Imágenes del carrito (líneas y 3 primeras recomendaciones): lazy mientras nadie va a abrirlo (una página donde
   * no se abre el carrito no descarga nada); en cuanto hay intención (cursor o dedo sobre el icono, add en curso,
   * apertura) pasan a carga inmediata y, al abrirse, ya están. Son las mismas imágenes de siempre, solo antes.
   */
  warmImages() {
    if (this.imgLoading === 'eager') return;
    this.imgLoading = 'eager';
    for (const im of [...this.body.querySelectorAll('img[loading="lazy"]'), ...[...this.upsEl.querySelectorAll('.cc-up img')].slice(0, 3)]) im.loading = 'eager';
    this.render();
  }

  refreshOnce() {
    if (!this.pending) {
      this.pending = this.ctl.refresh()
        .catch((e) => { this.notice = { type: 'error', text: e.message }; this.render(); })
        .finally(() => { setTimeout(() => { this.pending = null; }, 300); });
    }
    return this.pending;
  }

  close() {
    if (this.root.hidden) return;
    this.root.classList.remove('is-open');
    document.documentElement.classList.remove('cc-lock');
    setTimeout(() => { this.root.hidden = true; }, 280);
    this.lastFocus?.focus?.();
  }

  trapFocus(e) {
    const f = [...this.root.querySelectorAll('button:not([disabled]), a[href], input, [tabindex="0"]')].filter((x) => x.offsetParent);
    if (!f.length) return;
    const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  // ---------- render ----------
  onState({ busy, result }) {
    if (busy !== undefined) this.busy = busy;
    if (result) this.busy = false;
    this.render();
    this.updateBubbles();
    this.maybeLoadAuto();
  }

  updateBubbles() {
    const n = this.ctl.cart?.item_count ?? 0;
    document.querySelectorAll('.cart-link__bubble-num').forEach((el) => { el.textContent = n; });
    document.querySelectorAll('.cart-link__bubble').forEach((el) => el.classList.toggle('cart-link__bubble--visible', n > 0));
    document.querySelectorAll('[data-cc-count]').forEach((el) => { el.textContent = n ? `(${n})` : ''; });
  }

  render() {
    const cart = this.ctl.cart;
    if (!cart) return;
    this.root.classList.toggle('is-busy', this.busy);
    const groups = this.ctl.groups;
    const empty = !groups.length;
    this.foot.hidden = empty;
    this.summary.hidden = empty;
    const assure = this.root.querySelector('[data-cc-assure]');
    if (assure) assure.hidden = empty;
    const notice = this.notice ? `<div class="cc-notice cc-notice--${this.notice.type}" role="status">${esc(this.notice.text)}</div>` : '';
    if (empty) {
      setHTML(this.body, `${notice}<div class="cc-empty"><p class="cc-empty__title">Tu carrito está vacío</p>
        <p class="cc-empty__text">Descubre la base que se adapta a tu tono.</p>
        <a class="cc-btn cc-btn--outline" href="${esc(this.config.emptyUrl || '/products/base-inteligente')}">Ver la Base Inteligente</a></div>`);
    } else {
      const after = afterPayPerks(cart, groups, this.config.afterPay || []);
      setHTML(this.body, `${notice}<ul class="cc-items" role="list">${displayOrder(groups).map((g) => this.renderGroup(cart, g, after.filter((a) => a.gid === g.gid).map((a) => a.rule))).join('')}</ul>`);
      setHTML(this.details, this.renderDetails(cart));
      setHTML(this.totalEl, this.renderTotal(cart));
    }
    // las recomendaciones se pintan aparte: si fallan o tardan, no afectan a productos, descuentos ni checkout
    const prevScroll = this.upsEl.querySelector('[data-cc-ups-track]')?.scrollLeft;
    try { if (setHTML(this.upsEl, this.renderUpsells(cart, groups))) this.wireCarousel(prevScroll); } catch (e) { setHTML(this.upsEl, ''); }
    this.trackView();
  }

  /** Guarda los componentes de los packs nativos (HTML de clarene-cart-state o mapa ya leído). */
  learnComponents(src) {
    const m = componentsFrom(src);
    if (m) Object.assign(this.comps, m);
  }

  /**
   * Desglose de un pack nativo (Mega Pack) o null. Si el carrito llegó por /cart.js sin sus componentes (p. ej. un add
   * de otro script), se piden una vez a la sección clarene-cart-state y se vuelve a pintar.
   */
  packBreakdown(pack, line, packs) {
    const rows = packIncludes(pack, this.comps[String(line.variant_id)], packs);
    if (!rows && !this.compsLoading) {
      this.compsLoading = true;
      const root = this.config.routes?.root || '/';
      fetch(`${root.endsWith('/') ? root : `${root}/`}cart?section_id=clarene-cart-state`, { credentials: 'same-origin' })
        .then((r) => (r.ok ? r.text() : ''))
        .then((html) => { this.learnComponents(html); if (packIncludes(pack, this.comps[String(line.variant_id)], packs) && this.ctl.cart) this.render(); })
        .catch(() => {})
        .finally(() => { setTimeout(() => { this.compsLoading = false; }, 5000); });
    }
    return rows;
  }

  /** Contenido declarado de un pack (bundles de la referencia, por id de producto), o null. */
  packContents(line) {
    const b = (this.refBundles || this.config.bundles || []).find((x) => x.contents && String(x.productId) === String(line.product_id));
    return b && Number(b.contents.bases) > 0 && b.contents.label ? b.contents : null;
  }

  /**
   * Tarjeta de un grupo. Arriba lo que se paga (foto, nombre, etiquetas del pack o de la suscripción, cantidad y
   * precio); debajo, en filas, lo que va con él: regalos (verde, «GRATIS»), productos de pago único anidados (base de
   * bienvenida) y ventajas sin línea (envío gratis, regalo misterioso que llega por email). Todo sale de /cart.js.
   */
  renderGroup(cart, g, afterPay = []) {
    const line = g.lines[0];
    if (!line) return '';
    let title = esc(line.product_title);
    const variant = line.variant_title && !/default/i.test(line.variant_title) ? `<span class="cc-item__variant">${esc(line.variant_title)}</span>` : '';
    const view = packView(g); // unidades compradas + bases gratis (solo packs de Kaching verificados)
    const plan = g.plan;
    const cad = plan ? planCadence(plan) : null;
    const chips = [];
    if (view.label) chips.push(`<span class="cc-chip cc-chip--pack">${esc(view.label)}</span>`);
    if (plan) chips.push(`<span class="cc-chip cc-chip--sub">${ICON_REPEAT}Suscripción${cad?.adj ? ' ' + cad.adj : ''}</span>`);
    const props = Object.entries(line.properties || {}).filter(([k, v]) => !k.startsWith('_') && v).map(([k, v]) => `<span class="cc-item__prop">${esc(k)}: ${esc(v)}</span>`).join('');
    // Pack con contenido declarado (Pack Buena Cara, por id de producto): cada unidad de Shopify es un pack entero
    const contents = this.packContents(line);
    let includes = contents ? `<span class="cc-item__variant">Incluye ${g.qty * contents.bases} ${esc(contents.label)}</span>` : '';
    // Pack nativo de Shopify (Mega Pack): nombre corto y, debajo, lo que incluye (cantidades × packs) y el envío gratis
    const nativePack = componentPack(this.config, line);
    if (nativePack) {
      title = esc(nativePack.name || line.product_title);
      const inc = this.packBreakdown(nativePack, line, g.qty);
      const ship = nativePack.shipping && shippingSummary(cart, this.config).free;
      if (inc) {
        includes = `<div class="cc-item__incl"><span class="cc-item__incl-h">Incluye:</span><ul class="cc-item__incl-list" role="list">${inc.map((r) => `<li>${r.qty} × ${esc(r.title)}</li>`).join('')}${ship ? `<li class="cc-item__incl-ok">${ICON_CHECK}Envío gratis</li>` : ''}</ul></div>`;
      }
    }
    // Suscripción: una sola línea al final de la tarjeta con lo que se recibe y se cobra después (plan de Shopify)
    const perDelivery = plan ? g.lines.reduce((s2, l) => s2 + (l.selling_plan_allocation?.per_delivery_price ?? l.final_price) * l.quantity, 0) : 0;
    const isBase = /\bbase\b/i.test(line.product_title || '');
    const what = isBase ? `${g.qty} ${g.qty > 1 ? 'bases' : 'base'}` : `${g.qty} ${g.qty > 1 ? 'uds.' : 'ud.'}`;
    const renew = plan ? `<p class="cc-item__renew">${ICON_REPEAT}<span>Después, ${esc(what)} ${esc(cad ? cad.per : 'en cada entrega')} por ${this.money(perDelivery)}. Cancela cuando quieras.</span></p>` : '';
    // Suscripción con 2.ª base de bienvenida (landing): se presenta como el pack que eligió la clienta («2 Bases
    // Inteligentes + regalos»), con el importe del primer pedido. Solo es presentación: las líneas, el plan y los
    // descuentos reales no cambian.
    const welcomeLines = plan ? g.children.filter((l) => l.properties?._cls_welcome) : [];
    const asPack = welcomeLines.length > 0;
    const packBases = asPack ? g.qty + welcomeLines.reduce((s2, l) => s2 + l.quantity, 0) : 0;
    const packGifts = asPack && (g.children.some((l) => !l.properties?._cls_welcome && l.final_line_price === 0) || afterPay.length > 0);
    const final = g.finalCents, orig = g.originalCents;
    const disabled = this.busy ? 'disabled' : '';
    let qtyCtl;
    if (g.editable) {
      const max = g.maxQty;
      // En los packs de Kaching − / + pasan al tramo anterior / siguiente (stepQty: qty ± 1 con tramos seguidos, como
      // siempre; 2 ↔ 4 en la variante A); el número muestra las unidades compradas. Las etiquetas dicen a qué pack se pasa.
      const step = (q) => (g.kind === 'offer' && q > 0 && offerTier(g.offer, q) ? `Cambiar a ${tierName(offerTier(g.offer, q))}` : null);
      const down = stepQty(g, -1), up = stepQty(g, 1);
      qtyCtl = `<div class="cc-qty" role="group" aria-label="Cantidad">
          <button type="button" class="cc-qty__btn" data-cc-action="qty" data-gid="${esc(g.gid)}" data-qty="${down}" aria-label="${esc(step(down) || 'Quitar una unidad')}" ${disabled}>−</button>
          <span class="cc-qty__val" aria-live="polite">${view.paid}</span>
          <button type="button" class="cc-qty__btn" data-cc-action="qty" data-gid="${esc(g.gid)}" data-qty="${up}" aria-label="${esc(step(up) || 'Añadir una unidad')}" ${g.qty >= max || disabled ? 'disabled' : ''}>+</button>
        </div>`;
    } else {
      // cantidad fija (suscripción, pack con regalos de la landing, packs de Kaching que no son la oferta): solo se elimina
      const unit = contents || asPack ? (g.qty > 1 ? 'packs' : 'pack') : g.qty > 1 ? 'uds.' : 'ud.';
      qtyCtl = `<span class="cc-qty cc-qty--fixed">${g.qty} ${unit}</span>`;
    }
    // Filas: primero la base gratis del pack (su importe ya va en el precio del pack, por eso sin tachado), después las
    // líneas de regalo y las anidadas (las de un mismo producto y precio se juntan) y al final las ventajas sin línea.
    if (asPack) title = esc(`${packBases} ${isBase ? 'Bases Inteligentes' : 'unidades'}${packGifts ? ' + regalos' : ''}`);
    const rows = view.free ? [{ kind: 'gift', image: line.image, title: line.product_title, qty: view.free, base: true }] : [];
    if (asPack) rows.push({ kind: 'item', image: line.image, title: `${packBases} × ${isBase ? 'Base Inteligente' : line.product_title}` });
    for (const l of [...g.gifts, ...g.children]) {
      if (asPack && l.properties?._cls_welcome) continue;
      const free = l.final_line_price === 0;
      const welcome = !!l.properties?._cls_welcome;
      const same = rows.find((r) => !r.base && r.variantId === l.variant_id && r.free === free && r.welcome === welcome);
      if (same) { same.qty += l.quantity; same.orig += l.original_line_price; same.final += l.final_line_price; continue; }
      rows.push({ kind: free ? 'gift' : 'paid', free, welcome, variantId: l.variant_id, image: l.image, title: l.product_title, qty: l.quantity, orig: l.original_line_price, final: l.final_line_price });
    }
    const thumb = (src) => (src ? `<img src="${esc(img(src, 96))}" alt="" width="40" height="40" loading="${this.imgLoading}">` : '');
    const row = ({ kind, image, icon, tag, title: t, note, price }) => `<li class="cc-gift cc-gift--${kind}">
        ${image ? thumb(image) : icon ? `<span class="cc-gift__icon" aria-hidden="true">${icon}</span>` : ''}
        <span class="cc-gift__body">
          <span class="cc-gift__title">${tag ? `<span class="cc-gift__tag">${esc(tag)}</span>` : ''}${esc(t)}</span>
          ${note ? `<span class="cc-gift__note">${esc(note)}</span>` : ''}
        </span>
        ${price ? `<span class="cc-gift__price">${price}</span>` : ''}
      </li>`;
    const rowHtml = rows.map((r) => {
      const name = `${r.title}${r.qty > 1 ? ` ×${r.qty}` : ''}`;
      if (r.kind === 'item') return row({ kind: 'item', image: r.image, title: r.title });
      if (r.kind === 'gift') {
        return row({ kind: 'gift', image: r.image, tag: 'Regalo', title: name, note: plan && !r.base && !asPack ? 'Solo en el primer pedido' : '',
          price: r.base || !(r.orig > 0) ? '<strong>GRATIS</strong>' : `<s>${this.money(r.orig)}</s> <strong>GRATIS</strong>` });
      }
      return row({ kind: 'paid', image: r.image, tag: plan ? 'Pago único' : '', title: name,
        note: plan ? (r.welcome ? '2.ª base de bienvenida · no se renueva' : 'Solo en este pedido · no se renueva') : '',
        price: `${r.orig > r.final ? `<s>${this.money(r.orig)}</s> ` : ''}<b>${this.money(r.final)}</b>` });
    });
    const perks = [
      ...packPerks(g, KACHING_MEDIA).map((p) => (isMystery(p.text)
        ? { kind: 'perk', image: p.image, icon: PERK_ICON_GIFT, ...MYSTERY }
        : { kind: 'perk', image: p.image, icon: p.shipping ? PERK_ICON_SHIPPING : PERK_ICON_GIFT, text: p.text.replace(/^\+\s*/, '').replace(/^./, (c) => c.toUpperCase()), price: '<strong>INCLUIDO</strong>' })),
      ...afterPay.map((r) => ({ kind: 'perk', image: r.image, icon: PERK_ICON_GIFT, text: r.text || MYSTERY.text })),
    ].map((p) => row({ kind: 'perk', image: p.image, icon: p.icon, title: p.text, note: p.note, price: p.price }));
    const gifts = [...rowHtml, ...perks].join('');
    // Pack de la landing de compra única: la imagen principal de la landing (config.landingUpgrades), no la del producto
    const mainImage = landingPackImage(this.config, line) || line.image;
    return `<li class="cc-item${plan ? ' cc-item--sub' : ''}" data-gid="${esc(g.gid)}">
      <div class="cc-item__main">
        <a class="cc-item__img" href="${esc(line.url)}">${mainImage ? `<img src="${esc(img(mainImage, 200))}" alt="${esc(line.product_title)}" width="88" height="88" loading="${this.imgLoading}">` : ''}</a>
        <div class="cc-item__info">
          <div class="cc-item__top">
            <a class="cc-item__title" href="${esc(line.url)}">${title}</a>
            <button type="button" class="cc-item__remove" data-cc-action="remove" data-gid="${esc(g.gid)}" aria-label="Eliminar ${nativePack ? title : esc(line.product_title)}${rows.length ? ' y lo que va con él' : ''}" ${disabled}>
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>
            </button>
          </div>
          ${chips.length ? `<div class="cc-item__chips">${chips.join('')}</div>` : ''}${includes}${variant}${props}
          <div class="cc-item__bottom">
            ${qtyCtl}
            <div class="cc-item__price">${asPack ? this.renderPackPrice(g) : this.renderPrice(cart, g, orig, final)}</div>
          </div>
        </div>
      </div>
      ${gifts ? `<ul class="cc-gifts" role="list" aria-label="Incluido con ${title}">${gifts}</ul>` : ''}
      ${renew}
      ${this.renderUpgrade(cart, g)}
    </li>`;
  }

  /**
   * Precio del pack de suscripción con base de bienvenida: lo que se paga hoy por todas sus líneas (/cart.js) y,
   * tachado, el precio normal de las bases (compare_at del plan para la suscrita, precio original para la bienvenida).
   */
  renderPackPrice(g) {
    const all = [...g.lines, ...g.children];
    const now = all.reduce((a, l) => a + l.final_line_price, 0);
    const was = all.filter((l) => l.final_line_price > 0 || l.properties?._cls_welcome)
      .reduce((a, l) => a + (l.selling_plan_allocation?.compare_at_price ? l.selling_plan_allocation.compare_at_price * l.quantity : l.original_line_price), 0);
    return `${was > now ? `<s>${this.money(was)}</s>` : ''}<strong>${this.money(now)}</strong>`;
  }

  renderPrice(cart, g, orig, final) {
    const plain = plainPrice(cart, this.ctl.groups, g, this.compareAt(g.lines[0]));
    // unidad realmente gratis (Shopify la deja a 0 €): «GRATIS» en verde en lugar de «0,00 €»
    if (isFreeLine(g, plain)) return `<s>${this.money(plain.was)}</s><strong class="cc-item__free">GRATIS</strong>`;
    const p = plain || offerPrice(cart, this.ctl.groups, g);
    if (p) {
      const code = p.codes.length ? `<span class="cc-item__code">con ${esc(p.codes.join(' + '))}</span>` : '';
      return `${p.was > p.now ? `<s>${this.money(p.was)}</s>` : ''}<strong>${this.money(p.now)}</strong>${code}`;
    }
    const withCode = groupPriceAfterOrderDiscount(cart, this.ctl.groups, g);
    if (withCode == null) return `${orig > final ? `<s>${this.money(orig)}</s>` : ''}<strong>${this.money(final)}</strong>`;
    const code = (cart.cart_level_discount_applications || []).map((d) => d.title).join(' + ');
    return `<s>${this.money(orig)}</s><strong>${this.money(withCode)}</strong><span class="cc-item__code">con ${esc(code)}</span>`;
  }

  /**
   * Precio de referencia (compare-at) de la variante de una línea, de los datos de producto que ya tiene la página
   * (recomendaciones del editor y automáticas). Sin peticiones. Solo vale si su precio coincide con el de la línea.
   */
  compareAt(line) {
    return variantCompareAt([...(this.config.upsell?.manual || []), ...this.auto], line);
  }

  renderUpgrade(cart, g) {
    const tier = nextTier(g);
    if (!tier) return this.renderLandingUpgrade(cart, g);
    const { deltaCents, exact, withCode } = upgradeDelta(cart, g, tier);
    // Ampliación sin coste (1 → 1+1 de la variante A): solo si el importe es exacto (0 €, ni más ni menos), el tramo es
    // de la configuración en vivo de Kaching (verificado) y trae unidades gratis. Las demás, como siempre, solo si suben.
    const free = deltaCents === 0 && exact && !!g.offer.live && tier.free > 0;
    if (deltaCents <= 0 && !free) return '';
    const unit = g.lines[0].original_price;
    const save = Math.max(0, unit * tier.qty - tier.price); // igual que la etiqueta de Kaching (sin contar la brocha)
    const code = (cart.cart_level_discount_applications || []).map((d) => d.title).join(' + ');
    // Si Shopify no permite conocer el céntimo exacto antes del clic, se marca como aproximado (≈).
    const { head, sub, label, gift } = upgradeCopy(tier, { delta: this.money(deltaCents), save: this.money(save), exact, withCode, code, from: g.qty, free, fromFree: g.tier?.free ?? 0 });
    const price = free ? 'GRATIS' : `${exact ? '' : '≈ '}+${this.money(deltaCents)}`;
    return `<div class="cc-upgrade${gift ? ' cc-upgrade--gift' : ' cc-upgrade--deal'}">
      <div class="cc-upgrade__text">
        <span class="cc-upgrade__head">${esc(head)}</span>
        <span class="cc-upgrade__sub">${esc(sub)}</span>
      </div>
      <button type="button" class="cc-upgrade__btn" data-cc-action="upgrade" data-gid="${esc(g.gid)}" aria-label="${esc(label)}" ${this.busy ? 'disabled' : ''}>
        ${esc(price)}
      </button>
    </div>`;
  }

  /** Ampliación de la landing (1 base → pack + regalo): el mismo bloque y estilo que las de Kaching. */
  renderLandingUpgrade(cart, g) {
    const u = landingUpgradeFor(cart, g, this.config);
    if (!u) return '';
    // importe redondo sin céntimos («10 €»), como en el texto pedido
    const delta = this.money(u.delta).replace(/[,.]00(?=\D*$)/, '');
    const fill = (t) => String(t || '').replace(/\[delta\]/g, delta);
    return `<div class="cc-upgrade cc-upgrade--gift cc-upgrade--landing">
      <div class="cc-upgrade__text">
        <span class="cc-upgrade__head">${esc(fill(u.head))}</span>
        <span class="cc-upgrade__sub">${esc(fill(u.sub))}</span>
      </div>
      <button type="button" class="cc-upgrade__btn" data-cc-action="upgrade-landing" data-gid="${esc(g.gid)}" aria-label="${esc(fill(u.head))}" ${this.busy ? 'disabled' : ''}>
        ${esc(fill(u.button))}
      </button>
    </div>`;
  }

  renderUpsells(cart, groups) {
    const u = this.config.upsell || {};
    this.shown = pickUpsells({ cart, groups, config: this.config, manual: u.manual || [], automatic: this.auto, bundleState: this.bundleState });
    if (!this.shown.length) return '';
    const title = u.title || 'Completa tu rutina';
    const dis = this.busy ? 'disabled' : '';
    // Carrusel horizontal: ~2 tarjetas visibles y un trozo de la siguiente como pista de que hay más.
    const more = this.shown.length > 2;
    return `<section class="cc-ups" aria-labelledby="ClareneCartUpsTitle">
      <div class="cc-ups__head">
        <h3 class="cc-ups__title" id="ClareneCartUpsTitle">${esc(title)}</h3>
        ${more ? '<span class="cc-ups__hint" aria-hidden="true">Desliza <svg viewBox="0 0 16 16" width="12" height="12"><path d="M6 3l5 5-5 5" fill="none" stroke="currentColor" stroke-width="1.6"/></svg></span>' : ''}
        <span class="cc-ups__nav">
          <button type="button" class="cc-ups__arrow" data-cc-ups-dir="-1" aria-controls="ClareneCartUpsTrack" aria-label="Ver recomendaciones anteriores"><svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true"><path d="M10 3L5 8l5 5" fill="none" stroke="currentColor" stroke-width="1.6"/></svg></button>
          <button type="button" class="cc-ups__arrow" data-cc-ups-dir="1" aria-controls="ClareneCartUpsTrack" aria-label="Ver más recomendaciones"><svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true"><path d="M6 3l5 5-5 5" fill="none" stroke="currentColor" stroke-width="1.6"/></svg></button>
        </span>
      </div>
      <ul class="cc-ups__track${more ? ' has-more' : ''}" role="list" id="ClareneCartUpsTrack" data-cc-ups-track>${this.shown.map((p, i) => {
        const first = p.options?.[0];
        const sub = p.action === 'bundle' ? `<span class="cc-up__gifts">+ ${esc(this.giftNames(p))} de regalo</span>` : '';
        const select = p.action === 'choose' ? `<label class="cc-visually-hidden" for="cc-up-v-${i}">Tono de ${esc(p.title)}</label>
            <select id="cc-up-v-${i}" class="cc-up__select" data-cc-variant data-index="${i}" ${dis}>
              ${p.options.map((v) => `<option value="${v.id}">${esc(v.title)}</option>`).join('')}
            </select>` : '';
        const image = (first && first.image) || p.image;
        return `<li class="cc-up">
          <div class="cc-up__top">
            <span class="cc-up__img">${image ? `<img src="${esc(img(image, 120))}" alt="" width="48" height="48" loading="${i < 3 ? this.imgLoading : 'lazy'}">` : ''}</span>
            <div class="cc-up__info">
              <span class="cc-up__title">${esc(p.title)}</span>
              <span class="cc-up__price" data-cc-up-price>${this.upPrice(first || p, p)}</span>
            </div>
          </div>
          ${sub}
          <div class="cc-up__actions">
            ${select}
            <button type="button" class="cc-up__add" data-cc-action="upsell" data-index="${i}" ${dis} aria-label="Añadir ${esc(p.title)} al carrito">Añadir</button>
          </div>
        </li>`;
      }).join('')}</ul>
    </section>`;
  }

  /**
   * El carrusel conserva su posición al repintar y quita la pista de "más" al llegar al final.
   * Escritorio: flechas en el encabezado (solo si hay tarjetas fuera del área visible; la del extremo se
   * desactiva) y Shift + rueda. El deslizamiento táctil y el trackpad siguen siendo nativos.
   */
  wireCarousel(prevScroll) {
    this.upsRO?.disconnect();
    const track = this.upsEl.querySelector('[data-cc-ups-track]');
    if (!track) return;
    const section = track.closest('.cc-ups');
    const arrows = [...section.querySelectorAll('[data-cc-ups-dir]')];
    track.scrollLeft = prevScroll || 0;
    const update = () => {
      const st = carouselState(track);
      track.classList.toggle('at-end', st.atEnd);
      section.classList.toggle('is-scrollable', st.scrollable);
      // aria-disabled (y no disabled): el foco del teclado no se pierde al llegar a un extremo
      for (const a of arrows) a.setAttribute('aria-disabled', String(a.dataset.ccUpsDir === '1' ? st.atEnd : st.atStart));
    };
    const step = () => {
      const card = track.querySelector('.cc-up');
      return carouselStep(card?.getBoundingClientRect().width, parseFloat(getComputedStyle(track).columnGap));
    };
    const go = (dir) => {
      const left = carouselTarget(track, step(), dir);
      if (Math.abs(left - track.scrollLeft) < 1) return false;
      const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      track.scrollTo({ left, behavior: reduce ? 'auto' : 'smooth' });
      return true;
    };
    for (const a of arrows) a.addEventListener('click', () => { if (a.getAttribute('aria-disabled') !== 'true') go(Number(a.dataset.ccUpsDir)); });
    // Shift + rueda: una tarjeta por gesto; sin Shift no se toca (scroll vertical normal)
    let lock = 0;
    track.addEventListener('wheel', (e) => {
      const dir = wheelDirection(e);
      const st = dir && carouselState(track);
      if (!dir || !st.scrollable || (dir > 0 && st.atEnd) || (dir < 0 && st.atStart)) return;
      e.preventDefault();
      if (Date.now() < lock) return;
      if (go(dir)) lock = Date.now() + 280;
    }, { passive: false });
    track.addEventListener('scroll', update, { passive: true });
    // el carril cambia de ancho al abrir el carrito o al redimensionar la ventana
    if (window.ResizeObserver) { this.upsRO = new ResizeObserver(update); this.upsRO.observe(track); }
    update();
  }

  giftNames(p) {
    const b = (this.config.bundles || []).find((x) => String(x.productId) === String(p.productId));
    const names = (b?.gifts || []).map((g) => g.title).filter(Boolean);
    if (!names.length) return 'regalos';
    return names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} y ${names[names.length - 1]}`;
  }

  /**
   * Desglose siempre visible (bloque «Resumen del pedido», zona con scroll). Todos los importes salen de /cart.js.
   * El total y el ahorro van en el pie fijo, así que aquí no se repiten.
   */
  renderDetails(cart) {
    const lineSavings = Math.max(0, cart.original_total_price - cart.items_subtotal_price);
    const dis = this.busy ? 'disabled' : '';
    const codes = cart.cart_level_discount_applications || [];
    const applied = new Set(codes.map((d) => String(d.title).toUpperCase()));
    // códigos aceptados por Shopify pero sin importe en este carrito (p. ej. no aplicables): se avisa sin inventar descuento
    const idle = (cart.discount_codes || []).filter((d) => !applied.has(String(d.code).toUpperCase()));
    const ship = shippingSummary(cart, this.config);
    const hasFree = (cart.items || []).some((l) => l.final_line_price === 0 && l.original_line_price > 0);
    return `
      <dl class="cc-sum">
        <div><dt>Subtotal</dt><dd>${this.money(cart.original_total_price)}</dd></div>
        ${lineSavings ? `<div class="cc-sum__disc"><dt>${hasFree ? 'Descuentos y regalos' : 'Descuentos'}</dt><dd>−${this.money(lineSavings)}</dd></div>` : ''}
        ${codes.map((d) => `<div class="cc-sum__disc"><dt>${esc(d.title)}${d.type === 'discount_code' ? ` <button type="button" class="cc-sum__x" data-cc-action="remove-code" data-code="${esc(d.title)}" aria-label="Quitar el código ${esc(d.title)}" ${dis}>Quitar</button>` : ''}</dt><dd>−${this.money(d.total_allocated_amount)}</dd></div>`).join('')}
        ${idle.map((d) => `<div class="cc-sum__idle"><dt>${esc(d.code)} <button type="button" class="cc-sum__x" data-cc-action="remove-code" data-code="${esc(d.code)}" aria-label="Quitar el código ${esc(d.code)}" ${dis}>Quitar</button></dt><dd>${d.applicable ? '—' : 'No aplicable'}</dd></div>`).join('')}
        <div><dt>Envío</dt><dd${ship.free ? ' class="cc-sum__free"' : ''}>${esc(ship.text)}</dd></div>
      </dl>`;
  }

  /** Pie fijo: total, ahorro destacado y botón de pago. Con suscripción, «Total hoy» y lo que se cobra después. */
  renderTotal(cart) {
    const savings = cartSavings(cart);
    const rec = recurringSummary(cart);
    return `<div class="cc-total__row">
        <span class="cc-total__label">${rec ? 'Total hoy' : 'Total'}</span>
        ${savings ? `<span class="cc-total__save">Ahorras ${this.money(savings)}</span>` : ''}
        <strong class="cc-total__amount">${this.money(cart.total_price)}</strong>
      </div>${rec ? `<div class="cc-total__rec"><span>Después</span><strong>${this.money(rec.amount)}${esc(rec.cadence ? rec.cadence.short : ' en cada entrega')}</strong></div>` : ''}`;
  }
}

function boot() {
  const root = document.getElementById('ClareneCart');
  const config = readJSON('ClareneCartConfig');
  if (!root || !config) return;
  // Sustituye el carrito lateral de Impulse: el tema sigue usando su add Ajax (cart_type = drawer),
  // pero en lugar de abrir su drawer abre el nuestro.
  if (window.theme) {
    window.theme.CartDrawer = function () {};
  }
  // Configuración de Kaching en vivo (Liquid, mismo HTML que el carrito): ofertas, tramos, regalos y variantes A/B
  // desde el primer render, sin esperar a Kaching ni hacer peticiones (docs/06). Si no está o no se entiende, las
  // referencias de docs/referencia, pero sin anunciar upgrades hasta que Kaching las confirme (watchReference).
  const refBundles = config.bundles;
  const lc = liveConfig(config, readJSON('ClareneKachingLive'), { sessionId: kachingSession() });
  let marketInfo = null;
  if (lc.source === 'kaching') {
    config.offers = lc.offers;
    config.bundles = lc.bundles;
  } else {
    marketInfo = marketVariantOffers(config.marketOffers, config.market);
    config.offers = [...(config.offers || []), ...marketInfo.offers].map((o) => ({ ...o, pending: true }));
    // bundles: resolveBundle los comprueba con fetchDeals antes de recomendarlos; sin mercado propio, solo en euros
    // (Pack Buena Cara: sus precios fuera del euro no están verificados)
    config.bundles = (config.bundles || []).filter((b) => (b.market != null ? String(b.market) === String(config.market) : config.currency === (config.freeShippingCurrency || 'EUR')));
  }
  const cart = new ClareneCart(root, config, null);
  cart.refBundles = refBundles;
  cart.marketInfo = marketInfo;
  cart.kaching = { source: lc.source, problem: lc.problem || null, live: lc.source === 'kaching' ? readJSON('ClareneKachingLive') : null, catalog: lc.catalog, digest: lc.digest, predicted: lc.predicted || {}, at: Date.now(), origin: 'Liquid' };
  cart.visitor = {};
  cart.applyVisitor();
  const initial = readJSON('ClareneCartInitial');
  cart.learnComponents(readJSON('ClareneCartInitialComponents'));
  if (initial) cart.ctl.hydrate(withParents(initial, readJSON('ClareneCartInitialParents')));
  window.ClareneCart = { open: () => cart.open(true), close: () => cart.close(), refresh: () => cart.ctl.refresh(), controller: cart.ctl, market: () => cart.market || null, kaching: () => cart.kachingInfo(), analytics: () => cart.track.log.slice() };
  if (/[?&]clarene_cart=open/.test(location.search)) cart.open(true);
  cart.watchKaching();
}

boot();

})();
